import React, { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { Link, useParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from './AuthContext';
import NiceLoader from './NiceLoader';
import TwickEditorPage from './TwickEditorPage';
import web2mobile from './assets/vitrine/web2mobile.jpg';

/* ================= Utils ================= */
const wvShuffleCopy = (arr) => {
  const a = Array.isArray(arr) ? [...arr] : [];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const wvMoveItem = (arr, from, to) => {
  const a = Array.isArray(arr) ? [...arr] : [];
  if (from === to) return a;
  if (from < 0 || from >= a.length) return a;
  let target = to;
  if (target < 0) target = 0;
  if (target >= a.length) target = a.length - 1;
  const [it] = a.splice(from, 1);
  a.splice(target, 0, it);
  return a;
};

const wvToggleInArray = (arr, value) => {
  const a = Array.isArray(arr) ? [...arr] : [];
  const v = String(value);
  const idx = a.findIndex((x) => String(x) === v);
  if (idx >= 0) {
    a.splice(idx, 1);
    return a;
  }
  a.push(value);
  return a;
};

const wvClamp = (n, min, max) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  return Math.min(max, Math.max(min, x));
};


/* ================= Localomponents (inlined) ================= */
// Robust JSON fetch that avoids SPA fallback HTML.
// Tries absolute path, /api prefix, then backend port :3001.

const feLog = (...args) => console.log('[Export][FE]', ...args);
// Simple 3-dot loader
function LoaderDots({ label = "Génération en cours" }) {
  return (
    <div role="status" aria-live="polite" className="d-flex align-items-center gap-2">
      <span className="fw-semibold">{label}</span>
      <span style={{ fontFamily: 'monospace' }}>
        <span className="dot">.</span>
        <span className="dot" style={{ animationDelay: '150ms' }}>.</span>
        <span className="dot" style={{ animationDelay: '300ms' }}>.</span>
      </span>
      <style>{`
        .dot{animation: blink 900ms infinite ease-in-out; opacity:.25}
        @keyframes blink{0%{opacity:.25}50%{opacity:1}100%{opacity:.25}}
      `}</style>
    </div>
  );
}

function DraftsList({ projectId, token, addToast, onRestore, reloadSignal = 0 }) {
  const [drafts, setDrafts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await fetch(`/projects/${projectId}/montage-drafts`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const ct = (r.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) throw new Error('NON_JSON_RESPONSE');
      const rows = await r.json();
      const arr = Array.isArray(rows) ? rows.slice() : [];
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setDrafts(arr);
    } catch (e) {
      console.error('drafts load error:', e);
      addToast?.('Erreur lors du chargement des brouillons.', 'error');
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, [projectId, token, reloadSignal]);

  if (loading) {
    return <div className="text-muted small"><i className="bi bi-arrow-clockwise me-1" /> Chargement…</div>;
  }

  if (!drafts.length) {
    return <div className="text-muted small">Aucun brouillon enregistré pour ce projet.</div>;
  }

  return (
    <div className="list-group">
      {drafts.map((d) => {
        const date = d?.created_at ? new Date(d.created_at) : null;
        const label = date ? date.toLocaleString() : `ID ${d.id}`;
        const clipCount = (() => { try { return (JSON.parse(d.media_order || '[]') || []).length; } catch { return 0; } })();
        const volPct = (typeof d.music_volume !== 'undefined' && d.music_volume !== null)
          ? Math.round(Number(d.music_volume) * 100) : null;
        const chips = [
          d?.montage_type ? `Mode: ${d.montage_type}` : null,
          d?.theme ? `Thème: ${d.theme}` : null,
          d?.audio_mix_option ? `Mix: ${d.audio_mix_option}` : null,
          Number.isFinite(volPct) ? `Vol: ${volPct}%` : null,
          `${clipCount} clip${clipCount > 1 ? 's' : ''}`,
          d?.voice_file ? 'Voix IA' : (d?.voice_over_type === 'file' ? 'Voix (fichier)' : (d?.voice_over_type === 'none' ? 'Sans voix' : null)),
          d?.music_file ? 'Musique' : null
        ].filter(Boolean);


        return (
          <div key={d.id} className="list-group-item d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <i className="bi bi-file-earmark-text text-muted" />
              <div>
                <div className="fw-semibold">{label}</div>
                {chips.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {chips.map((c, i) => (
                      <span key={i} className="badge bg-light text-dark">{c}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => { onRestore?.(d); (window.addToast || addToast)?.('Brouillon chargé dans l’éditeur.', 'success'); }}
                title="Restaurer ce brouillon"
              >
                <i className="bi bi-upload me-1" /> Restaurer
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}



// Close & Share actions
function CloseShareBar({ projectId, finalVideoUrl, busy, addToast, onClosed }) {
  const share = async () => {
    if (!finalVideoUrl) return;
    try {
      await navigator.clipboard.writeText(window.location.origin + finalVideoUrl);
      addToast?.('Lien copié dans le presse-papiers.', 'success');
    } catch {
      addToast?.('Impossible de copier le lien.', 'error');
    }
  };

  const closeProject = async () => {
    try {
      const r = await fetch(`/projects/${id}/close`, { method: 'POST' });
      if (!r.ok) throw new Error('close-failed');
      addToast('Projet clôturé.', 'success', 5000);
      // Optional: visually reflect closure (e.g., switch visibility / go back)
      setVisibility('PRIVATE');
    } catch {
      addToast(
        'Impossible de clôturer le projet (route manquante côté backend ?).',
        'error',
        5000
      );
    }
  };

  return (
    <div className="d-flex flex-wrap gap-2 mt-3">
      <button className="btn btn-outline-primary" onClick={share} disabled={!finalVideoUrl || busy}>
        <i className="bi bi-share me-1" /> Partager la vidéo
      </button>
      <button className="btn btn-outline-danger ms-auto" onClick={closeProject} disabled={busy}>
        <i className="bi bi-lock-fill me-1" /> Clôturer le projet
      </button>
    </div>
  );
}

function useToasts() {
  const [toasts, setToasts] = useState([]); // {id, type:'success'|'error', msg}
  const addToast = (msg, type = 'success', timeout = 3500) => {
    const id = Math.random().toString(36).slice(2);
    setToasts(t => [...t, { id, msg, type }]);
    if (timeout) setTimeout(() => {
      setToasts(t => t.filter(x => x.id !== id));
    }, timeout);
  };
  const removeToast = (id) => setToasts(t => t.filter(x => x.id !== id));

  const Toasts = () => (
    <div style={toastStyles.wrap} aria-live="polite" aria-atomic="true">
      {toasts.map(t => (
        <div key={t.id} style={{ ...toastStyles.toast, ...(t.type === 'error' ? toastStyles.error : toastStyles.success) }}>
          <div className="d-flex align-items-start">
            <div className="me-2">{t.type === 'error' ? <i className="bi bi-x-circle" /> : <i className="bi bi-check2-circle" />}</div>
            <div className="flex-grow-1 small">{t.msg}</div>
            <button className="btn btn-sm btn-light ms-2" onClick={() => removeToast(t.id)} aria-label="Fermer">
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return { addToast, Toasts };
}

const toastStyles = {
  wrap: {
    position: 'fixed',
    right: 16,
    bottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    zIndex: 9999
  },
  toast: {
    minWidth: 260,
    maxWidth: 420,
    padding: '10px 12px',
    borderRadius: 12,
    color: '#0f172a',
    border: '1px solid transparent',
    boxShadow: '0 10px 24px rgba(0,0,0,.10), 0 1px 2px rgba(0,0,0,.06)',
    background: '#fff'
  },
  success: {
    borderColor: 'rgba(25,135,84,.35)',
    background: 'linear-gradient(135deg, rgba(25,135,84,.08), #fff)'
  },
  error: {
    borderColor: 'rgba(220,53,69,.35)',
    background: 'linear-gradient(135deg, rgba(220,53,69,.08), #fff)'
  }
};

/* ================= Helpers ================= */
const prettyRole = (r) => ({
  scenariste: 'Scénariste',
  'co-scenariste': 'Co-scenariste',
  contributor: 'Contributeur',
  admin: 'Admin',
}[r] || r);

const ROLE_ORDER = ['admin', 'scenariste', 'co-scenariste', 'contributor'];

const withUploadsPath = (p) => {
  if (!p) return '';
  return p.startsWith('/uploads/') ? p : `/uploads/${p.replace(/^\/+/, '')}`;
};

const styles = {
  page: { paddingTop: 90, paddingBottom: 40 },
  layout: { display: 'block' },
  card: { background: '#fff', border: '1px solid #eee', borderRadius: 18, padding: 18, width: '100%', maxWidth: '100%', },

  /* Montage centering (already present) */
  montageStage: { maxWidth: 1100, margin: '0 auto' },
  montageControlsRow: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  montageSectionTitle: { textAlign: 'center', marginBottom: 16 },

  /* --- NEW: Export centering --- */
  exportStage: {
    margin: '0 -18px',
    width: 'calc(100% + 36px)'
  },


  wizardNav: {
    marginTop: 16,
    display: 'flex',
    gap: 12,
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 16,
    padding: '14px 12px',
    boxShadow: '0 10px 24px rgba(0,0,0,0.06)',
    position: 'sticky',
    top: 80,
    zIndex: 5
  },
  navBtn: (active) => ({
    border: 'none',
    background: active ? '#0d6efd' : 'transparent',
    color: active ? '#fff' : '#111',
    padding: '10px 16px',
    borderRadius: 12,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }),

  accordionBtn: {
    width: '100%',
    textAlign: 'left',
    borderRadius: 12,
    border: '1px solid #e6e6e6',
    background: '#f9fafb',
    padding: '10px 12px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  accordionPanel: {
    border: '1px solid #e6e6e6',
    borderTop: 'none',
    borderRadius: '0 0 12px 12px',
    padding: 12,
    background: '#fff'
  },

  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 16,
    width: '100%',
    alignItems: 'stretch'
  },
  stepCard: {
    background: '#f8fafc',
    border: '1px solid #e6e6e6',
    borderRadius: 14,
    padding: 14,
    transition: 'transform .12s ease, box-shadow .12s ease, border-color .12s ease',
    minWidth: 0,
    width: '100%'
  },

  libPane: {
    maxHeight: '72vh',
    overflowY: 'auto',
    paddingRight: 6,
    borderRadius: 12
  },
  libGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12
  },
  libItem: {
    border: '1px solid #e7e7e7',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#fff'
  },
  libThumbWrap: {
    width: '100%',
    aspectRatio: '4 / 3',
    background: '#0b0c0d'
  },
  libThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },

  chatWrap: { border: '1px solid #e9ecef', borderRadius: 16, overflow: 'hidden' },
  chatHeader: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
    borderBottom: '1px solid #eef2f6',
    padding: '10px 12px'
  },
  chatList: {
    maxHeight: '42vh',
    overflowY: 'auto',
    padding: 12,
    background: '#fbfdff'
  },
  chatComposer: {
    borderTop: '1px solid #eef2f6',
    padding: 10,
    background: '#fff'
  },
  bubble: (mine) => ({
    alignSelf: mine ? 'flex-end' : 'flex-start',
    background: mine ? '#0d6efd' : '#f1f5f9',
    color: mine ? '#fff' : '#0f172a',
    borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    padding: '8px 12px',
    maxWidth: '78%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  }),
  meta: { fontSize: 12, opacity: .7, marginTop: 4 }
};

const TRANSITION_OPTIONS = [
  {
    key: 'none',
    label: 'Coupe directe',
  },
  {
    key: 'fade',
    label: 'Fondu enchaîné',
  },
  {
    key: 'slideleft',
    label: 'Slide vers la gauche',
  },
  {
    key: 'slideright',
    label: 'Slide vers la droite',
  },
  {
    key: 'slideup',
    label: 'Slide vers le haut',
  },
  {
    key: 'slidedown',
    label: 'Slide vers le bas',
  },
  {
    key: 'radial',
    label: 'Radial',
  },
  {
    key: 'circleopen',
    label: 'Cercle ouvrant',
  },
  {
    key: 'circleclose',
    label: 'Cercle fermant',
  },
  {
    key: 'dissolve',
    label: 'Dissolve',
  },
  {
    key: 'fadeblack',
    label: 'Fondu au noir',
  },
  {
    key: 'fadewhite',
    label: 'Fondu au blanc',
  },
  {
    key: 'wipeleft',
    label: 'Wipe gauche',
  },
  {
    key: 'wiperight',
    label: 'Wipe droite',
  },
  {
    key: 'wipeup',
    label: 'Wipe haut',
  },
  {
    key: 'wipedown',
    label: 'Wipe bas',
  },
  {
    key: 'zoom',
    label: 'Zoom',
  },
  {
    key: 'slidebars',
    label: 'Slide bars',
  }
  // tu peux ajouter d'autres transitions ici si besoin, en gardant {key, label, hint, icon}
];

/* ================= Viby (TikTok / CapCut-like vibe controls) ================= */
const VIBY_MUSIC_PRESETS = [
  { key: 'trending_pop', label: 'Trending Pop', hint: 'Energetic chart pop with punchy drums' },
  { key: 'lofi_chill', label: 'Lo‑Fi Chill', hint: 'Warm keys, soft beat, cozy ambience' },
  { key: 'afro_groove', label: 'Afro Groove', hint: 'Percussive groove, sunny vibe' },
  { key: 'edm_drop', label: 'EDM Drop', hint: 'Build + drop, festival energy' },
  { key: 'cinematic', label: 'Cinematic', hint: 'Epic pads, wide, emotional' },
  { key: 'minimal_piano', label: 'Minimal Piano', hint: 'Clean, intimate, elegant' },
  { key: 'vintage_funk', label: 'Vintage Funk', hint: 'Groovy bass, retro brass' },
  { key: 'drill_dark', label: 'Dark Drill', hint: 'Dark 808s, gritty, modern' }
];

const VIBY_FILTERS = [
  { key: 'none', label: 'None' },
  { key: 'neon_glow', label: 'Neon Glow' },
  { key: 'cinema_warm', label: 'Cinema Warm' },
  { key: 'vhs', label: 'VHS' },
  { key: 'bw_noir', label: 'Noir B&W' },
  { key: 'pastel_dream', label: 'Pastel Dream' },
  { key: 'crisp_hdr', label: 'Crisp HDR' }
];

const VIBY_EFFECTS = [
  { key: 'beat_flash', label: 'Beat Flash' },
  { key: 'zoom_punch', label: 'Zoom Punch' },
  { key: 'rgb_split', label: 'RGB Split' },
  { key: 'shake', label: 'Shake' },
  { key: 'motion_blur', label: 'Motion Blur' },
  { key: 'bokeh', label: 'Bokeh' },
  { key: 'film_grain', label: 'Film Grain' },
  { key: 'vignette', label: 'Vignette' },
  { key: 'light_leaks', label: 'Light Leaks' },
  { key: 'sparkles', label: 'Sparkles' }
];

const WV_DEFAULT_VIBY = {
  music_mode: 'auto',      // none | auto | preset | prompt
  music_preset: 'trending_pop',
  music_prompt: '',
  filter: 'none',
  filter_intensity: 60,    // 0..100
  effects: [],             // array of effect keys
  beat_sync: true,
  cut_energy: 70           // 0..100
};

const wvNormalizeViby = (raw) => {
  const o = (raw && typeof raw === 'object') ? raw : {};
  const next = {
    music_mode: String(o.music_mode || WV_DEFAULT_VIBY.music_mode),
    music_preset: String(o.music_preset || WV_DEFAULT_VIBY.music_preset),
    music_prompt: String(o.music_prompt || ''),
    filter: String(o.filter || WV_DEFAULT_VIBY.filter),
    filter_intensity: wvClamp(o.filter_intensity ?? WV_DEFAULT_VIBY.filter_intensity, 0, 100),
    effects: Array.isArray(o.effects) ? o.effects.map((x) => String(x)) : [],
    beat_sync: !!(o.beat_sync ?? WV_DEFAULT_VIBY.beat_sync),
    cut_energy: wvClamp(o.cut_energy ?? WV_DEFAULT_VIBY.cut_energy, 0, 100)
  };
  // Keep only known values when possible
  if (!VIBY_MUSIC_PRESETS.some((x) => x.key === next.music_preset)) next.music_preset = WV_DEFAULT_VIBY.music_preset;
  if (!VIBY_FILTERS.some((x) => x.key === next.filter)) next.filter = WV_DEFAULT_VIBY.filter;
  next.effects = next.effects.filter((k) => VIBY_EFFECTS.some((x) => x.key === k));
  return next;
};



// ===== Export templates registry (front) ============================
// NOTE: we intentionally keep only the categories that actually contain templates (no empty tabs).
const EXPORT_TEMPLATES = [
  // ✅ Core / For you
  { id: 'classic', title: 'Classic', desc: 'Sequential montage', badge: 'AUTO', cat: 'for_you' },
  { id: 'grid_2x2', title: 'Grid 2×2', desc: '4 tiles on screen', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'split_v', title: 'Split Vertical', desc: '2 side by side', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'split_h', title: 'Split Horizontal', desc: '2 top / bottom', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'hero_3', title: 'Hero + 3', desc: 'Big + stacked', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'strips_3', title: '3 Strips', desc: 'Vertical bands', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'corners_float', title: 'Floating Corners', desc: 'Blur bg + tiles', badge: 'LAYOUT', cat: 'for_you' },

  // ✅ FX / Ambiances (single media + vibe)
  { id: 'fx_moonrise',    title: 'Moonrise',     desc: 'Cinematic glow + default music', badge: 'FX', cat: 'fx' },
  { id: 'fx_beat_punch',  title: 'Beat Punch',   desc: 'Punchy cuts + upbeat bed',       badge: 'FX', cat: 'fx' },
  { id: 'fx_dreamy',      title: 'Dreamy',       desc: 'Soft blur + warm ambience',      badge: 'FX', cat: 'fx' },
  { id: 'fx_glitch',      title: 'Glitch',       desc: 'Digital chroma shift + noise',   badge: 'FX', cat: 'fx' },
  { id: 'fx_vhs',         title: 'VHS',          desc: 'Retro noise + vignette',         badge: 'FX', cat: 'fx' },


  // 🎧 Viby (TikTok / CapCut-like vibe presets: music + filters + effects)
  { id: 'viby_trend_pop',    title: 'Viby • Trend Pop',     desc: 'Trending pop bed + neon glow + beat hits',     badge: 'VIBY', cat: 'viby', baseId: 'fx_beat_punch',
    viby: { music_mode: 'preset', music_preset: 'trending_pop', music_prompt: '', filter: 'neon_glow', filter_intensity: 72, effects: ['beat_flash','zoom_punch','vignette'], beat_sync: true, cut_energy: 82 },
    style: { fx: 'neon', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_lofi_chill',   title: 'Viby • Lo‑Fi Chill',    desc: 'Lo‑fi chill bed + pastel dream + soft cuts',  badge: 'VIBY', cat: 'viby', baseId: 'fx_dreamy',
    viby: { music_mode: 'preset', music_preset: 'lofi_chill', music_prompt: '', filter: 'pastel_dream', filter_intensity: 68, effects: ['bokeh','film_grain'], beat_sync: false, cut_energy: 45 },
    style: { fx: 'dreamy', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_afro_sunset',  title: 'Viby • Afro Sunset',   desc: 'Afro groove + warm cinema + light leaks',     badge: 'VIBY', cat: 'viby', baseId: 'fx_fireflies',
    viby: { music_mode: 'preset', music_preset: 'afro_groove', music_prompt: '', filter: 'cinema_warm', filter_intensity: 74, effects: ['light_leaks','sparkles','vignette'], beat_sync: true, cut_energy: 70 },
    style: { fx: 'fireflies', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_edm_drop',     title: 'Viby • EDM Drop',      desc: 'Build + drop + strobe cuts + beat sync',      badge: 'VIBY', cat: 'viby', baseId: 'fx_beat_punch',
    viby: { music_mode: 'preset', music_preset: 'edm_drop', music_prompt: '', filter: 'crisp_hdr', filter_intensity: 62, effects: ['beat_flash','shake','motion_blur'], beat_sync: true, cut_energy: 92 },
    style: { fx: 'prism', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_vhs_beat',     title: 'Viby • VHS Beat',      desc: 'VHS filter + RGB split + punchy rhythm',      badge: 'VIBY', cat: 'viby', baseId: 'fx_vhs',
    viby: { music_mode: 'preset', music_preset: 'vintage_funk', music_prompt: '', filter: 'vhs', filter_intensity: 78, effects: ['rgb_split','film_grain','vignette'], beat_sync: true, cut_energy: 76 },
    style: { fx: 'vhs', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_glitch_drop',  title: 'Viby • Glitch Drop',   desc: 'Glitch FX + dark drill + sharp cuts',         badge: 'VIBY', cat: 'viby', baseId: 'fx_glitch',
    viby: { music_mode: 'preset', music_preset: 'drill_dark', music_prompt: '', filter: 'crisp_hdr', filter_intensity: 55, effects: ['rgb_split','shake','beat_flash'], beat_sync: true, cut_energy: 88 },
    style: { fx: 'glitch', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_cinematic',    title: 'Viby • Cinematic',     desc: 'Cinematic score + warm grade + slow motion',  badge: 'VIBY', cat: 'viby', baseId: 'fx_moonrise',
    viby: { music_mode: 'preset', music_preset: 'cinematic', music_prompt: '', filter: 'cinema_warm', filter_intensity: 70, effects: ['vignette','film_grain'], beat_sync: false, cut_energy: 40 },
    style: { fx: 'moonrise', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_minimal',      title: 'Viby • Minimal',       desc: 'Minimal piano + clean look + gentle cuts',    badge: 'VIBY', cat: 'viby', baseId: 'classic',
    viby: { music_mode: 'preset', music_preset: 'minimal_piano', music_prompt: '', filter: 'none', filter_intensity: 60, effects: [], beat_sync: false, cut_energy: 35 },
    style: { fx: 'none', music: { mode: 'auto', prompt: '' } }
  },

  // ✨ New FX variations (mapped to existing bases)
  { id: 'fx_neon_night',  title: 'Neon Night',   desc: 'Neon wash + glossy glow',        badge: 'FX', cat: 'fx', baseId: 'fx_beat_punch', style: { fx: 'neon', music: { mode: 'auto', prompt: 'neon synthwave bed, punchy, clean, 100-120bpm' } } },
  { id: 'fx_film_grain',  title: 'Film Grain',   desc: 'Soft grain + cinematic fade',    badge: 'FX', cat: 'fx', baseId: 'fx_moonrise', style: { fx: 'film',  music: { mode: 'auto', prompt: 'cinematic ambient, warm pads, subtle hits' } } },
  { id: 'fx_bokeh_burst', title: 'Bokeh Burst',  desc: 'Bokeh sparkle + smooth drift',   badge: 'FX', cat: 'fx', baseId: 'fx_dreamy',   style: { fx: 'bokeh', music: { mode: 'auto', prompt: 'dreamy lofi, gentle sparkle, slow' } } },
  { id: 'fx_prism',       title: 'Prism',        desc: 'Prismatic edges + light leaks',  badge: 'FX', cat: 'fx', baseId: 'fx_glitch',   style: { fx: 'prism', music: { mode: 'auto', prompt: 'future bass minimal, airy, upbeat' } } },
  { id: 'fx_fireflies',   title: 'Fireflies',    desc: 'Floating lights + twilight tone',badge: 'FX', cat: 'fx', baseId: 'fx_moonrise', style: { fx: 'fireflies', music: { mode: 'auto', prompt: 'evening ambient, soft piano, shimmer' } } },
  { id: 'fx_noir',        title: 'Noir',         desc: 'High contrast + noir vignette',  badge: 'FX', cat: 'fx', baseId: 'fx_vhs',      style: { fx: 'noir', music: { mode: 'auto', prompt: 'noir jazz, brushed drums, smoky, slow' } } },

  // 🎛 Backgrounds (multi‑tile with animated / image-like backdrops)
  { id: 'bg_gradient_wave', title: 'Gradient Wave',  desc: 'Animated gradient + floating tiles', badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'animated-gradient', name: 'wave' } } },
  { id: 'bg_neon_grid',     title: 'Neon Grid',      desc: 'Neon grid + modern glass tiles',   badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'neon-grid' }, fx: 'neon' } },
  { id: 'bg_particles',     title: 'Particles',      desc: 'Particle field + depth layers',     badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'particles' } } },
  { id: 'bg_blur_cover',    title: 'Blur Cover',     desc: 'Blurred cover + crisp cards',       badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'blur-from-media', source: 'first' } } },
  { id: 'bg_paper_cut',     title: 'Paper Cut',      desc: 'Paper texture + taped frames',      badge: 'BG', cat: 'backgrounds', baseId: 'hero_3',        style: { background: { type: 'paper-texture' } } },
  { id: 'bg_sunburst',      title: 'Sunburst',       desc: 'Radial burst + punchy layout',      badge: 'BG', cat: 'backgrounds', baseId: 'grid_2x2',      style: { background: { type: 'sunburst' } } },
  { id: 'bg_glassmorphism', title: 'Glassmorphism',  desc: 'Frosted panels + soft glow',         badge: 'BG', cat: 'backgrounds', baseId: 'hero_3',        style: { background: { type: 'glass' }, fx: 'soft-glow' } },

  // 🎞 Animated (UI‑preview + motion defaults; backend remains compatible via baseId)
  { id: 'ani_carousel',     title: 'Carousel',       desc: 'Swipe rhythm + card carousel',       badge: 'ANIM', cat: 'animated', baseId: 'split_v',      style: { motion: 'capcut' } },
  { id: 'ani_flash_cards',  title: 'Flash Cards',    desc: 'Fast flips + crisp frames',          badge: 'ANIM', cat: 'animated', baseId: 'grid_2x2',     style: { motion: 'capcut', transition: 'zoom' } },
  { id: 'ani_parallax',     title: 'Parallax',       desc: 'Depth layers + slow drift',          badge: 'ANIM', cat: 'animated', baseId: 'hero_3',       style: { motion: 'subtle', fx: 'parallax' } },
  { id: 'ani_pop_stickers', title: 'Pop Stickers',   desc: 'Stickers + playful HUD',             badge: 'ANIM', cat: 'animated', baseId: 'corners_float',style: { motion: 'capcut', fx: 'stickers' } },
  { id: 'ani_comic_panels', title: 'Comic Panels',   desc: 'Panel slides + bold vibe',           badge: 'ANIM', cat: 'animated', baseId: 'strips_3',     style: { motion: 'capcut', fx: 'comic' } },
  { id: 'ani_timeline',     title: 'Timeline',       desc: 'Progress line + story beats',        badge: 'ANIM', cat: 'animated', baseId: 'hero_3',       style: { motion: 'subtle' } },
  { id: 'ani_spotlight',    title: 'Spotlight',      desc: 'Spotlight sweep + vignette',         badge: 'ANIM', cat: 'animated', baseId: 'split_h',      style: { motion: 'subtle', fx: 'spotlight' } },

  // 🧩 Collage variants (different structures, mapped to known bases)
  { id: 'collage_diagonal', title: 'Diagonal Split', desc: 'Diagonal cut + modern collage',      badge: 'LAYOUT', cat: 'for_you', baseId: 'split_v',      style: { layout: 'diagonal' } },
  { id: 'collage_stack',    title: 'Stacked Cards',  desc: 'Stacked frames + depth shadows',     badge: 'LAYOUT', cat: 'for_you', baseId: 'corners_float',style: { layout: 'stack' } },
  { id: 'collage_circle',   title: 'Circle Collage', desc: 'Circular crops + clean grid',        badge: 'LAYOUT', cat: 'for_you', baseId: 'grid_2x2',     style: { layout: 'circle' } },

  // 🪩 Retro / Editorial
  { id: 'retro_polaroid',   title: 'Polaroid Stack', desc: 'Polaroid frames + tape',             badge: 'RETRO', cat: 'retro', baseId: 'corners_float', style: { fx: 'polaroid' } },
  { id: 'retro_magazine',   title: 'Magazine',       desc: 'Editorial blocks + headlines',      badge: 'RETRO', cat: 'retro', baseId: 'hero_3',       style: { fx: 'editorial' } },
  { id: 'retro_super8',     title: 'Super 8',        desc: 'Warm flicker + film gate',          badge: 'RETRO', cat: 'retro', baseId: 'fx_vhs',       style: { fx: 'super8', music: { mode: 'auto', prompt: 'retro indie, warm, tape wow' } } },

  // "Wall" templates (single media + UI overlays)
  { id: 'wall_sale',   title: 'Sale Poster',    desc: 'Promo sticker + CTA',     badge: 'WALL', cat: 'business' },
  { id: 'wall_brand',  title: 'Brand Showcase', desc: 'Title + subtitle blocks', badge: 'WALL', cat: 'business' },
  { id: 'wall_quote',  title: 'Quote Story',    desc: 'Centered quote',          badge: 'WALL', cat: 'for_you' },
  { id: 'wall_lyrics', title: 'Lyrics Flow',    desc: 'Line-by-line text',       badge: 'WALL', cat: 'lyrics' },
  { id: 'wall_meme',   title: 'Meme Caption',   desc: 'Top/bottom captions',     badge: 'WALL', cat: 'memes' },
  { id: 'wall_retro',  title: 'Retro Frame',    desc: 'Film border + timestamp', badge: 'WALL', cat: 'retro' },
  { id: 'wall_speed',  title: 'Speed Cut',      desc: 'Bold HUD overlay',        badge: 'WALL', cat: 'speed' },

  // ✨ New wall styles
  { id: 'wall_podcast', title: 'Podcast Cover',  desc: 'Title + waveform overlay', badge: 'WALL', cat: 'business', baseId: 'wall_brand', style: { overlay: 'waveform' } },
  { id: 'wall_news',    title: 'News Card',      desc: 'Headline + ticker bar',    badge: 'WALL', cat: 'business', baseId: 'wall_brand', style: { overlay: 'ticker' } },
  { id: 'wall_recipe',  title: 'Recipe Card',    desc: 'Ingredients + badge',      badge: 'WALL', cat: 'business', baseId: 'wall_brand', style: { overlay: 'recipe' } },
  { id: 'wall_event',   title: 'Event Flyer',    desc: 'Date/time + location',     badge: 'WALL', cat: 'business', baseId: 'wall_sale',  style: { overlay: 'event' } },
];

const TEMPLATE_DEFAULTS = {
  // Music + FX templates (auto music, motion, and default transitions)
  fx_moonrise:     { transition: 'dissolve',  motion: 'capcut', hasMusic: true, hasFx: true },
  fx_dreamy:       { transition: 'fade',      motion: 'subtle', hasMusic: true, hasFx: true },
  fx_beat_punch:   { transition: 'zoom',      motion: 'capcut', hasMusic: true, hasFx: true },
  fx_glitch:       { transition: 'vblur',     motion: 'capcut', hasMusic: true, hasFx: true },
  fx_vhs:          { transition: 'wipeleft',  motion: 'subtle', hasMusic: true, hasFx: true },

  // New FX variants (mapped to existing bases)
  fx_neon_night:   { transition: 'zoom',      motion: 'capcut', hasMusic: true, hasFx: true },
  fx_film_grain:   { transition: 'dissolve',  motion: 'subtle', hasMusic: true, hasFx: true },
  fx_bokeh_burst:  { transition: 'fade',      motion: 'subtle', hasMusic: true, hasFx: true },
  fx_prism:        { transition: 'vblur',     motion: 'capcut', hasMusic: true, hasFx: true },
  fx_fireflies:    { transition: 'dissolve',  motion: 'subtle', hasMusic: true, hasFx: true },
  fx_noir:         { transition: 'fadeblack', motion: 'subtle', hasMusic: true, hasFx: true },


  // Viby presets (session-only controls; backend can consume export_viby later)
  viby_trend_pop:  { transition: 'zoom',      motion: 'capcut', hasMusic: true, hasFx: true },
  viby_lofi_chill: { transition: 'fade',      motion: 'subtle', hasMusic: true, hasFx: true },
  viby_afro_sunset:{ transition: 'dissolve',  motion: 'subtle', hasMusic: true, hasFx: true },
  viby_edm_drop:   { transition: 'zoom',      motion: 'capcut', hasMusic: true, hasFx: true },
  viby_vhs_beat:   { transition: 'wipeleft',  motion: 'capcut', hasMusic: true, hasFx: true },
  viby_glitch_drop:{ transition: 'vblur',     motion: 'capcut', hasMusic: true, hasFx: true },
  viby_cinematic:  { transition: 'dissolve',  motion: 'subtle', hasMusic: true, hasFx: true },
  viby_minimal:    { transition: 'fade',      motion: 'subtle', hasMusic: true, hasFx: false },

  // Background templates (multi-tile)
  bg_gradient_wave:{ transition: 'smoothleft', motion: 'subtle', hasFx: true },
  bg_neon_grid:    { transition: 'smoothleft', motion: 'capcut', hasFx: true },
  bg_particles:    { transition: 'dissolve',   motion: 'subtle', hasFx: true },
  bg_blur_cover:   { transition: 'dissolve',   motion: 'subtle', hasFx: true },
  bg_paper_cut:    { transition: 'wipeup',     motion: 'subtle', hasFx: true },
  bg_sunburst:     { transition: 'zoom',       motion: 'capcut', hasFx: true },
  bg_glassmorphism:{ transition: 'fade',       motion: 'subtle', hasFx: true },

  // Animated (preview + motion defaults)
  ani_carousel:    { transition: 'slideleft',  motion: 'capcut' },
  ani_flash_cards: { transition: 'zoom',       motion: 'capcut' },
  ani_parallax:    { transition: 'dissolve',   motion: 'subtle' },
  ani_pop_stickers:{ transition: 'dissolve',   motion: 'capcut' },
  ani_comic_panels:{ transition: 'wipeleft',   motion: 'capcut' },
  ani_timeline:    { transition: 'dissolve',   motion: 'subtle' },
  ani_spotlight:   { transition: 'fadeblack',  motion: 'subtle' },

  // Collage variants
  collage_diagonal:{ transition: 'slideleft',  motion: 'subtle' },
  collage_stack:   { transition: 'dissolve',   motion: 'subtle' },
  collage_circle:  { transition: 'fade',       motion: 'subtle' },

  // Retro / editorial
  retro_polaroid:  { transition: 'fade',       motion: 'subtle' },
  retro_magazine:  { transition: 'dissolve',   motion: 'subtle' },
  retro_super8:    { transition: 'wipeleft',   motion: 'subtle', hasMusic: true, hasFx: true },

  // Layout templates (motion+transition only unless you also pick a music file)
  corners_float:   { transition: 'smoothleft', motion: 'subtle' },
  wall_quote:      { transition: 'fadeblack',  motion: 'subtle' },
  split_v:         { transition: 'slideleft',  motion: 'subtle' },
  split_h:         { transition: 'slideup',    motion: 'subtle' },
  hero_3:          { transition: 'dissolve',   motion: 'subtle' },
  strips_3:        { transition: 'wipeup',     motion: 'subtle' },

  // New wall styles (mapped)
  wall_podcast:    { transition: 'fade',       motion: 'subtle' },
  wall_news:       { transition: 'wipeleft',   motion: 'subtle' },
  wall_recipe:     { transition: 'fade',       motion: 'subtle' },
  wall_event:      { transition: 'zoom',       motion: 'capcut' },
};



const EXPORT_TPL_CAT_LABELS = {
  for_you: 'Pour toi',
  ai_generated: 'IA GENERATED',
  viby: 'Viby',
  backgrounds: 'Arrière‑plans',
  animated: 'Animés',
  fx: 'Ambiances / FX',
  business: 'Entreprise',
  lyrics: 'Paroles',
  memes: 'Mèmes',
  speed: 'Vitesse',
  retro: 'Édition rétro',
};

// Order of categories shown as chips (only non-empty categories will appear)
const EXPORT_TPL_CAT_ORDER = ['for_you', 'ai_generated', 'viby', 'animated', 'backgrounds', 'fx', 'retro', 'business', 'lyrics', 'memes', 'speed'];


/* ================= Main Component ================= */

/* =========================================================================================
 * WEB SCREEN TEMPORARILY DISABLED — MOBILE-FIRST EXPERIENCE
 * The original web implementation is preserved below as JourneyAdminLegacy.
 * ========================================================================================= */
const WV_MOBILE_GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";
const WV_MOBILE_IOS_WAITLIST_URL = "/join-beta";
const WV_MOBILE_LANG_KEY = "weavevo_lang";

const WV_MOBILE_SCREEN_COPY = {
  admin: {
    en: {
      eyebrow: "MOBILE PROJECT MANAGEMENT",
      title: "Manage your Weave from the mobile app.",
      body: "Project setup, contributors, scenario management, moderation and delivery controls are designed for a more focused mobile workflow.",
    },
    fr: {
      eyebrow: "GESTION MOBILE DU PROJET",
      title: "Gérez votre Weave depuis l’app mobile.",
      body: "La gestion du projet, des contributeurs, du scénario, de la modération et de la livraison est optimisée pour une expérience mobile plus fluide.",
    },
  },
  studio: {
    en: {
      eyebrow: "MOBILE STUDIO",
      title: "Finish your Weave in the mobile Studio.",
      body: "Media selection, layouts, transitions, music, effects and final export are easier to shape from the mobile Studio experience.",
    },
    fr: {
      eyebrow: "STUDIO MOBILE",
      title: "Finalisez votre Weave dans le Studio mobile.",
      body: "La sélection des médias, les layouts, transitions, musiques, effets et l’export final sont conçus pour être plus fluides depuis le Studio mobile.",
    },
  },
  contributor: {
    en: {
      eyebrow: "MOBILE CONTRIBUTION",
      title: "Contribute to this Weave from your phone.",
      body: "Capture, upload, slot guidance, AI review and collaboration are designed around the device already in your hand.",
    },
    fr: {
      eyebrow: "CONTRIBUTION MOBILE",
      title: "Contribuez à cette Weave depuis votre téléphone.",
      body: "La capture, l’upload, le guidage par slot, la revue IA et la collaboration sont pensés autour du mobile.",
    },
  },
};

export default function JourneyAdmin() {
  const [wvMobileLang, setWvMobileLang] = useState(() => {
    try {
      const raw = localStorage.getItem(WV_MOBILE_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
      return String(raw).toLowerCase().startsWith("fr") ? "fr" : "en";
    } catch (_) {
      return "en";
    }
  });

  useEffect(() => {
    const refreshLang = () => {
      try {
        const raw = localStorage.getItem(WV_MOBILE_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
        setWvMobileLang(String(raw).toLowerCase().startsWith("fr") ? "fr" : "en");
      } catch (_) { }
    };
    window.addEventListener("storage", refreshLang);
    window.addEventListener("focus", refreshLang);
    return () => {
      window.removeEventListener("storage", refreshLang);
      window.removeEventListener("focus", refreshLang);
    };
  }, []);

  const baseCopy = WV_MOBILE_SCREEN_COPY.admin[wvMobileLang] || WV_MOBILE_SCREEN_COPY.admin.en;
  const labels = wvMobileLang === "fr"
    ? {
        google: "Télécharger sur Google Play",
        apple: "App Store / iOS",
        soon: "Bientôt disponible — rejoindre la bêta",
      }
    : {
        google: "Get it on Google Play",
        apple: "App Store / iOS",
        soon: "Coming soon — join the beta",
      };

  return (
    <div className="wv-mobile-screen-gate">
      <div className="wv-mobile-screen-orb wv-mobile-screen-orb-a" aria-hidden="true" />
      <div className="wv-mobile-screen-orb wv-mobile-screen-orb-b" aria-hidden="true" />

      <section className="wv-mobile-screen-card" aria-labelledby="wv-mobile-screen-title">
        <div className="wv-mobile-screen-visual">
          <img
            src={web2mobile}
            alt="Weavevo web and mobile experience"
            className="wv-mobile-screen-visual-img"
          />
        </div>

        <div className="wv-mobile-screen-copy">
          <span className="wv-mobile-screen-eyebrow">{baseCopy.eyebrow}</span>
          <h1 id="wv-mobile-screen-title">{baseCopy.title}</h1>
          <p className="wv-mobile-screen-lead">{baseCopy.body}</p>


          <div className="wv-mobile-screen-actions">
            <a
              className="wv-mobile-screen-store is-google"
              href={WV_MOBILE_GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={labels.google}
            >
              <i className="bi bi-google-play" />
              <span>{labels.google}</span>
            </a>

            <a
              className="wv-mobile-screen-store is-apple"
              href={WV_MOBILE_IOS_WAITLIST_URL}
              aria-label={`${labels.apple} — ${labels.soon}`}
            >
              <i className="bi bi-apple" />
              <span>
                <strong>{labels.apple}</strong>
                <small>{labels.soon}</small>
              </span>
            </a>
          </div>

        </div>
      </section>

      <style>{`
        .wv-mobile-screen-gate,
        .wv-mobile-screen-gate * { box-sizing: border-box; }
        .wv-mobile-screen-gate {
          position: relative;
          min-height: calc(100vh - 70px);
          width: 100%;
          overflow: hidden;
          display: grid;
          place-items: center;
          padding: clamp(28px, 5vw, 72px);
          background:
            radial-gradient(circle at 18% 18%, rgba(70,154,255,.14), transparent 32%),
            radial-gradient(circle at 82% 74%, rgba(139,92,246,.12), transparent 34%),
            linear-gradient(180deg, #fbfdff 0%, #f5f8ff 100%);
          color: #08111e;
        }
        .wv-mobile-screen-orb {
          position: absolute;
          width: 340px;
          height: 340px;
          border-radius: 50%;
          filter: blur(60px);
          opacity: .32;
          pointer-events: none;
        }
        .wv-mobile-screen-orb-a { background:#5ab7ff; top:-150px; left:-80px; }
        .wv-mobile-screen-orb-b { background:#8b5cf6; right:-120px; bottom:-170px; }
        .wv-mobile-screen-card {
          position: relative;
          z-index: 1;
          width: min(1040px, 100%);
          min-height: 560px;
          display: grid;
          grid-template-columns: minmax(260px,.78fr) minmax(0,1.22fr);
          align-items: center;
          gap: clamp(34px,6vw,86px);
          padding: clamp(34px,5vw,70px);
          border: 1px solid rgba(10,32,70,.09);
          border-radius: 34px;
          background: rgba(255,255,255,.91);
          box-shadow: 0 35px 100px rgba(21,53,95,.12);
          backdrop-filter: blur(18px);
        }
        .wv-mobile-screen-visual {
          width:min(360px,82vw);
          justify-self:center;
          border-radius:28px;
          overflow:hidden;
          background:#eef3f8;
          box-shadow:0 28px 64px rgba(8,19,37,.18);
          transform:rotate(-1.5deg);
        }
        .wv-mobile-screen-visual-img {
          display:block;
          width:100%;
          height:auto;
          object-fit:cover;
        }
        .wv-mobile-screen-copy { min-width:0; }
        .wv-mobile-screen-eyebrow {
          display:inline-flex;
          padding:7px 11px;
          border-radius:999px;
          background:#eef8ff;
          color:#087ea4;
          font-size:12px;
          font-weight:850;
          letter-spacing:.13em;
        }
        .wv-mobile-screen-copy h1 {
          margin:18px 0;
          max-width:720px;
          font-size:clamp(2.5rem,5vw,4.8rem);
          line-height:.98;
          letter-spacing:-.055em;
          color:#07111f;
        }
        .wv-mobile-screen-lead {
          max-width:700px;
          margin:0;
          color:#415169;
          font-size:clamp(1rem,1.45vw,1.17rem);
          line-height:1.72;
        }
        .wv-mobile-screen-actions { display:flex; flex-wrap:wrap; gap:12px; margin-top:28px; }
        .wv-mobile-screen-store {
          min-height:58px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:11px;
          padding:10px 18px;
          border-radius:16px;
          text-decoration:none!important;
          font-weight:800;
          transition:transform .18s ease, box-shadow .18s ease;
        }
        .wv-mobile-screen-store:hover { transform:translateY(-2px); }
        .wv-mobile-screen-store > i { font-size:1.5rem; }
        .wv-mobile-screen-store.is-google {
          background:#08111e;
          color:#fff;
          box-shadow:0 14px 28px rgba(8,17,30,.18);
        }
        .wv-mobile-screen-store.is-apple {
          border:1px solid #d8e1ec;
          background:#fff;
          color:#08111e;
        }
        .wv-mobile-screen-store.is-apple span { display:grid; line-height:1.15; }
        .wv-mobile-screen-store.is-apple small {
          margin-top:3px;
          color:#6f7d91;
          font-size:10px;
          font-weight:700;
        }
        @media (max-width:820px) {
          .wv-mobile-screen-gate { padding:22px 16px; }
          .wv-mobile-screen-card {
            grid-template-columns:1fr;
            padding:30px 22px;
            border-radius:26px;
            text-align:center;
          }
          .wv-mobile-screen-visual { width:min(330px,88vw); transform:none; }
          .wv-mobile-screen-copy h1,
          .wv-mobile-screen-lead { margin-left:auto; margin-right:auto; }
          .wv-mobile-screen-actions { justify-content:center; }
        }
      `}</style>
    </div>
  );
}

/* Original web implementation preserved below. */

function JourneyAdminLegacy() {
  const { id } = useParams();
  const projectId = id;
  const { token } = useAuth();
  const { addToast, Toasts } = useToasts();
  const [niceLoader, setNiceLoader] = useState({ visible: false, text: '' });
  const [mediaById, setMediaById] = useState({});
  const { token: kc_token } = useAuth();
  // --- Post-export modal state ---
  // --- Post-export modal state ---
  const [postExportOpen, setPostExportOpen] = useState(false);
  const [postExportUrl, setPostExportUrl] = useState('');
  const [postExportMontageId, setPostExportMontageId] = useState(null);
  // ... tes states existants

  // ✅ Limite d’affichage pour "Ma bibliothèque"
  const [libRenderLimit, setLibRenderLimit] = useState(4);



  // modal toggles
  const [optSaveAsDraft, setOptSaveAsDraft] = useState(false);
  const [optShareInChat, setOptShareInChat] = useState(true);
  const [optShareText, setOptShareText] = useState('Voici la vidéo générée 🎬');
  const [optCloseProject, setOptCloseProject] = useState(false);
  const [optCloseStatus, setOptCloseStatus] = useState('Clôturé');
  const [step, setStep] = useState('scenario'); // 'scenario' | 'contribute' | 'montage' | 'export'

  // Scenario / Project
  const [scenario, setScenario] = useState(null);
  const [scenarioForm, setScenarioForm] = useState({ title: '', description: '', steps: [] });
  const [scenarioSaving, setScenarioSaving] = useState(false);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [project, setProject] = useState(null);
  const [montageDrafts, setMontageDrafts] = useState([]);
  const [restoredMontageId, setRestoredMontageId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [visibility, setVisibility] = useState('PRIVATE');
  const [lastUsedExport, setLastUsedExport] = useState(null);
  // Forum
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const chatListRef = useRef(null);

  // Media
  const [projectMedia, setProjectMedia] = useState([]);

  const [myMedia, setMyMedia] = useState([]);
  const [mediaFilter, setMediaFilter] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('image');
  const [useExistingMedia, setUseExistingMedia] = useState(true);

  // ✅ UX perf: on limite l'affichage initial de la bibliothèque
  const LIB_CHUNK = 12;


  useEffect(() => {
    // Reset à chaque changement important pour éviter un gros re-render
    setLibRenderLimit(LIB_CHUNK);
  }, [mediaFilter, mediaTypeFilter, useExistingMedia, id]);

  // Library perf state (paged, non-blocking)
  const PAGE = 24;
  const [libOffset, setLibOffset] = useState(0);
  const [libHasMore, setLibHasMore] = useState(true);
  const [libLoading, setLibLoading] = useState(false);
  const [libError, setLibError] = useState(null);
  const [isLibPending, startLibTransition] = useTransition();
  const sentinelRef = useRef(null);

  // Upload form
  const [uplTitle, setUplTitle] = useState('');
  const [uplLocation, setUplLocation] = useState('');
  const [uplDescription, setUplDescription] = useState('');
  const [uplFile, setUplFile] = useState(null);
  const [uplComment, setUplComment] = useState('');
  const [uplTags, setUplTags] = useState([]);

  // Montage + Export
  const [selectedMontageId, setSelectedMontageId] = useState(null);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [montageMode, setMontageMode] = useState('IA'); // Manuel | IA (mode assisté) | Advanced
  // ===== Studio simplification (TEMP) =============================
  // Weavevo Studio is NOT a video editor: we focus on weaving via Assisted mode + Templates.
  // Manual montage + Advanced (Twick) are temporarily disabled for now (code kept for later).
  const STUDIO_ASSISTED_ONLY = true;

  const montageModeLabel = (montageMode === 'Manuel'
    ? 'Mode manuel'
    : (montageMode === 'Advanced' ? 'Advanced mode' : 'Mode assisté'));

  // Force Assisted mode when Studio is simplified (prevents old saved drafts from switching modes)
  useEffect(() => {
    if (!STUDIO_ASSISTED_ONLY) return;
    if (montageMode !== 'IA') setMontageMode('IA');
  }, [STUDIO_ASSISTED_ONLY, montageMode]);

  const [collapsedStepsMontage, setCollapsedStepsMontage] = useState({});
  const [montageConfigs, setMontageConfigs] = useState([]);
  const [montageConfigChoice, setMontageConfigChoice] = useState('NEW'); // 'NEW' | 'LOAD'
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [manualVideoFileName, setManualVideoFileName] = useState(null);
  const [uploadedManualVideoFile, setUploadedManualVideoFile] = useState(null);


  // --- ADD: refs for manual/AI single-save guards
  const manualObjectUrlRef = useRef(null);
  const manualSaveOnceRef = useRef(false);
  const aiSaveOnceRef = useRef(false);
  const [theme, setTheme] = useState('Documentaire');
  const [voiceGender, setVoiceGender] = useState('FEMALE');
  const [voiceLang, setVoiceLang] = useState('fr-FR');
  const [voiceOverFile, setVoiceOverFile] = useState(null);
  const [voiceOverFileName, setVoiceOverFileName] = useState(null);
  const [musicFile, setMusicFile] = useState(null);
  const [audioMixOption, setAudioMixOption] = useState('voice+music');
  const [musicVolume, setMusicVolume] = useState(20);
  const [voiceMode, setVoiceMode] = useState('global');
  // Transition entre les médias (partagée montage + export)
  const [transition, setTransition] = useState('none');
  const [perStepVoiceMap, setPerStepVoiceMap] = useState({});
  const [aiPlan, setAiPlan] = useState('');
  const [finalVideoUrl, setFinalVideoUrl] = useState('');
  const [busy, setBusy] = useState(false);

  // Export options
  const [exportPreset, setExportPreset] = useState('youtube_1080p_16_9');
  const [exportRes, setExportRes] = useState('1920x1080');
  const [exportFPS, setExportFPS] = useState(30);
  const [exportFormat, setExportFormat] = useState('mp4');
  const [exportBitrate, setExportBitrate] = useState('6M');
  const [exportWatermarkText, setExportWatermarkText] = useState('');
  const [exportAspect, setExportAspect] = useState('16:9');
    const [exportTemplate, setExportTemplate] = useState('classic'); // 'classic' | templates

  // Per-user template overrides (session only; does NOT update templates in DB)
  const [tplOverrides, setTplOverrides] = useState({});
  const [tplOverrideOpen, setTplOverrideOpen] = useState(false);
  const [tplOverrideDraft, setTplOverrideDraft] = useState({ transition: 'none', motion: 'capcut' });


  
  // Viby (session-only). No DB writes — exported in payload for future engines.
  const [vibyOverrides, setVibyOverrides] = useState({});
  const [vibyOpen, setVibyOpen] = useState(false);
  const [vibyMusicOpen, setVibyMusicOpen] = useState(false);
  const [vibyDraft, setVibyDraft] = useState(wvNormalizeViby(WV_DEFAULT_VIBY));

// Export media order (before generating)
  const [exportMediaOrder, setExportMediaOrder] = useState([]);
  const [exportOrderTouched, setExportOrderTouched] = useState(false);
  const [exportDragIndex, setExportDragIndex] = useState(null);
  const [exportDragOverIndex, setExportDragOverIndex] = useState(null);

  // Default: random order (user can override by reordering)
  useEffect(() => {
    const base = Array.isArray(selectedMediaIds) ? selectedMediaIds : [];
    if (!base.length) {
      setExportMediaOrder([]);
      setExportOrderTouched(false);
      return;
    }

    if (exportOrderTouched) {
      // Keep current order for existing IDs, append any newly selected ones.
      setExportMediaOrder((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const baseSet = new Set(base.map((x) => String(x)));
        const kept = prevArr.filter((x) => baseSet.has(String(x)));
        const keptSet = new Set(kept.map((x) => String(x)));
        const missing = base.filter((x) => !keptSet.has(String(x)));
        return missing.length ? [...kept, ...missing] : kept;
      });
      return;
    }

    setExportMediaOrder(wvShuffleCopy(base));
    setExportOrderTouched(false);
  }, [selectedMediaIds]);


  // Runway AI (beta) — calls backend /runway/* which proxies Runway API (key stays server-side)
  const [runwayEnabled, setRunwayEnabled] = useState(false);
  const RUNWAY_UNLOCK_PASSWORD = 'Leoni123+$'; // 🔒 Front-only gate (replace with your secret)

  const [runwayUnlocked, setRunwayUnlocked] = useState(() => {
    try { return sessionStorage.getItem('wv_runway_unlocked') === '1'; } catch (e) { return false; }
  });
  const [runwayPassInput, setRunwayPassInput] = useState('');
  const [runwayPassError, setRunwayPassError] = useState('');

  const [runwayMode, setRunwayMode] = useState('auto'); // auto | image_to_video | video_to_video | text_to_video
  const [runwaySeedId, setRunwaySeedId] = useState('');
  const [runwayRatio, setRunwayRatio] = useState('720:1280'); // default vertical (Runway allowed)
  const [runwayDuration, setRunwayDuration] = useState(8); // 2..10 (ignored in video_to_video)
  const [runwayPromptText, setRunwayPromptText] = useState('');
  const [runwayTaskId, setRunwayTaskId] = useState('');
  const [runwayStatus, setRunwayStatus] = useState('');
  const [runwayOutputUrl, setRunwayOutputUrl] = useState('');


  const unlockRunway = () => {
    const expected = String(RUNWAY_UNLOCK_PASSWORD || '');
    const provided = String(runwayPassInput || '');
    if (!expected || expected === 'CHANGE_ME_RUNWAY_LOCK') {
      setRunwayPassError('Définis RUNWAY_UNLOCK_PASSWORD dans le code.');
      try { addToast('Définis RUNWAY_UNLOCK_PASSWORD dans le code.', 'warning'); } catch (e) {}
      return;
    }
    if (provided === expected) {
      setRunwayUnlocked(true);
      setRunwayPassError('');
      try { sessionStorage.setItem('wv_runway_unlocked', '1'); } catch (e) {}
      try { addToast('Runway déverrouillé.', 'success'); } catch (e) {}
    } else {
      setRunwayPassError('Mot de passe incorrect.');
      try { addToast('Mot de passe incorrect.', 'danger'); } catch (e) {}
    }
  };

  const lockRunway = () => {
    setRunwayUnlocked(false);
    setRunwayEnabled(false);
    setRunwayPassInput('');
    setRunwayPassError('');
    try { sessionStorage.removeItem('wv_runway_unlocked'); } catch (e) {}
  };

  const [runwayOrgInfo, setRunwayOrgInfo] = useState('');
  const [runwayBusy, setRunwayBusy] = useState(false);
  const runwayPollRef = useRef(null);



// Auto-apply template defaults (or user overrides) on template change (session only; no DB writes)
useEffect(() => {
  // 1) User override (session-only)
  const o = (tplOverrides && tplOverrides[exportTemplate]) || null;
  if (o && typeof o === 'object') {
    if (typeof o.transition === 'string' && o.transition) setTransition(o.transition);
    if (typeof o.motion === 'string' && o.motion) setExportMotion(o.motion);
    return;
  }

  // 2) Template defaults (DB template_json if present, else TEMPLATE_DEFAULTS)
  const baseT = selectedTplBaseDefaults?.transition || (TEMPLATE_DEFAULTS[exportTemplate]?.transition || '');
  const baseM = selectedTplBaseDefaults?.motion || (TEMPLATE_DEFAULTS[exportTemplate]?.motion || '');

  if (transition === 'none' && baseT) setTransition(baseT);
  if ((exportMotion === 'none' || exportMotion === 'capcut') && baseM && baseM !== exportMotion) {
    setExportMotion(baseM);
  }
}, [exportTemplate, tplOverrides]); // apply when template changes or when overrides load



// Runway helpers
const aspectToRunwayRatio = (a) => {
  // Runway API (X-Runway-Version: 2024-11-06) validates `ratio` against a fixed list.
  // Use only known-good values (see backend normalizeRunwayRatio as well).
  if (a === '9:16') return '720:1280';
  if (a === '16:9') return '1280:720';
  if (a === '1:1') return '960:960';
  if (a === '4:3') return '640:480';
  if (a === '3:4') return '832:1104';
  return '720:1280';
};

const absolutizeClientUrl = (u) => {
  if (!u) return '';
  const s = String(u);
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('runway://') || s.startsWith('data:')) return s;
  const base = (window.location?.origin || '').replace(/\/+$/, '');
  if (!base) return s;
  return `${base}${s.startsWith('/') ? '' : '/'}${s}`;
};


const defaultRunwayPromptForTemplate = (tpl) => {
  const base =
    "CapCut-style energetic montage, beat-synced cuts, punchy zoom-blur, smooth motion, cinematic color grade, subtle film grain, dynamic transitions (including torn-paper/halftone moments), clean typography, no logos.";
  switch (tpl) {
    case 'fx_paper_tear':
      return base + " Emphasize torn-paper transitions and halftone texture accents.";
    case 'fx_moonrise':
      return base + " Dreamy lunar vibe, neon highlights, soft bloom, gentle parallax.";
    case 'fx_parallax':
      return base + " Strong parallax depth, layered motion, micro-zooms on beats.";
    case 'fx_glitchbeat':
      return base + " Glitch + strobe accents on beats, punchy rhythm, high energy.";
    default:
      return base;
  }
};

// Auto-fill seed + prompt when enabling Runway
useEffect(() => {
  if (!runwayEnabled) return;

  // pick first selected media as seed if empty
  const first = (selectedMediaIds && selectedMediaIds.length) ? String(selectedMediaIds[0]) : '';
  if (!runwaySeedId && first) setRunwaySeedId(first);

  // align ratio to export aspect (best effort)
  setRunwayRatio((prev) => prev || aspectToRunwayRatio(exportAspect));

  // set default prompt only if empty
  setRunwayPromptText((prev) => (prev && prev.trim() ? prev : defaultRunwayPromptForTemplate(exportTemplate)));
}, [runwayEnabled]);

useEffect(() => {
  if (!runwayEnabled) return;
  // Keep ratio roughly aligned if user hasn't chosen something very different
  setRunwayRatio((prev) => prev || aspectToRunwayRatio(exportAspect));
}, [exportAspect]);

useEffect(() => {
  // Cleanup any pending polling
  return () => {
    if (runwayPollRef.current) {
      clearTimeout(runwayPollRef.current);
      runwayPollRef.current = null;
    }
  };
}, []);


  const [tplPanelOpen, setTplPanelOpen] = useState(true);
  const [tplCategory, setTplCategory] = useState('for_you');

  // NEW: Templates pagination (keeps the list short)
  const [tplPage, setTplPage] = useState(1);
  const [tplPageSize, setTplPageSize] = useState(12);


  // ===== Export Templates (DB / AI) =====
  const [dbExportTemplates, setDbExportTemplates] = useState([]);
  const [aiTplPrompt, setAiTplPrompt] = useState('');
  const [aiTplName, setAiTplName] = useState('');
  const [aiTplDesc, setAiTplDesc] = useState('');
  const [aiTplBaseId, setAiTplBaseId] = useState('grid_2x2');
  const [aiTplFx, setAiTplFx] = useState('none');
  const [aiTplTransition, setAiTplTransition] = useState('fade');
  const [aiTplMotion, setAiTplMotion] = useState('capcut');
  const [aiTplMusicMode, setAiTplMusicMode] = useState('auto'); // none | auto | prompt
  const [aiTplMusicPrompt, setAiTplMusicPrompt] = useState('');
  const [aiTplPassword, setAiTplPassword] = useState('');
  const [aiTplLoading, setAiTplLoading] = useState(false);

  // Batch generation
  const [aiBatchCount, setAiBatchCount] = useState(6);
  const [aiBatchLoading, setAiBatchLoading] = useState(false);
  const [exportMotion, setExportMotion] = useState('capcut'); // 'none' | 'subtle' | 'capcut'
  const [showPlayer, setShowPlayer] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // NEW: export execution mode
  const [exportAsync, setExportAsync] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);

  // ===== TWICK (Advanced editor) persistence =====
  const [twickState, setTwickState] = useState({});
  const [twickDirty, setTwickDirty] = useState(false);
  const [twickLoading, setTwickLoading] = useState(false);
  const [twickSaving, setTwickSaving] = useState(false);
  const [twickSavedAt, setTwickSavedAt] = useState(null);
  const [twickReloadKey, setTwickReloadKey] = useState(0);


  // ===== Network / anti-loop guards (Twick) =====
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const twickHasLoadedOnceRef = useRef(false);
  const twickLoadInFlightRef = useRef(false);
  const twickLoadAbortRef = useRef(null);
  const twickSaveInFlightRef = useRef(false);
  const twickOfflineToastOnceRef = useRef(false);
  const twickLastLoadAttemptRef = useRef(0);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      twickOfflineToastOnceRef.current = false;
    };
    const onOffline = () => setIsOnline(false);

    try {
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
    } catch (_) { /* noop */ }

    return () => {
      try {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
      } catch (_) { /* noop */ }
    };
  }, []);

  const twickUploadInputRef = useRef(null);


  // Contributions collapse
  const [collapsedStepsContrib, setCollapsedStepsContrib] = useState({});
  // --- Drafts reload signal (increments after a save) ---
  const [reloadDraftsTick, setReloadDraftsTick] = useState(0);
  const [aiHasGenerated, setAiHasGenerated] = useState(false);
  useEffect(() => {
    window.addToast = addToast;
    return () => { delete window.addToast; };
  }, [addToast]);

  // ===== TWICK (Advanced editor) backend connection =====
  const twickAuthHeaders = useMemo(() => {
    const t = token || localStorage.getItem('kc_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  const loadTwickState = useCallback(
    async ({ silent, force } = {}) => {
      if (!projectId) return;

      // ✅ Avoid request storms: don't run multiple loads in parallel
      if (twickLoadInFlightRef.current && !force) return;

      // ✅ Cooldown (prevents loops on fast re-renders)
      const now = Date.now();
      if (!force && (now - (twickLastLoadAttemptRef.current || 0)) < 1500) return;
      twickLastLoadAttemptRef.current = now;

      // ✅ If browser is offline, don't call the API
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setIsOnline(false);
        setTwickLoading(false);
        if (!silent && !twickOfflineToastOnceRef.current) {
          (window.addToast || addToast)?.("Connexion internet perdue : impossible de charger Twick.", 'warning');
          twickOfflineToastOnceRef.current = true;
        }
        return;
      }

      // Abort any previous pending load
      if (twickLoadAbortRef.current) {
        try { twickLoadAbortRef.current.abort(); } catch (_) { /* noop */ }
      }

      const ac = new AbortController();
      twickLoadAbortRef.current = ac;
      twickLoadInFlightRef.current = true;

      setTwickLoading(true);
      try {
        const res = await fetch(`/projects/${projectId}/twick-state`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            ...twickAuthHeaders
          },
          credentials: 'same-origin',
          cache: 'no-store',
          signal: ac.signal
        });

        if (!res.ok) {
          // Do NOT auto-retry; user can click "Recharger" if needed.
          if (!silent) {
            const msg =
              res.status === 404
                ? "Aucun état Twick trouvé (première ouverture)."
                : "Impossible de charger l'état Twick.";
            (window.addToast || addToast)?.(msg, res.status === 404 ? 'info' : 'warning');
          }
          return;
        }

        const ct = (res.headers.get('content-type') || '').toLowerCase();
        const json = ct.includes('application/json') ? await res.json().catch(() => ({})) : {};

        const state = json?.state ?? (json?.state_json ? (() => {
          try { return JSON.parse(json.state_json); } catch (_) { return null; }
        })() : null);

        setTwickState(state ?? {});
        setTwickDirty(false);
        setTwickSavedAt(json?.updated_at || null);

        twickHasLoadedOnceRef.current = true;
      } catch (e) {
        if (e?.name === 'AbortError') return;

        const offlineNow = (typeof navigator !== 'undefined' && navigator.onLine === false);
        if (offlineNow) setIsOnline(false);

        if (!silent && offlineNow && !twickOfflineToastOnceRef.current) {
          (window.addToast || addToast)?.("Connexion internet perdue : impossible de charger Twick.", 'warning');
          twickOfflineToastOnceRef.current = true;
        } else if (!silent) {
          (window.addToast || addToast)?.("Erreur réseau pendant le chargement Twick.", 'danger');
        }
      } finally {
        twickLoadInFlightRef.current = false;
        setTwickLoading(false);
      }
    },
    [projectId, twickAuthHeaders, addToast]
  );

  const saveTwickState = useCallback(
    async (stateOverride, { silent } = {}) => {
      const stateToSave = (typeof stateOverride !== 'undefined') ? stateOverride : twickState;
      if (!projectId) return;
      if (!stateToSave) {
        if (!silent) (window.addToast || addToast)?.("Aucun état Twick à sauvegarder.", 'warning');
        return;
      }

      // ✅ Avoid save storms
      if (twickSaveInFlightRef.current) return;

      // ✅ If offline, don't try to save (prevents loops)
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setIsOnline(false);
        if (!silent && !twickOfflineToastOnceRef.current) {
          (window.addToast || addToast)?.("Connexion internet perdue : sauvegarde Twick impossible.", 'warning');
          twickOfflineToastOnceRef.current = true;
        }
        return;
      }

      twickSaveInFlightRef.current = true;
      setTwickSaving(true);
      try {
        const res = await fetch(`/projects/${projectId}/twick-state`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...twickAuthHeaders
          },
          body: JSON.stringify({ state: stateToSave }),
          credentials: 'same-origin'
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.error) {
          if (!silent) (window.addToast || addToast)?.("Sauvegarde Twick échouée.", 'danger');
          return;
        }

        setTwickDirty(false);
        setTwickSavedAt(json?.updated_at || new Date().toISOString());
        if (!silent) (window.addToast || addToast)?.("Twick sauvegardé.", 'success');
      } catch (e) {
        if (!silent) (window.addToast || addToast)?.("Erreur réseau pendant la sauvegarde Twick.", 'danger');
      } finally {
        twickSaveInFlightRef.current = false;
        setTwickSaving(false);
      }
    },
    [projectId, twickState, twickAuthHeaders, addToast]
  );

  const uploadTwickFinalVideo = useCallback(
    async (fileOrBlob, fileNameHint) => {
      if (!projectId) return;
      if (!fileOrBlob) return;

      try {
        setBusy(true);
        const fd = new FormData();
        const name =
          fileNameHint ||
          fileOrBlob?.name ||
          `twick_export_${Date.now()}.mp4`;
        fd.append('video', fileOrBlob, name);

        const res = await fetch(`/projects/${projectId}/twick/upload-final`, {
          method: 'POST',
          headers: {
            ...twickAuthHeaders
          },
          body: fd,
          credentials: 'same-origin'
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.error || !json?.videoUrl) {
          (window.addToast || addToast)?.("Upload de la vidéo Twick échoué.", 'danger');
          return;
        }

        setFinalVideoUrl(json.videoUrl);
        (window.addToast || addToast)?.("Vidéo Twick envoyée et disponible.", 'success');
      } catch (e) {
        (window.addToast || addToast)?.("Erreur réseau pendant l'upload Twick.", 'danger');
      } finally {
        setBusy(false);
      }
    },
    [projectId, twickAuthHeaders, addToast, setFinalVideoUrl, setBusy]
  );

  const handleTwickStateChange = useCallback((nextState) => {
    // ✅ accept empty objects ({}), but ignore null/undefined
    if (nextState === null || typeof nextState === 'undefined') return;
    setTwickState(nextState);
    setTwickDirty(true);
  }, []);

  // Auto-load when Advanced mode is active (once, no retry loop)
  useEffect(() => {
    if (montageMode !== 'Advanced') return;
    if (!isOnline) return;
    if (twickHasLoadedOnceRef.current) return;
    loadTwickState({ silent: true });
  }, [montageMode, isOnline, loadTwickState]);

// Listen for state updates coming from Twick editor (either via props callback, CustomEvent, or postMessage)
  useEffect(() => {
    const onCustomState = (ev) => {
      const detail = ev?.detail;
      const next = detail?.state ?? detail;
      if (next !== null && typeof next !== 'undefined') handleTwickStateChange(next);
    };

    const onMessage = (ev) => {
      const data = ev?.data;
      if (!data) return;

      // postMessage contract:
      // { type: 'TWICK_STATE_UPDATE', state: {...} }  OR { type: 'TWICK_EXPORT', file: Blob/File, fileName?: 'x.mp4' }
      if (data.type === 'TWICK_STATE_UPDATE' || data.type === 'twick:state') {
        const next = data.state ?? data.payload ?? null;
        if (next !== null && typeof next !== 'undefined') handleTwickStateChange(next);
      }

      if (data.type === 'TWICK_EXPORT' || data.type === 'twick:export') {
        const file = data.file ?? data.blob ?? null;
        const name = data.fileName ?? data.name ?? null;
        if (file) uploadTwickFinalVideo(file, name);
      }
    };

    const onCustomExport = (ev) => {
      const detail = ev?.detail;
      const file = detail?.file ?? detail?.blob ?? null;
      const name = detail?.fileName ?? detail?.name ?? null;
      if (file) uploadTwickFinalVideo(file, name);
    };

    window.addEventListener('twick:state', onCustomState);
    window.addEventListener('twick:export', onCustomExport);
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('twick:state', onCustomState);
      window.removeEventListener('twick:export', onCustomExport);
      window.removeEventListener('message', onMessage);
    };
  }, [handleTwickStateChange, uploadTwickFinalVideo]);



  // Current user
  const [me, setMe] = useState({ username: '', name: '', avatar: '' });
  const [musicFileName, setMusicFileName] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState('');
  const [musicUrl, setMusicUrl] = useState('');
  const [musicError, setMusicError] = useState(null);

  // Advanced mode: parameters passed to Twick editor (media + voice + music)
  const advancedMontageParams = useMemo(() => {
    const mediaIds = Array.isArray(selectedMediaIds) ? selectedMediaIds : [];

    const voiceLabel =
      voiceOverFileName
        ? `IA • ${voiceOverFileName}`
        : (voiceOverFile instanceof File)
          ? `Fichier • ${voiceOverFile.name}`
          : voiceUrl
            ? 'Voix • sélectionnée'
            : 'Aucune';

    const musicLabel =
      musicFileName
        ? musicFileName
        : (musicFile instanceof File)
          ? musicFile.name
          : musicUrl
            ? 'Musique • sélectionnée'
            : 'Aucune';

    return {
      projectId,
      selectedMediaIds: mediaIds,
      media_order: mediaIds,
      theme,
      transition,
      audio_mix_option: audioMixOption,
      music_volume: musicVolume,
      export_aspect: exportAspect,
      voice: {
        mode: voiceMode,
        url: voiceUrl || (voiceOverFileName ? `/uploads/${voiceOverFileName}` : null),
        fileName: voiceOverFileName || ((voiceOverFile instanceof File) ? voiceOverFile.name : null),
        by_step: voiceMode === 'per-step' ? (perStepVoiceMap || {}) : null,
        label: voiceLabel,
      },
      music: {
        url: musicUrl || (musicFileName ? `/uploads/${musicFileName}` : null),
        fileName: musicFileName || ((musicFile instanceof File) ? musicFile.name : null),
        label: musicLabel,
      },

      // Advanced editor state (persisted in backend)
      twick_state: twickState || null,
      twick_saved_at: twickSavedAt || null,
    };
  }, [
    projectId,
    selectedMediaIds,
    theme,
    transition,
    audioMixOption,
    musicVolume,
    exportAspect,
    voiceMode,
    voiceUrl,
    voiceOverFileName,
    voiceOverFile,
    perStepVoiceMap,
    musicUrl,
    musicFileName,
    musicFile,
    twickState,
    twickSavedAt,
  ]);

  // Avatars source

  const [usersByUsername, setUsersByUsername] = useState({});
  const avatarFor = (username) =>
    (usersByUsername[username]?.avatar_url) ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(username)}`;
  const avatarStyle = {
    width: 28, height: 28, borderRadius: '999px',
    border: '2px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
  };
  // headers with auth token if present
  const authHeaders = () => {
    const t = localStorage.getItem('kc_token') || localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  // local drafts per project (pure FE storage)
  const saveClientDraft = (projectId, montageId, videoUrl) => {
    const key = `weavevo:project:${projectId}:draftVideos`;
    const list = JSON.parse(localStorage.getItem(key) || '[]');
    const entry = { montageId, videoUrl, savedAt: new Date().toISOString() };
    localStorage.setItem(key, JSON.stringify([entry, ...list].slice(0, 20)));
  };

  // try list of POST endpoints; first 2xx wins
  const tryPostEndpoints = async (endpoints, bodyObj) => {
    for (const ep of endpoints) {
      try {
        const r = await fetch(ep, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(bodyObj || {})
        });
        if (r.ok) return { ok: true, data: await r.json().catch(() => ({})), ep, status: r.status };
      } catch { /* continue */ }
    }
    return { ok: false };
  };

  // Wizard step

  // NEW CODE – helper for hover preview
  const handlePreviewHover = (e, play) => {
    const video = e.currentTarget.querySelector('video');
    if (!video) return;
    if (play) {
      video.play().catch(() => { });
    } else {
      video.pause();
      try { video.currentTime = 0; } catch (e) { }
    }
  };

  // --- ADD: background save of manual upload into project_media
  const uploadManualToDB = async (file) => {
    if (!file || manualSaveOnceRef.current) return;
    try {
      const fd = new FormData();
      fd.append('video', file);
      fd.append('title', manualVideoFileName || file.name || 'Clip manuel');
      fd.append('location', 'Manual Upload');
      const r = await fetch(`/projects/${id}/upload-manual-video`, { method: 'POST', headers: { ...authHeaders() }, body: fd });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      manualSaveOnceRef.current = true;
      (window.addToast || addToast)?.('✅ Vidéo manuelle sauvegardée dans le projet.', 'success');
      try {
        const rows = await fetch(`/projects/${id}/media/list`).then(x => x.json());
        setProjectMedia(Array.isArray(rows) ? rows : []);
      } catch { }
    } catch {
      (window.addToast || addToast)?.('Échec de la sauvegarde DB du clip manuel.', 'error');
    }
  };


  const ManualUploadPanel = () => {
    const [previewUrl, setPreviewUrl] = React.useState(null);

    const handleVideoChange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      onManualVideoSelected(e); // keep your original upload logic
    };

    return (
      <div className="border rounded p-3 h-100">
        <h6 className="mb-3 d-flex align-items-center gap-2">
          <i className="bi bi-tools" /> Montage manuel — importez votre vidéo finale
        </h6>

        <div
          className="p-4 rounded-4"
          style={{
            border: '2px dashed #cfe4ff',
            background: 'linear-gradient(135deg,#f8fbff,#ffffff)',
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-cloud-upload fs-2 text-primary" />
            <div>
              <div className="fw-semibold">Déposez votre vidéo ici</div>
              <div className="text-muted small">
                Après votre montage local (Premiere/DaVinci…), importez un fichier <strong>.mp4/.mov</strong>.
              </div>
            </div>
          </div>

          <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              className="form-control"
              style={{ maxWidth: 360 }}
            />
            <span className="small text-muted">
              <i className="bi bi-info-circle me-1" /> Le clip sera pré-visualisé et sauvegardé automatiquement.
            </span>
          </div>
        </div>

        {previewUrl && (
          <div className="mt-4 text-center">
            <div className="fw-semibold mb-2 d-flex align-items-center justify-content-center gap-2">
              <i className="bi bi-play-circle text-primary" /> Prévisualisation du montage
            </div>
            <video
              src={previewUrl}
              controls
              playsInline
              className="rounded shadow-sm"
              style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }}
            />
          </div>
        )}
      </div>
    );
  };

  const doSaveAsDraft = async () => {
    if (!postExportUrl || !postExportMontageId) return;
    saveClientDraft(id, postExportMontageId, postExportUrl);
    addToast?.('Brouillon vidéo enregistré côté client.', 'success');
  };

  const doShareInChat = async () => {
    if (!postExportUrl) return;
    const message = `${(optShareText || 'Vidéo générée').trim()}\n${postExportUrl}`;
    const { ok } = await tryPostEndpoints(
      [
        `/projects/${id}/messages`,
        `/api/projects/${id}/messages`,
        `${window.location.protocol}//${window.location.hostname}:3001/projects/${id}/messages`
      ],
      { message }
    );
    addToast?.(ok ? 'Vidéo partagée dans le chat.' : 'Partage chat: échec.', ok ? 'success' : 'error');
    if (ok) {
      // refresh chat quietly
      try {
        const rows = await fetch(`/projects/${id}/messages`).then(r => r.json());
        setMessages(Array.isArray(rows) ? rows : []);
        setTimeout(() => scrollChatToBottom(), 30);
      } catch { }
    }
  };

  const doCloseProject = async () => {
    const status = optCloseStatus; // 'Clôturé' | 'Terminé'
    const { ok } = await tryPostEndpoints(
      [
        `/projects/${id}/close`,
        `/api/projects/${id}/close`,
        `${window.location.protocol}//${window.location.hostname}:3001/projects/${id}/close`
      ],
      { status }
    );
    addToast?.(ok ? `Projet marqué ${status}.` : 'Clôture projet: échec.', ok ? 'success' : 'error');
    if (ok) {
      try {
        const p = await fetch(`/projects/${id}`).then(r => r.json());
        setProject(p);
      } catch { }
    }
  };

  const downloadAllMedias = async () => {
    try {
      window.location.href = `/projects/${id}/media/archive`;
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    if (STUDIO_ASSISTED_ONLY) return;
    if (step === 'export' && montageMode === 'Manuel') {
      if (uploadedManualVideoFile) {
        if (manualObjectUrlRef.current) { URL.revokeObjectURL(manualObjectUrlRef.current); manualObjectUrlRef.current = null; }
        const url = URL.createObjectURL(uploadedManualVideoFile);
        manualObjectUrlRef.current = url;
        setFinalVideoUrl(url);
        setShowPlayer(true);
        uploadManualToDB(uploadedManualVideoFile); // background save
      } else {
        setShowPlayer(false);
        setFinalVideoUrl('');
        manualSaveOnceRef.current = false;
      }
    }
    return () => {
      if (manualObjectUrlRef.current && (step !== 'export' || montageMode !== 'Manuel')) {
        URL.revokeObjectURL(manualObjectUrlRef.current); manualObjectUrlRef.current = null;
      }
    };
  }, [STUDIO_ASSISTED_ONLY, step, montageMode, uploadedManualVideoFile, manualVideoFileName]);

  // auto-pick a montage when the project loads/changes
  useEffect(() => {
    if (!id) return;
    const ac = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem('kc_token');
        const r = await fetch(`/projects/${id}/montage-configs`, {
          headers: {
            'Accept': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: ac.signal
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const list = await r.json();
        const latest = list.find(x => x.is_draft === 0) || list[0];
        setSelectedMontageId(latest?.id ?? null);
        feLog('Auto-pick montage from /montage-configs ->', { picked: latest?.id, latest });
      } catch (e) {
        (window.addToast || addToast)?.(`Chargement montages: ${e.message || e}`, 'error');
        setSelectedMontageId(null);
      }
    })();
    return () => ac.abort();
  }, [id]);
  useEffect(() => {
    // On se base uniquement sur projectMedia pour l’index (source unique et fiable)
    const map = {};
    (projectMedia || []).forEach((m) => {
      if (!m) return;
      const id = m.id ?? m.media_id;
      if (!id) return;
      map[id] = m;
    });
    setMediaById(map);
  }, [projectMedia]);

  const onChangeMontageSelect = (e) => {
    const v = e.target.value;
    setSelectedConfigId(v);
    feLog('Dropdown changed -> selectedConfigId =', v);
  };
  // Prefix with /uploads when needed
  const upath = (p) => {
    if (!p) return '';
    if (p.startsWith('/uploads/')) return p;
    if (p.startsWith('http')) return p;
    if (p.startsWith('/')) return p;
    return `/uploads/${p}`;
  };
  const clientDrafts = (() => {
    try { return JSON.parse(localStorage.getItem(`weavevo:project:${id}:draftVideos`) || '[]'); }
    catch { return []; }
  })();
  // Return the correct preview URL used in Contribution
  const mediaUrlById = (id) => {
    const m = mediaById[id];
    if (!m) return '';
    const p = m.thumb_url || m.preview_url || m.file_path || m.path || m.location || m.url;
    return upath(p);
  };

  const isVideoUrl = (url) => /\.(mp4|mov|webm|mkv)$/i.test(url || '');

  // Prefer original video file for Runway seeds (not the thumbnail)
  const mediaRunwaySeedUrlById = (id) => {
    const m = mediaById[id];
    if (!m) return '';
    const fp = m.file_path || m.path || m.location || m.url || '';
    const pv = m.preview_url || m.preview || '';
    const th = m.thumb_url || m.thumbnail_url || m.thumb || '';

    // If file_path looks like a video, use it
    if (fp && isVideoUrl(fp)) return upath(fp);

    // Otherwise, try any field that looks like a video
    const candidates = [pv, th, fp];
    for (const c of candidates) {
      if (c && isVideoUrl(c)) return upath(c);
    }

    // Fall back to preview/image
    const img = th || pv || fp;
    return upath(img);
  };

  const onManualVideoSelected = (e) => {
    const f = e.target.files?.[0];
    if (f) setManualVideoFileName(f.name);
    setUploadedManualVideoFile(f || null);
  };

  const reloadMontageConfigs = React.useCallback(async () => {
    try {
      const r = await fetch(`/projects/${id}/montage-configs`);
      if (!r.ok) return;
      const json = await r.json();

      const list = Array.isArray(json) ? json : [];
      setMontageConfigs(list);

      if (list.length === 0) return;

      // Take most recent montage
      const latest = list[0];

      // --- TRANSITION ---
      if (latest.transition) setTransition(latest.transition);

      // --- AUDIO MIX OPTION ---
      if (latest.audio_mix_option) setAudioMixOption(latest.audio_mix_option);

      // --- MUSIC VOLUME (DB stores 0..1) ---
      if (latest.music_volume !== undefined && latest.music_volume !== null) {
        setMusicVolume(Math.round((Number(latest.music_volume) || 0) * 100)); // convert for slider %
      }

      // --- GLOBAL VOICE ---
      if (latest.voice_file) {
        if (latest.voice_file.startsWith('/uploads')) {
          setVoiceOverFileName(latest.voice_file.replace('/uploads/', ''));
        } else {
          setVoiceUrl(latest.voice_file);
        }
      } else {
        setVoiceOverFileName(null);
        setVoiceUrl(null);
      }

      // --- MUSIC FILE ---
      if (latest.music_file) {
        if (latest.music_file.startsWith('/uploads')) {
          setMusicFileName(latest.music_file.replace('/uploads/', ''));
        } else {
          setMusicUrl(latest.music_file);
        }
      } else {
        setMusicFileName(null);
        setMusicUrl(null);
      }

      // --- VOICE MODE ---
      // --- VOICE MODE ---
      const vm = latest.voice_mode || latest.voice_over_type || 'global';
      if (vm === 'per_step' || vm === 'per-step') {
        setVoiceMode('per-step');
      } else {
        setVoiceMode('global');
      }

      // --- PER STEP VOICE MAP ---
      if (latest.voice_by_step) {
        try {
          const raw =
            typeof latest.voice_by_step === 'string'
              ? JSON.parse(latest.voice_by_step)
              : latest.voice_by_step;

          const normalized = {};
          if (raw && typeof raw === 'object') {
            Object.entries(raw).forEach(([stepId, val]) => {
              if (!val) return;

              let url = null;

              if (typeof val === 'string') {
                if (/^https?:\/\//.test(val) || String(val).startsWith('/uploads/')) {
                  url = val;
                } else {
                  url = withUploadsPath(val);
                }
              } else if (val && typeof val === 'object') {
                if (val.url) {
                  url = val.url;
                } else if (val.serverPath) {
                  url = val.serverPath;
                } else if (val.fileName) {
                  url = withUploadsPath(val.fileName);
                }
              }

              if (!url) return;
              if (!/^https?:\/\//.test(url) && !String(url).startsWith('/uploads/')) {
                url = withUploadsPath(url);
              }

              const idx = Number(stepId);
              if (!Number.isFinite(idx)) return;

              normalized[idx] = {
                type: 'restored',
                fileName: null,
                url,
              };
            });
          }

          setPerStepVoiceMap(normalized);
        } catch {
          console.warn('⚠️ Invalid voice_by_step in DB', latest.voice_by_step);
          setPerStepVoiceMap({});
        }
      } else {
        setPerStepVoiceMap({});
      }


      // --- MEDIA ORDER ---
      if (latest.media_order) {
        try {
          const order =
            typeof latest.media_order === 'string'
              ? JSON.parse(latest.media_order)
              : latest.media_order;
          if (Array.isArray(order)) setSelectedMediaIds(order);
        } catch { }
      }

    } catch (e) {
      console.error('reloadMontageConfigs error:', e);
    }
  }, [id]);

  useEffect(() => { reloadMontageConfigs(); }, [reloadMontageConfigs]);

  // Collapse all montage groups by default once we know the steps
  useEffect(() => {
    const keys = new Set(['no-step']);
    (scenarioForm?.steps || []).forEach(st => keys.add(String(st.id)));
    setCollapsedStepsMontage(prev => {
      const next = { ...prev };
      keys.forEach(k => { if (typeof next[k] === 'undefined') next[k] = true; });
      return next;
    });
  }, [scenarioForm?.steps]);



  // Collapse all contribution groups by default once we know the steps
  useEffect(() => {
    const keys = new Set(['no-step']);
    (scenarioForm?.steps || []).forEach(st => keys.add(String(st.id)));
    setCollapsedStepsContrib(prev => {
      // keep user toggles if any, but default missing keys to true (collapsed)
      const next = { ...prev };
      keys.forEach(k => { if (typeof next[k] === 'undefined') next[k] = true; });
      return next;
    });
  }, [scenarioForm?.steps]);


  useEffect(() => {
    // Whenever we switch to IA mode, require a fresh AI generation before showing preview
    if (step === 'export' && montageMode === 'IA') {
      setAiHasGenerated(false);
    }
    // In manual mode, preview comes from user's uploaded file, so no guard needed
  }, [step, montageMode]);



  useEffect(() => {
    if (!id) return;
    if (step === 'montage') {
      loadMontageDrafts();
    }
  }, [id, step, reloadDraftsTick]);
  // DnD highlight
  const [dragOverStepKey, setDragOverStepKey] = useState(null); // string | 'no-step'

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch('/users', { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return; // be silent in UI, avoids noisy console
        const arr = await r.json();
        const map = {};
        (arr || []).forEach(u => { if (u?.username) map[u.username] = u; });
        setUsersByUsername(map);
      } catch {
        /* ignore in UI */
      }
    })();
  }, [token]);


  useEffect(() => {
    try {
      if (token) {
        const d = jwtDecode(token);
        const username = d?.preferred_username || '';
        const name = d?.name || d?.given_name || username;
        setMe({ username, name, avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(name)}` });
      }
    } catch (e) { /* ignore */ }
  }, [token]);

  // --- Template overrides: session-only per user (no DB writes) ---
  const tplOverrideStorageKey = useMemo(() => {
    const u = String(me?.username || '').trim().toLowerCase();
    return u ? `wv_tpl_overrides:${u}` : '';
  }, [me?.username]);

  // Load overrides from sessionStorage once the username is known
  useEffect(() => {
    if (!tplOverrideStorageKey) return;
    try {
      const raw = sessionStorage.getItem(tplOverrideStorageKey);
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (obj && typeof obj === 'object') setTplOverrides(obj);
    } catch {
      // silent
    }
  }, [tplOverrideStorageKey]);

  // Persist overrides in sessionStorage (current session only)
  useEffect(() => {
    if (!tplOverrideStorageKey) return;
    try {
      sessionStorage.setItem(tplOverrideStorageKey, JSON.stringify(tplOverrides || {}));
    } catch {
      // silent
    }
  }, [tplOverrideStorageKey, tplOverrides]);

  const vibyStorageKey = useMemo(() => {
    const u = me?.username ? String(me.username) : '';
    return u ? `wv_viby_overrides:${u}` : '';
  }, [me?.username]);

  useEffect(() => {
    if (!vibyStorageKey) return;
    try {
      const raw = sessionStorage.getItem(vibyStorageKey);
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (obj && typeof obj === 'object') setVibyOverrides(obj);
    } catch (e) {
      console.warn('⚠️ viby overrides load failed:', e);
    }
  }, [vibyStorageKey]);

  useEffect(() => {
    if (!vibyStorageKey) return;
    try {
      sessionStorage.setItem(vibyStorageKey, JSON.stringify(vibyOverrides || {}));
    } catch (e) {
      console.warn('⚠️ viby overrides persist failed:', e);
    }
  }, [vibyStorageKey, vibyOverrides]);







  

  // ===== Export Templates (DB / AI) helpers =====
  const safeJsonParse = useCallback((raw, fallback = null) => {
    if (raw == null) return fallback;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(String(raw)); } catch { return fallback; }
  }, []);

  const mapDbExportTemplate = useCallback((row) => {
    if (!row || !row.id) return null;
    const obj = safeJsonParse(row.template_json, {}) || {};
    const baseId = String(obj.layout || obj.baseId || obj.base_id || 'classic').toLowerCase();

    return {
      id: String(row.id).toLowerCase(),
      title: row.name || 'AI Template',
      desc: row.description || '',
      badge: 'AI',
      cat: (String(row.category || row.cat || 'ia_generated').toLowerCase() === 'ia_generated' ? 'ai_generated' : String(row.category || row.cat || 'ia_generated').toLowerCase()),
      __db: true,
      visibility: row.visibility || 'PUBLIC',
      created_by: row.created_by || 'unknown',
      baseId,
      _raw: row
    };
  }, [safeJsonParse]);

  const loadDbExportTemplates = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await fetch('/export-templates', { headers });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP_${r.status}`);
      const rows = Array.isArray(data.templates) ? data.templates : [];
      setDbExportTemplates(rows.map(mapDbExportTemplate).filter(Boolean));
    } catch (e) {
      console.warn('⚠️ loadDbExportTemplates failed:', e);
      setDbExportTemplates([]);
    }
  }, [token, mapDbExportTemplate]);

  useEffect(() => {
    if (!tplPanelOpen) return;
    loadDbExportTemplates();
  }, [tplPanelOpen, loadDbExportTemplates]);

  const allExportTemplates = useMemo(() => {
    const base = Array.isArray(EXPORT_TEMPLATES) ? EXPORT_TEMPLATES : [];
    const db = Array.isArray(dbExportTemplates) ? dbExportTemplates : [];
    const seen = new Set();
    const out = [];
    for (const t of base) {
      if (t?.id && !seen.has(t.id)) { seen.add(t.id); out.push(t); }
    }
    for (const t of db) {
      if (t?.id && !seen.has(t.id)) { seen.add(t.id); out.push(t); }
    }
    return out;
  }, [dbExportTemplates]);


  const selectedExportTemplateObj = useMemo(() => {
    const arr = Array.isArray(allExportTemplates) ? allExportTemplates : [];
    return arr.find(t => String(t?.id) === String(exportTemplate)) || null;
  }, [allExportTemplates, exportTemplate]);

  // Base defaults for the selected template (from DB template_json if present, else TEMPLATE_DEFAULTS)
  const selectedTplBaseDefaults = useMemo(() => {
    const out = { transition: '', motion: '' };

    // DB template_json may contain transition/motion
    if (selectedExportTemplateObj?.__db) {
      const raw = (
        selectedExportTemplateObj?._raw?.template_json ??
        selectedExportTemplateObj?._raw?.templateJson ??
        selectedExportTemplateObj?._raw?.template ??
        null
      );

      let obj = null;
      if (raw && typeof raw === 'object') obj = raw;
      else if (raw != null) {
        try { obj = JSON.parse(String(raw)); } catch { obj = null; }
      }
      obj = (obj && typeof obj === 'object') ? obj : {};

      const t = String(obj.transition || obj.export_transition || '').trim();
      const m = String(obj.motion || obj.export_motion || '').trim();
      if (t) out.transition = t;
      if (m) out.motion = m;
    }

    // Fallback to built-in defaults
    const d = TEMPLATE_DEFAULTS[exportTemplate] || {};
    if (!out.transition && d.transition) out.transition = d.transition;
    if (!out.motion && d.motion) out.motion = d.motion;

    return out;
  }, [selectedExportTemplateObj, exportTemplate]);


  const selectedTplVibyDefaults = useMemo(() => {
    // 1) Prefer explicit viby defaults on the template object (front presets)
    let viby = selectedExportTemplateObj?.viby || null;

    // 2) DB templates may store viby inside template_json
    if (selectedExportTemplateObj?.__db) {
      const raw = (
        selectedExportTemplateObj?._raw?.template_json ??
        selectedExportTemplateObj?._raw?.templateJson ??
        selectedExportTemplateObj?._raw?.template ??
        null
      );

      let obj = null;
      if (raw && typeof raw === 'object') obj = raw;
      else if (raw != null) {
        try { obj = JSON.parse(String(raw)); } catch { obj = null; }
      }
      obj = (obj && typeof obj === 'object') ? obj : {};

      if (obj.viby && typeof obj.viby === 'object') viby = obj.viby;
    }

    return wvNormalizeViby(viby || WV_DEFAULT_VIBY);
  }, [selectedExportTemplateObj, exportTemplate]);

  const vibyHasOverride = !!(vibyOverrides && vibyOverrides[exportTemplate]);

  // When the selected template changes: load the last session override (if any), otherwise use template defaults.
  useEffect(() => {
    if (!exportTemplate) return;
    try {
      const o = (vibyOverrides && vibyOverrides[exportTemplate]) || null;
      const base = selectedTplVibyDefaults || WV_DEFAULT_VIBY;
      const next = wvNormalizeViby({ ...(base || {}), ...(o || {}) });
      setVibyDraft(next);
    } catch (e) {
      setVibyDraft(wvNormalizeViby(WV_DEFAULT_VIBY));
    }
  }, [exportTemplate]);

  const updateViby = useCallback((patch, opts = {}) => {
    const persist = (opts.persist !== false);
    setVibyDraft((prev) => {
      const base = wvNormalizeViby(prev || WV_DEFAULT_VIBY);
      const partial = (typeof patch === 'function') ? patch(base) : (patch || {});
      const next = wvNormalizeViby({ ...base, ...partial });
      if (persist && exportTemplate) {
        setVibyOverrides((o) => ({ ...(o || {}), [exportTemplate]: next }));
      }
      return next;
    });
  }, [exportTemplate]);

  const resetViby = useCallback(() => {
    if (!exportTemplate) return;
    const base = wvNormalizeViby(selectedTplVibyDefaults || WV_DEFAULT_VIBY);
    setVibyDraft(base);
    setVibyOverrides((o) => {
      const next = { ...(o || {}) };
      delete next[exportTemplate];
      return next;
    });
    addToast('Viby reset to template defaults (session only).', 'info');
  }, [exportTemplate, selectedTplVibyDefaults, addToast]);


  const tplHasOverride = !!(tplOverrides && tplOverrides[exportTemplate]);

  const openTplOverrideModal = useCallback(() => {
    const baseT = selectedTplBaseDefaults?.transition || (TEMPLATE_DEFAULTS[exportTemplate]?.transition || 'none');
    const baseM = selectedTplBaseDefaults?.motion || (TEMPLATE_DEFAULTS[exportTemplate]?.motion || exportMotion || 'capcut');
    const curO = (tplOverrides && tplOverrides[exportTemplate]) || null;

    setTplOverrideDraft({
      transition: String((curO?.transition ?? transition ?? baseT) || 'none'),
      motion: String((curO?.motion ?? exportMotion ?? baseM) || 'capcut')
    });

    setTplOverrideOpen(true);
  }, [exportTemplate, tplOverrides, selectedTplBaseDefaults, transition, exportMotion]);

  const saveTplOverride = useCallback(() => {
    if (!exportTemplate) return;

    const next = {
      transition: String(tplOverrideDraft?.transition || 'none'),
      motion: String(tplOverrideDraft?.motion || 'capcut')
    };

    setTplOverrides((prev) => {
      const base = (prev && typeof prev === 'object') ? prev : {};
      return { ...base, [exportTemplate]: next };
    });

    // Apply immediately (no DB writes)
    setTransition(next.transition || 'none');
    setExportMotion(next.motion || 'capcut');

    setTplOverrideOpen(false);
    try { addToast?.('Template settings applied (session only).', 'success', 2500); } catch {}
  }, [exportTemplate, tplOverrideDraft, addToast]);

  const clearTplOverride = useCallback(() => {
    if (!exportTemplate) return;

    setTplOverrides((prev) => {
      const base = (prev && typeof prev === 'object') ? { ...prev } : {};
      delete base[exportTemplate];
      return base;
    });

    // Reset to template defaults (or "none/capcut" fallback) – no DB writes
    const baseT = selectedTplBaseDefaults?.transition || (TEMPLATE_DEFAULTS[exportTemplate]?.transition || 'none');
    const baseM = selectedTplBaseDefaults?.motion || (TEMPLATE_DEFAULTS[exportTemplate]?.motion || 'capcut');
    setTransition(baseT || 'none');
    setExportMotion(baseM || 'capcut');

    setTplOverrideOpen(false);
    try { addToast?.('Template settings reset to defaults.', 'info', 2500); } catch {}
  }, [exportTemplate, selectedTplBaseDefaults, addToast]);


  useEffect(() => {
    // Clamp template page when the list/page size changes (prevents empty pages)
    try {
      const templates = Array.isArray(allExportTemplates) ? allExportTemplates : [];
      const allowedCats = new Set(templates.map(t => t?.cat));
      allowedCats.add('for_you');
      allowedCats.add('ai_generated');
      const effectiveCat = allowedCats.has(tplCategory) ? tplCategory : 'for_you';

      const visible = templates.filter(t => {
        if (effectiveCat === 'for_you') return true;
        if (effectiveCat === 'ai_generated') return (t?.cat === 'ai_generated' || t?.cat === 'for_you');
        return t?.cat === effectiveCat;
      });

      const total = visible.length;
      const pageSize = Math.max(1, Number(tplPageSize || 12));
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const next = Math.min(Math.max(1, Number(tplPage || 1)), totalPages);
      if (next !== tplPage) setTplPage(next);
    } catch (_) { /* noop */ }
  }, [tplCategory, tplPageSize, allExportTemplates]);

  const onGenerateAiTemplate = useCallback(async () => {
    try {
      setAiTplLoading(true);

      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(aiTplPassword ? { 'x-ai-template-pass': aiTplPassword } : {})
      };

      const payload = {
        prompt: aiTplPrompt,
        name: aiTplName,
        description: aiTplDesc,
        baseId: aiTplBaseId,
        fx: aiTplFx,
        transition: aiTplTransition,
        motion: aiTplMotion,
        music_mode: aiTplMusicMode,
        music_prompt: aiTplMusicPrompt
      };

      const r = await fetch('/export-templates/ai-generate', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP_${r.status}`);

      addToast('Template created.', 'success', 3500);

      await loadDbExportTemplates();
      setTplCategory('ai_generated');

      const tpl = data?.template;
      if (tpl?.id) setExportTemplate(String(tpl.id).toLowerCase());
    } catch (e) {
      console.error(e);
      addToast('AI generation failed.', 'danger', 6000);
    } finally {
      setAiTplLoading(false);
    }
  }, [
    token,
    aiTplPassword,
    aiTplPrompt,
    aiTplName,
    aiTplDesc,
    aiTplBaseId,
    aiTplFx,
    aiTplTransition,
    aiTplMotion,
    aiTplMusicMode,
    aiTplMusicPrompt,
    addToast,
    loadDbExportTemplates,
    setExportTemplate
  ]);
  const onBatchGenerateAiTemplates = useCallback(async () => {
    try {
      setAiBatchLoading(true);

      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(aiTplPassword ? { 'x-ai-template-pass': aiTplPassword } : {})
      };

      const payload = {
        count: Number(aiBatchCount) || 6,
        prompt: aiTplPrompt || aiTplName || 'AI batch templates'
      };

      const r = await fetch('/export-templates/ai-batch', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP_${r.status}`);

      addToast(`Batch created: ${data?.created || (data?.templates?.length || 0)}`, 'success', 4500);

      await loadDbExportTemplates();
      setTplCategory('ai_generated');

      const first = Array.isArray(data?.templates) && data.templates[0];
      if (first?.id) setExportTemplate(String(first.id).toLowerCase());
    } catch (e) {
      console.error(e);
      addToast('Batch generation failed.', 'danger', 6000);
    } finally {
      setAiBatchLoading(false);
    }
  }, [token, aiTplPassword, aiBatchCount, aiTplPrompt, aiTplName, addToast, loadDbExportTemplates, setExportTemplate]);

  const toggleTemplateVisibility = useCallback(async (tplId, nextVisibility) => {
    try {
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const r = await fetch(`/export-templates/${encodeURIComponent(tplId)}/visibility`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ visibility: nextVisibility })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP_${r.status}`);
      await loadDbExportTemplates();
      addToast('Visibility updated.', 'success', 2500);
    } catch (e) {
      console.error(e);
      addToast('Failed to update visibility.', 'danger', 6000);
    }
  }, [token, loadDbExportTemplates, addToast]);
useEffect(() => {
    if (!id) return;

    // --- SCENARIO (handle array OR single object shape)
    (async () => {
      try {
        const r = await fetch(`/projects/${id}/scenarios`);
        if (!r.ok) throw new Error('scenarios fetch');
        const data = await r.json();

        const sc = Array.isArray(data) ? (data[0] || {}) : (data || {});
        setScenario(sc);

        const steps = safeParseSteps(sc.steps).map((st, i) => ({ id: st.id ?? i + 1, ...st }));
        setScenarioForm({
          title: sc.title || '',
          description: sc.description || '',
          steps
        });
      } catch {
        // Fallback to /scenario (single)
        try {
          const r2 = await fetch(`/projects/${id}/scenario`);
          if (!r2.ok) return;
          const sc = await r2.json();
          if (!sc) return;
          setScenario(sc);
          const steps = safeParseSteps(sc.steps).map((st, i) => ({ id: st.id ?? i + 1, ...st }));
          setScenarioForm({
            title: sc.title || '',
            description: sc.description || '',
            steps
          });
        } catch { }
      }
    })();

    // --- MEDIA
    fetch(`/projects/${id}/media`).then(res => res.json()).then(rows => {
      const list = Array.isArray(rows) ? rows : [];
      setProjectMedia(list);
      setSelectedMediaIds(list.filter(m => m.selected === 1).map(m => m.id));
    });

    // --- ROLES
    fetch(`/projects/${id}/roles`).then(r => r.json()).then((rs) => {
      setRoles(Array.isArray(rs) ? rs : []);
    });

    // --- PROJECT
    fetch(`/projects/${id}`)
      .then(r => r.json())
      .then(p => {
        setProject(p);
        setVisibility(p.visibility || 'PRIVATE');
      });

    // --- MESSAGES
    fetch(`/projects/${id}/messages`).then(r => r.json()).then((rows) => {
      setMessages(Array.isArray(rows) ? rows : []);
      setTimeout(() => scrollChatToBottom(), 50);
    });

  }, [id, token]);

  const loadMontageDrafts = async () => {
    try {
      const r = await fetch(`/projects/${id}/montage-drafts`, { headers: { Authorization: `Bearer ${kc_token}` } });



      if (!r.ok) {
        console.error('loadMontageDrafts HTTP error:', r.status);
        addToast?.(`Erreur ${r.status} lors du chargement des brouillons.`, 'error');
        setMontageDrafts([]);
        return [];
      }

      const ct = (r.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        // Sécurité : message clair si jamais le proxy renvoyait du HTML
        throw new Error('NON_JSON_RESPONSE');
      }

      const data = await r.json().catch(() => []);
      const arr = Array.isArray(data) ? data.slice() : [];

      // tri du plus récent au plus ancien
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setMontageDrafts(arr);

      // si l'éditeur est vide, on restaure le dernier brouillon
      const editorEmpty =
        (!selectedMediaIds || selectedMediaIds.length === 0) &&
        !voiceOverFileName && !musicFile && !voiceOverFile &&
        !theme && !montageMode;

      if (editorEmpty && arr.length > 0) {
        restoreDraftIntoEditor(arr[0]);
      }

      return arr;
    } catch (e) {
      console.error('loadMontageDrafts error:', e);
      addToast?.(
        "L'API '/projects/:id/montage-drafts' n’a pas renvoyé du JSON. Vérifie le proxy si ça persiste.",
        'error'
      );
      setMontageDrafts([]);
      return [];
    }
  };

  const reloadAndRestoreLatestDraft = async () => {
    const arr = await loadMontageDrafts();
    if (arr.length) {
      restoreDraftIntoEditor(arr[0]);
      (window.addToast || addToast)?.('Dernier brouillon rechargé dans l’éditeur.', 'success');
    } else {
      (window.addToast || addToast)?.('Aucun brouillon à recharger.', 'warning');
    }
  };


  const handleLoadLatestDraft = async () => {
    const arr = await loadMontageDrafts(); // on récupère et on a déjà trié
    if (arr.length === 0) {
      (window.addToast || addToast)?.('Aucun brouillon trouvé pour ce projet.', 'warning');
      return;
    }
    restoreDraftIntoEditor(arr[0]);
    (window.addToast || addToast)?.('Dernier brouillon chargé dans l’éditeur.', 'success');
  };


  const myRole = useMemo(() => {
    const meU = (me.username || '').toLowerCase();
    const r = roles.find(x => (x.username || '').toLowerCase() === meU);
    return (r?.role || '').toLowerCase().replace('_', '-');
  }, [roles, me.username]);

  const isOwner = useMemo(
    () => ((project?.created_by ?? project?.owner) || '').toLowerCase() === (me.username || '').toLowerCase(),
    [project, me.username]
  );
  const canSeeMontage = isOwner || ['scenariste', 'co-scenariste', 'admin'].includes(myRole);

  useEffect(() => {
    if (!canSeeMontage && (step === 'montage' || step === 'export')) setStep('scenario');
  }, [canSeeMontage, step]);






  /* ================= Scenario helpers ================= */
  function safeParseSteps(raw) {
    if (!raw) return [];
    try { const arr = typeof raw === 'string' ? JSON.parse(raw) : raw; return Array.isArray(arr) ? arr : []; } catch { return []; }
  }


  const saveScenario = async () => {
    if (!canEditWizard) {
      addToast("Vous n'avez pas les droits pour modifier le scénario.", 'error', 5000);
      return;
    }
    setScenarioSaving(true);
    try {
      const res = await fetch(`/projects/${id}/scenario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: scenarioForm.title,
          description: scenarioForm.description,
          steps: JSON.stringify(scenarioForm.steps.map(({ id, ...rest }) => ({ id, ...rest })))
        })
      });
      if (!res.ok) throw new Error('save scenario failed');

      // Re-load with array/single-object tolerance
      const r = await fetch(`/projects/${id}/scenarios`);
      const data = await r.json().catch(() => null);
      const sc = Array.isArray(data) ? (data[0] || {}) : (data || {});
      if (sc && (sc.title || sc.description)) {
        setScenario(sc);
        const steps = safeParseSteps(sc.steps).map((st, i) => ({ id: st.id ?? i + 1, ...st }));
        setScenarioForm({
          title: sc.title || '',
          description: sc.description || '',
          steps
        });
      }

      addToast('Scénario sauvegardé.', 'success');
    } catch (e) {
      addToast('Erreur lors de la sauvegarde du scénario.', 'error');
    } finally {
      setScenarioSaving(false);
    }
  };



  const shareCurrent = async () => {
    if (!finalVideoUrl) return;
    try {
      await navigator.clipboard.writeText(window.location.origin + finalVideoUrl);
      addToast('Lien copié dans le presse-papiers.', 'success');
    } catch {
      addToast('Impossible de copier le lien.', 'error');
    }
  };
  const closeProject = async () => {
    if (!window.confirm("Clôturer ce projet ? Vous pourrez toujours le rouvrir via l'administration.")) return;
    try {
      const r = await fetch(`/projects/${id}/close`, { method: 'POST' });
      if (!r.ok) throw new Error('close-failed');
      addToast('Projet clôturé.', 'success');
      // Optional: visually reflect closure (e.g., switch visibility / go back)
      setVisibility('PRIVATE');
    } catch {
      addToast('Impossible de clôturer le projet (route manquante côté backend ?).', 'error');
    }
  };
  const goWizardEdit = () => { window.location.href = `/wizard?edit=true&id=${id}`; };

  /* ================= Forum helpers ================= */
  const scrollChatToBottom = () => {
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const saveCurrentMontageConfig = async () => {
    try {
      const payload = {
        montage_type: montageMode,
        theme,
        media_order: selectedMediaIds,
        audio_mix_option: audioMixOption,
        music_volume: (musicVolume / 100),
        voice_over_type: voiceOverFile ? 'file' : (voiceOverFileName ? 'ia' : 'none'),
        voice_file: voiceOverFileName || null,
        music_file: musicFileName || null
      };
      await fetch(`/projects/${id}/montage-configs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      reloadMontageConfigs();
      (window.addToast || addToast)?.('Réglage enregistré.', 'success');
    } catch (e) {
      (window.addToast || addToast)?.('Échec de l’enregistrement du réglage.', 'error');
    }
  };

  const handleGenerateAIMontage = async () => {
    setBusy(true);
    try {
      const r = await fetch(`/projects/${id}/generate-ai-montage`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setAiPlan(j.plan || '');
      if (j.videoUrl) setFinalVideoUrl(j.videoUrl);
      (window.addToast || addToast)?.('Montage IA généré.', 'success');
    } catch (e) {
      (window.addToast || addToast)?.(`Erreur: ${e.message || e}`, 'error');
    } finally { setBusy(false); }
  };

  const sendMessage = async () => {
    const txt = (newMessage || '').trim();
    if (!txt) return;
    try {
      const res = await fetch(`/projects/${id}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: me.username || 'anonymous', message: txt })
      });
      if (!res.ok) throw new Error('msg fail');
      setNewMessage('');
      const rows = await fetch(`/projects/${id}/messages`).then(r => r.json());
      setMessages(Array.isArray(rows) ? rows : []);
      setTimeout(() => scrollChatToBottom(), 30);
      addToast('Message envoyé.', 'success');
    } catch {
      addToast('Erreur lors de l’envoi du message.', 'error');
    }
  };

  const groupedByStep = useMemo(() => {
    const groups = new Map();
    projectMedia.forEach(m => {
      const key = m.step_id != null ? String(m.step_id) : 'no-step';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    });
    for (const [, arr] of groups.entries()) arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return groups;
  }, [projectMedia]);

  // 🔒 Seul le propriétaire du projet, le scénariste / co-scenariste
  // ou le contributeur lui-même peut supprimer une contribution
  const canDeleteContribution = (media) => {
    if (!media) return false;

    const currentUser = (me.username || '').toLowerCase();
    const contributor = (media.contributor || '').toLowerCase();
    const role = (myRole || '').toLowerCase();

    // ➕ Propriétaire du projet
    if (isOwner) return true;

    // Scénariste / co-scenariste
    if (role === 'scenariste' || role === 'co-scenariste') return true;

    // Contributeur lui-même
    if (currentUser && contributor && currentUser === contributor) return true;

    return false;
  };


  const filteredMyMedia = useMemo(() => {
    const q = mediaFilter.toLowerCase();
    return (myMedia || []).filter(m => {
      const titleMatch = (m.title || '').toLowerCase().includes(q);
      const isVideo = /\.(mp4|webm|ogg)$/i.test(m.file_path || '');
      const typeMatch = !mediaTypeFilter || (mediaTypeFilter === 'video' && isVideo) || (mediaTypeFilter === 'image' && !isVideo);
      return titleMatch && typeMatch;
    });
  }, [myMedia, mediaFilter, mediaTypeFilter]);


  // ✅ Library loader (server-paged + light mode + session cache)
  const getLibType = () => (mediaTypeFilter || 'image');
  const libCacheKey = (type) => `weavevo:lib:${type}:page1`;
  const readLibCache = (type) => {
    try {
      const raw = sessionStorage.getItem(libCacheKey(type));
      if (!raw) return null;
      const data = JSON.parse(raw);
      const ts = Number(data?.ts || 0);
      if (!Array.isArray(data?.rows)) return null;
      // 2 minutes TTL to avoid stale surprises
      if (Date.now() - ts > 2 * 60 * 1000) return null;
      return data.rows;
    } catch { return null; }
  };
  const writeLibCache = (type, rows) => {
    try {
      sessionStorage.setItem(libCacheKey(type), JSON.stringify({ ts: Date.now(), rows }));
    } catch { /* ignore */ }
  };

  const loadLibraryPage = React.useCallback(async ({ reset = false } = {}) => {
    if (!token) return;
    if (libLoading) return;

    const type = getLibType();
    const nextOffset = reset ? 0 : libOffset;

    // ✅ Fast path cache for first page
    if (reset) {
      const cached = readLibCache(type);
      if (cached && cached.length) {
        startLibTransition(() => {
          setMyMedia(cached);
        });
        setLibOffset(cached.length);
        setLibHasMore(cached.length >= PAGE);
      }
    }

    setLibLoading(true);
    setLibError(null);
    try {
      const url = `/media?type=${encodeURIComponent(type)}&light=1&limit=${PAGE}&offset=${nextOffset}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const rows = await r.json();
      const safe = Array.isArray(rows) ? rows : [];

      startLibTransition(() => {
        setMyMedia(prev => reset ? safe : [...(prev || []), ...safe]);
      });

      if (reset) writeLibCache(type, safe);

      const newOffset = nextOffset + safe.length;
      setLibOffset(newOffset);
      setLibHasMore(safe.length === PAGE);
    } catch (e) {
      console.error('❌ Library load error', e);
      setLibError('Impossible de charger la bibliothèque');
      if (reset) {
        setLibHasMore(false);
      }
    } finally {
      setLibLoading(false);
    }
  }, [token, libLoading, libOffset, mediaTypeFilter, PAGE]);

  // ✅ Initial load: images first
  useEffect(() => {
    if (!token) return;
    // reset list when token changes
    setMyMedia([]);
    setLibOffset(0);
    setLibHasMore(true);
    loadLibraryPage({ reset: true });

    // idle prefetch of video metadata for faster toggle
    const prefetchVideos = async () => {
      try {
        const type = 'video';
        const cached = readLibCache(type);
        if (cached && cached.length) return;
        const r = await fetch(`/media?type=video&light=1&limit=${PAGE}&offset=0`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!r.ok) return;
        const rows = await r.json();
        const safe = Array.isArray(rows) ? rows : [];
        writeLibCache(type, safe);
      } catch { /* ignore */ }
    };

    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(prefetchVideos)
      : setTimeout(prefetchVideos, 350);

    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else clearTimeout(idleId);
    };
  }, [token]);

  // ✅ When type filter changes, reload from page 1
  useEffect(() => {
    if (!token) return;
    setMyMedia([]);
    setLibOffset(0);
    setLibHasMore(true);
    loadLibraryPage({ reset: true });
  }, [mediaTypeFilter]);

  // ✅ Infinite scroll trigger
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const rootEl = document.querySelector('#libScrollRoot');
    const obs = new IntersectionObserver((entries) => {
      const hit = entries.some(e => e.isIntersecting);
      if (hit && !libLoading && libHasMore) {
        loadLibraryPage();
      }
    }, { root: rootEl || null, rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [libLoading, libHasMore, loadLibraryPage]);




  const restoreDraftIntoEditor = (d) => {
    try {
      setRestoredMontageId(d?.id ?? null);
      feLog('Restore draft -> using montage_id =', d?.id);

      // 1) Ordre des médias
      if (d?.media_order) {
        try {
          const order = JSON.parse(d.media_order);
          if (Array.isArray(order)) setSelectedMediaIds(order);
        } catch {
          // silencieux
        }
      }

      // 2) Voix & musique globales
      setVoiceOverFile(null);
      setVoiceOverFileName(d?.voice_file || null);
      setMusicFile(null);
      setMusicFileName(d?.music_file || null);

      // 3) Paramètres du montage
      // TEMP (Studio simplification): force Assisted mode, keep legacy behavior for later re-enable.
      // if (d?.montage_type) setMontageMode(d.montage_type);
      if (STUDIO_ASSISTED_ONLY) {
        setMontageMode('IA');
      } else if (d?.montage_type) {
        setMontageMode(d.montage_type);
      }
      if (d?.theme) setTheme(d.theme);

      if (typeof d?.transition === 'string' && d.transition) {
        setTransition(d.transition);
      } else {
        setTransition('none');
      }

      // 3b) Mode de voix & voix par étape
      const rawMode = d?.voice_mode || d?.voice_over_type || 'global';
      if (rawMode === 'per_step' || rawMode === 'per-step') {
        setVoiceMode('per-step');
      } else {
        setVoiceMode('global');
      }

      let parsedByStep = {};
      try {
        const raw = d?.voice_by_step;
        if (raw) {
          if (typeof raw === 'string') {
            parsedByStep = JSON.parse(raw);
          } else if (typeof raw === 'object') {
            parsedByStep = raw;
          }
        }
      } catch {
        parsedByStep = {};
      }
      if (!parsedByStep || typeof parsedByStep !== 'object') {
        parsedByStep = {};
      }

      // On normalise vers des objets { type, fileName, url }
      const normalized = {};
      Object.entries(parsedByStep).forEach(([key, val]) => {
        if (!val) return;

        let url = null;

        if (typeof val === 'string') {
          if (/^https?:\/\//.test(val) || String(val).startsWith('/uploads/')) {
            url = val;
          } else {
            url = withUploadsPath(val);
          }
        } else if (val && typeof val === 'object') {
          if (val.url) {
            url = val.url;
          } else if (val.serverPath) {
            url = val.serverPath;
          } else if (val.fileName) {
            url = withUploadsPath(val.fileName);
          }
        }

        if (!url) return;
        if (!/^https?:\/\//.test(url) && !String(url).startsWith('/uploads/')) {
          url = withUploadsPath(url);
        }

        const idx = Number(key);
        if (!Number.isFinite(idx)) return;

        normalized[idx] = {
          type: 'restored',
          fileName: null,
          url,
        };
      });
      setPerStepVoiceMap(normalized);


      // 4) Mix & volume
      if (typeof d?.audio_mix_option === 'string') setAudioMixOption(d.audio_mix_option);
      if (d?.music_volume != null) {
        const pct = Math.max(0, Math.min(100, Math.round(Number(d.music_volume) * 100)));
        setMusicVolume(Number.isFinite(pct) ? pct : 20);
      }

      // 5) URL finale éventuelle
      if (d?.final_video_url) setFinalVideoUrl(d.final_video_url);

      (window.addToast || addToast)?.(
        `Brouillon chargé.`
        + (d?.voice_file ? ' 🎙️ Voix détectée.' : '')
        + (d?.music_file ? ' 🎵 Musique détectée.' : '')
        + (d?.audio_mix_option ? ` 🎚️ Mix: ${d.audio_mix_option}` : '')
        + (rawMode === 'per_step' || rawMode === 'per-step' ? ' 🧩 Voix par étape restaurées.' : ''),
        'success'
      );
    } catch (e) {
      (window.addToast || addToast)?.('Impossible de charger ce brouillon.', 'error');
    }
  };



  const toggleSelectMedia = (mid) => setSelectedMediaIds(prev => prev.includes(mid) ? prev.filter(x => x !== mid) : [...prev, mid]);

  const onDeleteContribution = async (mid) => {
    // 🔒 Double sécurité côté FE : on vérifie que l'utilisateur courant
    // est soit scénariste / co-scenariste, soit le contributeur lui-même.
    const media = projectMedia.find(m => m.id === mid);
    if (media && !canDeleteContribution(media)) {
      addToast(
        "Vous ne pouvez supprimer que vos propres médias ou en tant que scénariste du projet.",
        'error',
        5000
      );
      return;
    }

    // ✅ Plus d'ugly window.confirm, on passe en flux direct + tooltip 5s
    try {
      await fetch(`/linkedMedia/${mid}`, { method: 'DELETE' });
      const rows = await fetch(`/projects/${id}/media`).then(r => r.json());
      setProjectMedia(Array.isArray(rows) ? rows : []);
      addToast('Média supprimé.', 'success', 5000);
    } catch {
      addToast('Suppression non disponible.', 'error', 5000);
    }
  };

  const generateAiPlan = async () => {
    setBusy(true); setAiPlan('');
    try {
      const r = await fetch(`/projects/${id}/generate-ai-montage`, { method: 'POST' });
      const isJson = (r.headers.get('content-type') || '').includes('application/json');
      const j = isJson ? await r.json().catch(() => ({})) : {};

      if (!r.ok) {
        const msg = j?.error || `HTTP ${r.status}`;
        throw new Error(msg);
      }

      setAiPlan(j.plan || '');
      if (j.videoUrl) setFinalVideoUrl(j.videoUrl);
      addToast('Plan IA généré.', 'success');
    } catch (e) {
      addToast(`Erreur génération du plan IA: ${e?.message || e}`, 'error');
    } finally { setBusy(false); }
  };


  const downloadZip = async () => {
    try {
      const r = await fetch(`/projects/${id}/export-montage`);
      if (!r.ok) throw new Error('zip');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `montage_project_${id}.zip`; a.click();
      URL.revokeObjectURL(url);
      addToast('Téléchargement du ZIP lancé.', 'success');
    } catch {
      addToast('Erreur export ZIP.', 'error');
    }
  };


  // NEW (ajout transition dans la signature)
  function currentDraftSignature() {
    const computedVoiceType = voiceOverFile
      ? 'file'
      : (voiceOverFileName ? 'ia' : 'none');

    const voiceByStepMap = {};
    if (voiceMode === 'per-step' && perStepVoiceMap) {
      Object.entries(perStepVoiceMap).forEach(([stepId, info]) => {
        if (!info) return;

        if (info.serverPath) {
          voiceByStepMap[stepId] = info.serverPath;
        } else if (info.fileName) {
          voiceByStepMap[stepId] = `/uploads/${info.fileName}`;
        } else if (info.url) {
          voiceByStepMap[stepId] = info.url;
        } else if (typeof info === 'string') {
          voiceByStepMap[stepId] = info;
        }
      });
    }


    return JSON.stringify({
      montage_type: montageMode || 'IA',
      theme: theme || '',
      voice_over_type: computedVoiceType,
      voice_file: voiceOverFileName || null,
      music_file: musicFileName || null,
      media_order: Array.isArray(selectedMediaIds) ? selectedMediaIds : [],
      audio_mix_option: audioMixOption || 'voice+music',
      music_volume: Number(
        ((typeof musicVolume === 'number' ? musicVolume : 0) / 100).toFixed(3)
      ),
      transition: transition || 'none',
      voice_mode: voiceMode === 'per-step' ? 'per_step' : 'global',
      voice_by_step: voiceByStepMap
    });
  }


  async function saveDraft() {
    try {
      // 1) Signature courante (inclut voix par étape)
      const sig = currentDraftSignature();

      // 2) Récupérer le dernier brouillon existant
      const authHeaders = kc_token ? { Authorization: `Bearer ${kc_token}` } : {};
      let latest = null;

      try {
        const r = await fetch(`/projects/${id}/montage-drafts`, { headers: authHeaders });
        if (r.ok) {
          const arr = await r.json();
          if (Array.isArray(arr) && arr.length) latest = arr[0];
        }
      } catch {
        // silencieux
      }

      // 3) Comparer les signatures (avec voice_mode & voice_by_step)
      if (latest) {
        let latestVoiceByStep = {};
        try {
          if (latest.voice_by_step) {
            if (typeof latest.voice_by_step === 'string') {
              latestVoiceByStep = JSON.parse(latest.voice_by_step);
            } else if (typeof latest.voice_by_step === 'object') {
              latestVoiceByStep = latest.voice_by_step;
            }
          }
          if (!latestVoiceByStep || typeof latestVoiceByStep !== 'object') {
            latestVoiceByStep = {};
          }
        } catch {
          latestVoiceByStep = {};
        }

        // NEW – on ajoute transition
        const latestSig = JSON.stringify({
          montage_type: latest.montage_type || 'IA',
          theme: latest.theme || '',
          voice_over_type: latest.voice_over_type || 'none',
          voice_file: latest.voice_file || null,
          music_file: latest.music_file || null,
          media_order: (() => {
            try {
              return JSON.parse(latest.media_order || '[]');
            } catch {
              return [];
            }
          })(),
          audio_mix_option: latest.audio_mix_option || 'voice+music',
          music_volume: Number(Number(latest.music_volume || 0).toFixed(3)),
          transition: latest.transition || 'none',
          voice_mode: latest.voice_mode || 'global',
          voice_by_step: latestVoiceByStep
        });


        if (sig === latestSig) {
          addToast && addToast('Aucun changement détecté — rien à enregistrer.', 'success');
          return;
        }
      }

      // 4) Construire le FormData pour /save-montage
      const computedVoiceType = voiceOverFile
        ? 'file'
        : (voiceOverFileName ? 'ia' : 'none');

      const fd = new FormData();
      fd.append('montage_type', montageMode || 'IA');
      fd.append('theme', theme || '');
      fd.append('voice_over_type', computedVoiceType);

      // Voix globale
      if (computedVoiceType === 'file' && voiceOverFile) {
        fd.append('voice_over', voiceOverFile);
      } else if (computedVoiceType === 'ia' && voiceOverFileName) {
        fd.append('voice_file', voiceOverFileName);
      }

      // Musique
      if (musicFile) {
        fd.append('music', musicFile);
      } else if (musicFileName) {
        fd.append('music_file', musicFileName);
      }

      // Ordre des médias
      fd.append(
        'media_order',
        JSON.stringify(Array.isArray(selectedMediaIds) ? selectedMediaIds : [])
      );

      // NEW – on envoie aussi la transition au backend
      // Transition
      fd.append('transition', transition || 'none');

      // Mix audio
      fd.append('audio_mix_option', audioMixOption || 'voice+music');
      fd.append(
        'music_volume',
        String((typeof musicVolume === 'number' ? musicVolume : 0) / 100)
      );

      // Brouillon
      fd.append('draft', '1');

      // Mode de voix
      const effectiveVoiceMode = voiceMode === 'per-step' ? 'per_step' : 'global';
      fd.append('voice_mode', effectiveVoiceMode);


      const voiceByStepToSend = {};
      if (voiceMode === 'per-step' && perStepVoiceMap) {
        Object.entries(perStepVoiceMap).forEach(([stepId, info]) => {
          if (!info) return;

          if (info.serverPath) {
            voiceByStepToSend[stepId] = info.serverPath;
          } else if (info.fileName) {
            voiceByStepToSend[stepId] = `/uploads/${info.fileName}`;
          } else if (info.url) {
            voiceByStepToSend[stepId] = info.url;
          } else if (typeof info === 'string') {
            voiceByStepToSend[stepId] = info;
          }
        });
      }
      fd.append('voice_by_step', JSON.stringify(voiceByStepToSend));


      // 5) Appel API
      const res = await fetch(`/projects/${id}/save-montage`, {
        method: 'POST',
        headers: authHeaders,
        body: fd
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      addToast && addToast('Brouillon enregistré ✅', 'success');

      // refresh liste brouillons
      typeof setReloadDraftsTick === 'function' && setReloadDraftsTick(t => t + 1);
      if (typeof loadMontageDrafts === 'function') await loadMontageDrafts();

    } catch (e) {
      console.error('saveDraft failed:', e);
      addToast && addToast("Échec de l’enregistrement du brouillon", 'error');
    }
  }







  const generateFinalVideo = async () => {
    // Prefer the draft currently loaded in editor; fallback to the current working montage (if you keep that behavior)
    let chosenMontageId = Number(
      restoredMontageId ?? selectedMontageId
    );

    feLog('Export -> chosenMontageId =', chosenMontageId, {
      restoredMontageId, selectedMontageId,
      audioMixOption
    });

    // ✅ If no montage is selected yet, auto-save current settings first
    if (!Number.isFinite(chosenMontageId)) {
      const savedId = await saveSelectionAndMontage();
      chosenMontageId = Number(savedId ?? restoredMontageId ?? selectedMontageId);

      feLog('Export -> chosenMontageId (after auto-save) =', chosenMontageId, {
        savedId,
        restoredMontageId,
        selectedMontageId,
        audioMixOption
      });
    }

    if (!Number.isFinite(chosenMontageId)) {
      (window.addToast || addToast)?.('Sauvegardez un montage avant de générer.', 'warning');
      return;
    }

    setBusy(true);
    setFinalVideoUrl('');
    try {
      const token = localStorage.getItem('kc_token');
      const effectiveMediaOrder = (
        Array.isArray(exportMediaOrder) && exportMediaOrder.length
          ? exportMediaOrder
          : (Array.isArray(selectedMediaIds) ? selectedMediaIds : [])
      );
      // ✅ Credits (end screen): contributors + avatars (PNG-friendly for ffmpeg)
      const creditsPayload = (() => {
        const ids = new Set((Array.isArray(effectiveMediaOrder) ? effectiveMediaOrder : []).map(x => String(x)));
        const seen = new Set();
        const list = [];
        const details = [];

        const normalizeU = (raw) => {
          const s = String(raw || '').trim();
          if (!s) return '';
          return s.startsWith('@') ? s.slice(1) : s;
        };

        const dicebearPng = (u) =>
          `https://api.dicebear.com/7.x/initials/png?seed=${encodeURIComponent(u)}&size=256`;

        const pickAvatarUrl = (u) => {
          const rec = usersByUsername?.[u];
          const url = String(
            rec?.avatar_url ??
            rec?.avatarUrl ??
            rec?.photo_url ??
            rec?.picture ??
            rec?.image_url ??
            ''
          ).trim();

          // If no real avatar, fallback to a PNG (NOT SVG) so the backend can render it in credits.
          return url || dicebearPng(u);
        };

        (projectMedia || []).forEach((m) => {
          const mid = m?.id ?? m?.media_id ?? m?.mediaId;
          if (ids.size && !ids.has(String(mid))) return;

          const uname = normalizeU(
            m?.contributor_username ??
            m?.contributor ??
            m?.username ??
            m?.created_by ??
            m?.user_id ??
            ''
          );
          if (!uname) return;

          const handle = `@${uname}`;
          const key = handle.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);

          const avatar = pickAvatarUrl(uname);

          list.push(handle);
          details.push({ username: handle, avatar });
        });

        return { list, details };
      })();

      const creditsContributors = creditsPayload.list;
      const creditsContributorsDetails = creditsPayload.details;

      const effectiveViby = wvNormalizeViby((vibyOverrides && vibyOverrides[exportTemplate]) || vibyDraft || WV_DEFAULT_VIBY);

      const payload = {
        montage_id: chosenMontageId,
        audio_mix_option: audioMixOption,
        music_volume: (musicVolume ?? 20) / 100,
        selected_media_ids: Array.isArray(effectiveMediaOrder) ? effectiveMediaOrder : [],
        media_order: Array.isArray(effectiveMediaOrder) ? effectiveMediaOrder : [],
        transition,

        // Advanced editor state (optional)
        twick_state: (montageMode === 'Advanced' ? (twickState || null) : null),

        // NEW: tell backend how to stitch voices
        voice_mode: (voiceMode === 'per-step' ? 'per_step' : 'global'),
        voice_by_step: (voiceMode === 'per-step' ? perStepVoiceMap /* { [stepId]: '/uploads/xx.wav' or blob-backed saved filename } */ : null),

        export_preset: exportPreset,
        export_res: exportRes,
        export_fps: exportFPS,
        export_format: exportFormat,
        export_bitrate: exportBitrate,
        export_watermark_text: exportWatermarkText,
        export_aspect: exportAspect,
        // If a template is a front-only "variant" (bg_/ani_/collage_/retro_), keep backend compatible by sending its baseId.
        // For DB templates, keep the ID so backend can load template_json.
        export_template: (selectedExportTemplateObj?.__db ? exportTemplate : (selectedExportTemplateObj?.baseId || exportTemplate)),
        export_template_variant: exportTemplate,
        export_template_base: (selectedExportTemplateObj?.baseId || null),
        export_template_style: (selectedExportTemplateObj?.style || null),
        export_viby: effectiveViby,

        export_motion: exportMotion,

        // ✅ End credits screen (handled by backend)
        credits_enabled: true,
        credits_contributors: creditsContributors,
        credits_contributors_details: creditsContributorsDetails
      };


      feLog('POST /projects/:id/generate-final-video payload ->', payload);

      const res = await fetch(`/projects/${id}/generate-final-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload),
        credentials: 'same-origin'
      });

      feLog('HTTP status =', res.status);
      const json = await res.json().catch(() => ({}));
      feLog('Response JSON <-', json);

      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      if (json?.used) setLastUsedExport(json.used);
      const url = json.videoUrl || json.url || '';
      if (url) {
        setFinalVideoUrl(url);
        setShowPlayer(true);

        // --- open post-export modal (optional) ---
        setPostExportUrl(url);
        setPostExportMontageId(json?.montage_id ?? (restoredMontageId ?? selectedMontageId));
        setPostExportOpen(true);

        // --- show preview player for AI mode ---
        if (montageMode === 'IA' || montageMode === 'Advanced') {
          setAiHasGenerated(true);
          const videoSection = document.getElementById('aiFinalPreview');
          if (videoSection) {
            videoSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }

        // --- reset toggles ---
        setOptSaveAsDraft(false);
        setOptShareInChat(true);
        setOptShareText('Voici la vidéo générée 🎬');
        setOptCloseProject(false);
        setOptCloseStatus('Clôturé');

        (window.addToast || addToast)?.('🎬 Vidéo IA générée et affichée.', 'success');
      } else {
        (window.addToast || addToast)?.('Génération terminée sans URL.', 'error');
      }

    } catch (e) {
      (window.addToast || addToast)?.(`Erreur génération vidéo. ${e?.message || e}`, 'error');
    } finally {
      setBusy(false);
    }
  };


  // ---------------- Runway AI (beta) ----------------
  const refreshRunwayOrg = async () => {
    if (!runwayUnlocked) {
      try { addToast('Runway est verrouillé. Entre le mot de passe pour activer.', 'warning'); } catch (e) {}
      return;
    }
    try {
      setRunwayOrgInfo('');
      const r = await fetch('/runway/organization', { headers: { ...authHeaders() } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        setRunwayOrgInfo(`Runway: ${j?.error || 'Erreur'}`);
        return;
      }
      // Keep it short: show id + (if present) credits/limits-like fields
      const org = j.org || {};
      const id = org.id || org.orgId || '';
      const credits = org.credits ?? org.creditBalance ?? org.credit_balance ?? null;
      const spend = org.spendLimit ?? org.spend_limit ?? null;
      const bits = [];
      if (id) bits.push(`org=${String(id).slice(0, 10)}…`);
      if (credits != null) bits.push(`credits=${credits}`);
      if (spend != null) bits.push(`spendLimit=${spend}`);
      setRunwayOrgInfo(bits.length ? `✅ ${bits.join(' · ')}` : '✅ Runway OK');
    } catch (e) {
      setRunwayOrgInfo(`Runway: ${e?.message || 'Erreur'}`);
    }
  };

  const pollRunwayTask = async (taskId) => {
    if (!taskId) return;
    try {
      const r = await fetch(`/runway/tasks/${encodeURIComponent(taskId)}`, { headers: { ...authHeaders() } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        setRunwayStatus(j?.error || `Erreur (${r.status})`);
        return;
      }
      const task = j.task || {};
      const st = task.status || task.state || 'UNKNOWN';
      setRunwayStatus(st);

      if (j.outputUrl) {
        setRunwayOutputUrl(j.outputUrl);
      }

      const done = ['SUCCEEDED', 'FAILED', 'CANCELLED', 'CANCELED'].includes(String(st).toUpperCase());
      if (done) {
        setRunwayBusy(false);
        if (String(st).toUpperCase() === 'SUCCEEDED') {
          addToast('Runway : génération terminée ✅', 'success');
        } else {
          addToast(`Runway : ${st}`, 'error');
        }
        return;
      }

      // Poll again after 5 seconds (Runway recommends not more frequent)
      runwayPollRef.current = setTimeout(() => pollRunwayTask(taskId), 5000);
    } catch (e) {
      setRunwayStatus(e?.message || 'Erreur');
      setRunwayBusy(false);
    }
  };

  const generateWithRunway = async () => {
    if (!runwayUnlocked) {
      try { addToast('Runway est verrouillé. Entre le mot de passe pour activer.', 'warning'); } catch (e) {}
      return;
    }
    if (runwayBusy) return;

    try {
      setRunwayBusy(true);
      setRunwayTaskId('');
      setRunwayStatus('');
      setRunwayOutputUrl('');

      // Resolve seed URL (can be empty for text_to_video)
      const seedIdStr = runwaySeedId || (selectedMediaIds?.length ? String(selectedMediaIds[0]) : '');
      const seedUrlRaw = seedIdStr ? mediaRunwaySeedUrlById(Number(seedIdStr)) : '';
      const seedUrl = absolutizeClientUrl(seedUrlRaw);

      const body = {
        mode: runwayMode || 'auto',
        seedUrl: seedUrl || '',
        promptText: (runwayPromptText && runwayPromptText.trim())
          ? runwayPromptText.trim()
          : defaultRunwayPromptForTemplate(exportTemplate),
        ratio: runwayRatio || aspectToRunwayRatio(exportAspect),
        duration: runwayDuration || 8,
      };

      // If user picked text_to_video, allow empty seed
      if (String(body.mode) !== 'text_to_video' && !body.seedUrl) {
        addToast('Choisis un média “seed” (image ou vidéo).', 'error');
        setRunwayBusy(false);
        return;
      }

      const r = await fetch('/runway/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });

      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        addToast(j?.error || 'Erreur Runway', 'error');
        setRunwayBusy(false);
        return;
      }

      const taskId = j.taskId;
      setRunwayTaskId(taskId);
      setRunwayStatus('QUEUED');
      addToast('Runway : tâche créée (polling)…', 'success');

      // Start polling
      runwayPollRef.current = setTimeout(() => pollRunwayTask(taskId), 1500);
    } catch (e) {
      addToast(e?.message || 'Erreur Runway', 'error');
      setRunwayBusy(false);
    }
  };






  const saveSelectionAndMontage = async () => {
    try {
      await fetch(`/projects/${id}/media/selection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedMediaIds, message: '' })
      });

      const fd = new FormData();
      const computedVoiceOverType = voiceOverFile ? 'file' : (voiceOverFileName?.trim() ? 'ia' : 'none');
      fd.append('montage_type', montageMode || '');
      fd.append('theme', theme || '');
      fd.append('voice_over_type', computedVoiceOverType);
      if (voiceOverFile) fd.append('voice_over', voiceOverFile);
      if (voiceOverFileName) fd.append('voice_file', voiceOverFileName);
      if (musicFile) fd.append('music', musicFile);
      if (musicFileName) fd.append('music_file', musicFileName); // <-- keep previous music
      fd.append('media_order', JSON.stringify(selectedMediaIds || []));
      fd.append('audio_mix_option', audioMixOption);
      fd.append('music_volume', String(musicVolume));
      fd.append('transition', transition);

      fd.append('voice_mode', voiceMode === 'per-step' ? 'per_step' : 'global');
      if (voiceMode === 'per-step') {
        const map = {};
        for (const [stepId, info] of Object.entries(perStepVoiceMap || {})) {
          if (!info) continue;

          // Reformater selon le backend
          if (info.serverPath) {
            map[stepId] = info.serverPath;    // upload déjà enregistré
          } else if (info.fileName) {
            map[stepId] = `/uploads/${info.fileName}`;
          } else if (info.url) {
            map[stepId] = info.url;
          } else if (typeof info === 'string') {
            map[stepId] = info;
          }
        }
        fd.append('voice_by_step', JSON.stringify(map));
      } else {
        fd.append('voice_by_step', '{}');
      }



      const res = await fetch(`/projects/${id}/save-montage`, { method: 'POST', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `save montage failed ${res.status}`);

      const savedId = Number(json?.id ?? json?.montage_id ?? json?.montageId ?? 0);
      if (Number.isFinite(savedId) && savedId > 0) {
        setSelectedMontageId(savedId);
      }

      (window.addToast || addToast)('Montage sauvegardé.', 'success');


      // refresh the “Réglages précédents” (latest draft) card
      if (typeof loadMontageDrafts === 'function') {
        await loadMontageDrafts();
      }

      // ping the DraftsList to re-fetch
      setReloadDraftsTick(t => t + 1);

      return savedId;

    } catch (e) {
      console.error('saveSelectionAndMontage error:', e);
      (window.addToast || addToast)('Erreur lors de la sauvegarde du montage.', 'error');
      return null;
    }
  };




  const runWithLoader = async (label, fn) => {
    setNiceLoader({ visible: true, text: label });
    try { return await fn(); }
    finally { setNiceLoader({ visible: false, text: '' }); }
  };

  // Example: Export
  const onClickGenerateFinalVideo = async () => {
    await runWithLoader('Génération de la vidéo finale…', async () => {
      await generateFinalVideo();
    });
  };
  ////start here

  ////start here
  const progress = useMemo(
    () => ({ scenario: 25, contribute: 50, montage: 75, export: 100 })[step] || 0,
    [step]
  );

  // Drag depuis la bibliothèque personnelle (copie)
  const onLibraryDragStart = (e, media) => {
    try {
      e.dataTransfer.setData(
        'application/json',
        JSON.stringify({ media_id: media.id })
      );
      e.dataTransfer.effectAllowed = 'copy';
    } catch {
      // ignore
    }
  };

  // Drag depuis la section "Sans étape" (move vers une étape)
  const onSansStepDragStart = (e, projectMedia) => {
    try {
      // ⚠️ on force l'usage du media_id (ID de "Mes médias")
      const mediaId = projectMedia.media_id;
      if (!mediaId) return; // pas d'ID -> pas de drag

      const payload = {
        source: 'project',
        media_id: mediaId,          // ID du média source
        linked_id: projectMedia.id, // ID de la ligne /linkedMedia/:id
        from_step: projectMedia.step_id ?? null
      };

      e.dataTransfer.setData('application/json', JSON.stringify(payload));
      e.dataTransfer.effectAllowed = 'move';
    } catch {
      // ignore
    }
  };
  // Drag depuis une étape (autre que "Sans étape")
  const onStepDragStart = (e, projectMedia) => {
    try {
      const payload = {
        source: 'project',
        media_id: projectMedia.media_id,
        linked_id: projectMedia.id,
        from_step: projectMedia.step_id
      };
      e.dataTransfer.setData('application/json', JSON.stringify(payload));
      e.dataTransfer.effectAllowed = 'move';
    } catch { }
  };

  const onDropToStep = (stepKey) => async (e) => {
    e.preventDefault();
    setDragOverStepKey(null);

    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData('application/json') || '{}');
    } catch {
      data = {};
    }

    const targetStepId = stepKey === 'no-step' ? null : Number(stepKey);

    // on normalise les IDs
    const mid = data.media_id != null ? Number(data.media_id) : null;
    const linkedId = data.linked_id != null ? Number(data.linked_id) : null;
    const fromStep = data.from_step ?? null;

    // 👉 Cas dégénéré : aucun identifiant exploitable
    if (!linkedId && !mid) return;

    // 🔁 Cas 1 : drag depuis "Sans étape" / une étape (média déjà dans le projet)
    if (linkedId) {
      // si on drop sur la même "étape", ne rien faire
      const sourceKey = fromStep == null ? 'no-step' : String(fromStep);
      if (sourceKey === stepKey) return;

      try {
        // 🆕 Mise à jour directe de l'étape du média existant
        await fetch(`/linkedMedia/${linkedId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            step_id: targetStepId   // null = Sans étape
          }),
        });

        const rows = await fetch(`/projects/${id}/media`).then((r) => r.json());
        setProjectMedia(Array.isArray(rows) ? rows : []);
        addToast('Média déplacé vers la nouvelle étape.', 'success');
      } catch (err) {
        console.error('Erreur déplacement média:', err);
        addToast('Erreur lors du déplacement du média.', 'error');
      }
      return;
    }

    // 📥 Cas 2 : drag depuis la bibliothèque (comportement existant)
    try {
      await fetch(`/linkedMedia/existing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          media_ids: [mid],
          project_id: id,
          step_id: targetStepId,
        }),
      });

      const rows = await fetch(`/projects/${id}/media`).then((r) => r.json());
      setProjectMedia(Array.isArray(rows) ? rows : []);
      addToast('Média ajouté à l’étape.', 'success');
    } catch (err) {
      console.error('Erreur ajout média par DnD:', err);
      addToast('Erreur lors de l’ajout par glisser-déposer.', 'error');
    }
  };


  const allowDrop = (stepKey) => (e) => {
    e.preventDefault();
    setDragOverStepKey(stepKey);
  };
  const leaveDrop = () => setDragOverStepKey(null);

  // Fake progression pour le montage
  const FAKE_EXPORT_STEPS = [
    {
      key: 'prepare',
      label: 'Préparation des médias',
      desc: 'Chargement des clips, images et métadonnées.',
    },
    {
      key: 'audio',
      label: 'Mixage audio',
      desc: 'Synchronisation voix off et musique.',
    },
    {
      key: 'transitions',
      label: 'Transitions & effets',
      desc: 'Application des transitions entre les scènes.',
    },
    {
      key: 'export',
      label: 'Export final',
      desc: 'Encodage dans le format demandé.',
    },
  ];

  const [exportFakeProgress, setExportFakeProgress] = useState(0);
  const [exportFakeStepIndex, setExportFakeStepIndex] = useState(0);
  const EXPORT_STEP_DURATIONS_SEC = [25, 33, 32, 33];
  useEffect(() => {
    // Si pas busy ou mode async, on reset la progression fake
    if (!busy || exportAsync) {
      setExportFakeProgress(0);
      setExportFakeStepIndex(0);
      return;
    }

    const maxProgress = 97; // on laisse 3% pour le "snap" final quand busy passe à false
    const stepsCount = FAKE_EXPORT_STEPS.length;

    // Durées par étape en ms et temps total cible ≈ 2 min 3 s
    const stepDurationsMs = EXPORT_STEP_DURATIONS_SEC.map((s) => s * 1000);
    const expectedTotalMs = stepDurationsMs.reduce((sum, v) => sum + v, 0);

    const startTime = Date.now();

    const computeProgress = () => {
      const elapsed = Date.now() - startTime;
      const clampedElapsed = Math.min(Math.max(elapsed, 0), expectedTotalMs);

      // Progression linéaire sur le temps total, bornée à maxProgress
      const ratioTotal = clampedElapsed / expectedTotalMs;
      let progress = ratioTotal * maxProgress;

      if (progress < 0) progress = 0;
      if (progress > maxProgress) progress = maxProgress;

      // Détermination de l’étape en fonction du temps passé
      let acc = 0;
      let stepIndex = 0;
      for (let i = 0; i < stepsCount; i++) {
        acc += stepDurationsMs[i];
        if (clampedElapsed <= acc) {
          stepIndex = i;
          break;
        } else {
          stepIndex = i; // si on dépasse tout, on reste sur la dernière
        }
      }

      setExportFakeProgress(Math.round(progress));
      setExportFakeStepIndex(stepIndex);
    };

    // Tick immédiat pour éviter 0% figé
    computeProgress();

    // Mise à jour régulière
    const interval = setInterval(computeProgress, 700); // un tick toutes les ~0.7 s

    return () => clearInterval(interval);
  }, [busy, exportAsync]);



  //////ends here


  //////ends here
  /* ================= Render ================= */
  const toBool = (v) => {
    if (v === true) return true;
    if (v === false) return false;
    if (v === 1) return true;
    if (v === 0) return false;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (s === '1' || s === 'true' || s === 'yes' || s === 'y') return true;
      if (s === '0' || s === 'false' || s === 'no' || s === 'n') return false;
    }
    return Boolean(v);
  };

  const detailsPublic = toBool(project?.details_public);
const vizuPublic = toBool(project?.vizu_public);

const projectVisibility = String(project?.visibility || visibility || 'PRIVATE').toUpperCase();
const isPublicProject = projectVisibility === 'PUBLIC';
const isPrivateProject = !isPublicProject;

const meUsernameLower = String(me?.username || '').toLowerCase();

const isMember = Boolean(meUsernameLower) && (roles || []).some(
  (r) => String(r?.username || '').toLowerCase() === meUsernameLower
);

// ✅ Access rules (per your request)
// - PUBLIC: anyone can enter and contribute (montage/export remain restricted by roles/owner)
// - PRIVATE: user must request access from the Explorer, then get access (membership/owner)
const canAccessPrivate = isOwner || isMember;
const blockedStudio = Boolean(project) && isPrivateProject && !canAccessPrivate;

// Scenario/Wizard editing remains role-based (contributors can still post media/messages)
const canEditWizard = isOwner || ['scenariste', 'co-scenariste', 'admin'].includes(myRole);
const readOnlyStudio = isPublicProject && !canEditWizard;
/* ================= Render ================= */
  if (blockedStudio) {
    return (
      <div className="container-fluid" style={styles.page}>
        <div style={{ maxWidth: 980, margin: '0 auto', padding: '86px 24px 40px' }}>
          <div className="alert alert-warning d-flex align-items-start gap-3" role="alert" style={{ borderRadius: 16 }}>
            <i className="bi bi-shield-lock" style={{ fontSize: 20 }} />
            <div>
              <div className="fw-semibold">Accès restreint</div>
              <div className="small text-muted">
                Ce projet est privé. Pour le rejoindre et contribuer, demandez un accès depuis l’Explorer.
              </div>

              {token ? (
                <Link to="/explore" className="btn btn-primary btn-sm rounded-pill mt-2">
                  <i className="bi bi-compass me-1"></i>
                  Ouvrir l’Explorer
                </Link>
              ) : (
                <div className="small mt-2">
                  Connectez-vous, puis demandez l’accès depuis l’Explorer.
                </div>
              )}
            </div>
          </div>

          <div className="small text-muted">
            Projet : <span className="fw-semibold">{project?.title || `#${id}`}</span> · Visibilité :{' '}
            <span className="fw-semibold">{projectVisibility === 'PUBLIC' ? 'Public' : 'Privé'}</span> · Vizu public :{' '}
            <span className="fw-semibold">{vizuPublic ? 'Oui' : 'Non'}</span> · Détails public :{' '}
            <span className="fw-semibold">{detailsPublic ? 'Oui' : 'Non'}</span>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="container-fluid" style={styles.page}>
      <div style={styles.layout}>
        {/* Toasts */}
        <Toasts />
        <NiceLoader visible={niceLoader.visible} text={niceLoader.text} />

        {readOnlyStudio && (
          <div
            className="alert alert-info d-flex align-items-center justify-content-between gap-3"
            role="status"
            style={{ borderRadius: 16, marginTop: 14, marginBottom: 14 }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-eye" style={{ fontSize: 18 }} />
              <div>
                <div className="fw-semibold">Lecture seule</div>
                <div className="small text-muted">
                  Vous consultez ce studio en mode lecture seule. Les actions de modification sont désactivées.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== Project Card =*/}

        {/* ===== Project Card ===== */}
        <section style={styles.card} aria-label="Project Card">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-3">
              <div>
                <h3 className="m-0">  <i className="bi bi-folder me-2" />{project?.title || scenario?.title || `Projet #${id}`}</h3>
                <div className="text-muted small">
                  Propriétaire : {project?.owner || project?.created_by || '—'} · Visibilité : {visibility}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Link className="btn btn-primary btn-sm" to={`/projects/${projectId}/forum`}>
                <i className="bi bi-box-arrow-up-right me-1" /> Ouvrir le forum
              </Link>

              {canEditWizard && (
                <button className="btn btn-primary btn-sm" onClick={goWizardEdit} type="button">
                  <i className="bi bi-pencil-square me-1" /> Éditer dans Wizard
                </button>
              )}
            </div>

          </div>

          {/* Summary + Team + Forum */}
          {/* Summary + Team (Forum moved to dedicated page) */}
          <ProjectAccordions
            roles={roles}
            visibility={visibility}
            usersByUsername={usersByUsername}
            avatarFor={avatarFor}
            scenarioForm={scenarioForm}
            projectId={id}
          />

        </section>

        {/* ===== Wizard Nav ===== */}
        <nav style={styles.wizardNav}>
          <button style={styles.navBtn(step === 'scenario')} onClick={() => setStep('scenario')}>
            <i className="bi bi-journal-text" /> Scénario
          </button>
          <button style={styles.navBtn(step === 'contribute')} onClick={() => setStep('contribute')}>
            <i className="bi bi-film" /> Contributions
          </button>
          {canSeeMontage && (
            <>
              <button style={styles.navBtn(step === 'montage')} onClick={() => setStep('montage')}>
                <i className="bi bi-scissors" /> Montage
              </button>
              <button style={styles.navBtn(step === 'export')} onClick={() => setStep('export')}>
                <i className="bi bi-box-arrow-up" /> Export
              </button>
            </>
          )}
        </nav>

        <main>
          <div className="mb-3" style={{ height: 8, background: '#eef2f6', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: '#0dcaf0' }} />
          </div>

          {/* === SCENARIO === */}
          {step === 'scenario' && (
            <section className="w-100" style={styles.card}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="m-0"><i className="bi bi-journal-text me-2" />{scenario?.title || 'Scénario du projet'}</h3>
                <div className="d-flex gap-2">


                </div>
              </div>
              <p className="text-muted">
                {scenarioForm?.description || scenario?.description || project?.description || '—'}
              </p>

              <div className="d-flex align-items-center justify-content-between mb-2">
                <h5 className="m-0"><i className="bi bi-signpost-split me-2" />Étapes du scénario</h5>
                <small className="text-muted">Cliquez pour contribuer</small>
              </div>

              <div style={styles.stepsGrid}>
                {scenarioForm.steps.map((st, i) => {
                  const key = String(st.id ?? i + 1);
                  const items = groupedByStep.get(key) || [];
                  return (
                    <div
                      key={key}
                      style={{ ...styles.stepCard, display: 'flex', flexDirection: 'column' }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,.06)'; e.currentTarget.style.borderColor = '#d9e2ec'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = '#e6e6e6'; }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">Étape #{st.id}</span>
                        <span className="badge bg-light text-dark">{items.length}</span>
                      </div>
                      <h6 className="m-0 mb-1">
                        <i className="bi bi-geo-alt me-1" />
                        {st.location || 'Lieu'}
                      </h6>

                      {/* zone texte qui prend la hauteur dispo, sans gros blanc forcé */}
                      <div className="text-muted small flex-grow-1">
                        {st.description || '—'}
                      </div>

                      {/* bouton toujours collé en bas de la carte */}
                      <div className="d-flex justify-content-end mt-3">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setCurrentStepId(st.id);
                            setStep('contribute');
                          }}
                        >
                          <i className="bi bi-plus-circle me-1" /> Contribuer
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Post-export modal */}
                {postExportOpen && (
                  <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Vidéo générée</h5>
                          <button type="button" className="btn-close" onClick={() => setPostExportOpen(false)}></button>
                        </div>

                        <div className="modal-body">
                          <div className="mb-3">
                            <div className="ratio ratio-16x9 rounded overflow-hidden border">
                              <video src={postExportUrl} controls playsInline />
                            </div>
                            <div className="mt-2 small text-muted text-break">{postExportUrl}</div>
                          </div>

                          <div className="row g-3">
                            {/* Save as draft (client-side) */}
                            <div className="col-12">
                              <div className="form-check">
                                <input id="optSaveDraft" className="form-check-input" type="checkbox"
                                  checked={optSaveAsDraft} onChange={e => setOptSaveAsDraft(e.target.checked)} />
                                <label className="form-check-label ms-1" htmlFor="optSaveDraft">
                                  Enregistrer comme brouillon (local)
                                </label>
                              </div>
                              {optSaveAsDraft && (
                                <button className="btn btn-outline-secondary btn-sm mt-2" onClick={doSaveAsDraft}>
                                  <i className="bi bi-journal-bookmark"></i> Enregistrer le brouillon
                                </button>
                              )}
                            </div>

                            {/* Share in chat */}
                            <div className="col-12">
                              <div className="form-check">
                                <input id="optShareChat" className="form-check-input" type="checkbox"
                                  checked={optShareInChat} onChange={e => setOptShareInChat(e.target.checked)} />
                                <label className="form-check-label ms-1" htmlFor="optShareChat">
                                  Partager dans le chat du projet
                                </label>
                              </div>
                              {optShareInChat && (
                                <div className="d-flex gap-2 mt-2">
                                  <input className="form-control form-control-sm"
                                    placeholder="Message à poster…"
                                    value={optShareText}
                                    onChange={e => setOptShareText(e.target.value)} />
                                  <button className="btn btn-primary btn-sm" onClick={doShareInChat}>
                                    <i className="bi bi-send"></i> Partager
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Close project */}
                            <div className="col-12">
                              <div className="form-check">
                                <input id="optClose" className="form-check-input" type="checkbox"
                                  checked={optCloseProject} onChange={e => setOptCloseProject(e.target.checked)} />
                                <label className="form-check-label ms-1" htmlFor="optClose">
                                  Clôturer le projet
                                </label>
                              </div>
                              {optCloseProject && (
                                <div className="d-flex gap-2 mt-2">
                                  <select className="form-select form-select-sm" style={{ maxWidth: 180 }}
                                    value={optCloseStatus} onChange={e => setOptCloseStatus(e.target.value)}>
                                    <option value="Clôturé">Clôturé</option>
                                    <option value="Terminé">Terminé</option>
                                  </select>
                                  <button className="btn btn-dark btn-sm" onClick={doCloseProject}>
                                    <i className="bi bi-check2-circle"></i> Appliquer le statut
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button className="btn btn-secondary" onClick={() => setPostExportOpen(false)}>Fermer</button>
                          <a className="btn btn-outline-primary" href={postExportUrl} download>
                            <i className="bi bi-download"></i> Télécharger
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {postExportOpen && <div className="modal-backdrop fade show"></div>}

              </div>
            </section>
          )}

          {/* === CONTRIBUTIONS === */}
          {step === 'contribute' && (
            <section style={styles.card}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="m-0"><i className="bi bi-film me-2" />Contributions</h3>
                <div className="text-muted small">
                  {currentStepId ? <>Étape ciblée : <span className="badge bg-secondary">#{currentStepId}</span></> : 'Étape : aucune (général)'}
                </div>
              </div>

              <div className="row g-3">
                {/* LEFT */}
                <div className="col-12 col-lg-6">
                  <div className="border rounded p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="m-0"><i className="bi bi-folder2-open me-1" />Contributions en cours</h6>
                      <span className="badge bg-light text-dark">{projectMedia.length}</span>
                    </div>

                    <div className="vstack gap-3">
                      {scenarioForm.steps.map(st => {
                        const key = String(st.id);
                        const items = groupedByStep.get(key) || [];
                        const collapsed = !!collapsedStepsContrib[key];

                        return (
                          <div
                            key={key}
                            className={`border rounded-3 ${dragOverStepKey === key ? 'border-primary' : ''}`}
                            onDragOver={allowDrop(key)}
                            onDragLeave={leaveDrop}
                            onDrop={onDropToStep(key)}
                            style={{ background: dragOverStepKey === key ? 'rgba(13,110,253,.05)' : undefined }}
                          >
                            {/* HEADER identique pour toutes les étapes */}
                            <div className="d-flex align-items-center justify-content-between p-2">
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  className="btn btn-sm btn-light"
                                  onClick={() => setCollapsedStepsContrib(s => ({ ...s, [key]: !s[key] }))}
                                  aria-expanded={!collapsed}
                                  title={collapsed ? 'Développer' : 'Réduire'}
                                >
                                  {collapsed ? <i className="bi bi-chevron-right" /> : <i className="bi bi-chevron-down" />}
                                </button>
                                <h6 className="m-0">
                                  <i className="bi bi-ladder me-1" />
                                  Étape #{st.id} — {st.location || 'Lieu'}
                                </h6>
                              </div>
                              <span className="badge bg-light text-dark">{items.length}</span>
                            </div>

                            {/* CONTENU : soit les médias, soit une zone "drop" stylée */}
                            {!collapsed && (
                              <div className="p-2">
                                {items.length > 0 ? (
                                  <div className="row g-2">
                                    {items.map(m => {
                                      const isVideo = /\.(mp4|webm|ogg)$/i.test(m.file_path || '');
                                      const url = withUploadsPath(m.file_path || '');
                                      return (
                                        <div className="col-12 col-sm-6" key={m.id}>
                                          <div className="border rounded p-2 position-relative" style={{ background: '#fafafa' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                              <div className="small fw-semibold text-truncate" title={m.title}>
                                                {m.title || 'Sans titre'}
                                              </div>
                                              <div className="d-flex align-items-center gap-2">
                                                {m.contributor && (
                                                  <Link
                                                    to={`/users/${m.contributor}`}
                                                    className="d-inline-flex align-items-center text-decoration-none"
                                                    title={`@${m.contributor}`}
                                                  >
                                                    <img src={avatarFor(m.contributor)} alt={m.contributor} style={avatarStyle} />
                                                  </Link>
                                                )}

                                                {canDeleteContribution(m) && (
                                                  <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    title="Supprimer"
                                                    onClick={() => onDeleteContribution(m.id)}
                                                  >
                                                    <i className="bi bi-trash" />
                                                  </button>
                                                )}
                                              </div>
                                            </div>

                                            <Link
                                              to={`/mediaDetails/${m.id}`}
                                              className="d-block"
                                              style={{
                                                width: '100%',
                                                height: 160,
                                                background: '#0b0c0d',
                                                borderRadius: 8,
                                                overflow: 'hidden',
                                              }}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onMouseEnter={(e) => handlePreviewHover(e, true)}
                                              onMouseLeave={(e) => handlePreviewHover(e, false)}
                                            >
                                              {isVideo ? (
                                                <video
                                                  src={url}
                                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                  muted
                                                  playsInline
                                                  preload="metadata"
                                                />
                                              ) : (
                                                <img
                                                  src={url}
                                                  alt={m.title}
                                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                  loading="lazy"
                                                />
                                              )}
                                            </Link>

                                            {m.description && (
                                              <div
                                                className="small mt-1 text-muted text-truncate"
                                                title={m.description}
                                              >
                                                {m.description}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  // 🔥 même carte, mais contenu "drop zone" quand il n'y a pas encore de médias
                                  <div
                                    className="border rounded-3 p-3 text-muted small d-flex flex-column align-items-center justify-content-center"
                                    style={{ minHeight: 80, borderStyle: 'dashed' }}
                                  >
                                    <i className="bi bi-box-arrow-in-down mb-1" />
                                    <span>Déposez ici pour ajouter à l’étape #{st.id}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}


                      {/* Sans étape */}
                      {(() => {
                        const key = 'no-step';
                        const items = groupedByStep.get(key) || [];
                        const collapsed = !!collapsedStepsContrib[key];
                        return (
                          <div
                            className={`border rounded-3 ${dragOverStepKey === key ? 'border-primary' : ''}`}
                            onDragOver={allowDrop(key)}
                            onDragLeave={leaveDrop}
                            onDrop={onDropToStep(key)}
                            style={{ background: dragOverStepKey === key ? 'rgba(13,110,253,.05)' : undefined }}
                          >
                            <div className="d-flex align-items-center justify-content-between p-2">
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  className="btn btn-sm btn-light"
                                  onClick={() => setCollapsedStepsContrib(s => ({ ...s, [key]: !s[key] }))}
                                  aria-expanded={!collapsed}
                                  title={collapsed ? 'Développer' : 'Réduire'}
                                >
                                  {collapsed ? <i className="bi bi-chevron-right" /> : <i className="bi bi-chevron-down" />}
                                </button>
                                <h6 className="m-0">
                                  <i className="bi bi-box-seam me-1" />
                                  Contribution Aléatoire
                                </h6>
                              </div>
                              <span className="badge bg-light text-dark">{items.length}</span>
                            </div>

                            {!collapsed && (
                              <div className="row g-2 p-2">
                                {items.map(m => {
                                  const isVideo = /\.(mp4|webm|ogg)$/i.test(m.file_path || '');
                                  const url = withUploadsPath(m.file_path || '');
                                  return (
                                    <div className="col-12 col-sm-6" key={m.id}>
                                      <div
                                        className="border rounded p-2 position-relative"
                                        style={{ background: '#fafafa' }}
                                        draggable
                                        onDragStart={(e) => onStepDragStart(e, m)}
                                        title="Glisser vers une étape pour l’y affecter"
                                      >
                                        {/* Header identique aux Contributions en cours */}
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <div className="small fw-semibold text-truncate" title={m.title}>
                                            {m.title || 'Sans titre'}
                                          </div>
                                          <div className="d-flex align-items-center gap-2">
                                            {m.contributor && (
                                              <Link
                                                to={`/users/${m.contributor}`}
                                                className="d-inline-flex align-items-center text-decoration-none"
                                                title={`@${m.contributor}`}
                                              >
                                                <img
                                                  src={avatarFor(m.contributor)}
                                                  alt={m.contributor}
                                                  style={avatarStyle}
                                                />
                                              </Link>
                                            )}

                                            {canDeleteContribution(m) && (
                                              <button
                                                className="btn btn-outline-danger btn-sm"
                                                title="Supprimer"
                                                onClick={() => onDeleteContribution(m.id)}
                                              >
                                                <i className="bi bi-trash" />
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        {/* Prévisualisation + hover preview identique */}
                                        <div
                                          style={{
                                            width: '100%',
                                            height: 160,
                                            background: '#0b0c0d',
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            cursor: 'pointer'
                                          }}
                                          onMouseEnter={(e) => handlePreviewHover(e, true)}
                                          onMouseLeave={(e) => handlePreviewHover(e, false)}
                                          onClick={() => window.open(`/mediaDetails/${m.id}`, '_blank', 'noopener,noreferrer')}
                                          title="Ouvrir les détails du média dans une nouvelle fenêtre"
                                        >
                                          {isVideo ? (
                                            <video
                                              src={url}
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                              muted
                                              playsInline
                                              preload="metadata"
                                            />
                                          ) : (
                                            <img
                                              src={url}
                                              alt={m.title}
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                              loading="lazy"
                                            />
                                          )}
                                        </div>

                                        {m.description && (
                                          <div
                                            className="small mt-1 text-muted text-truncate"
                                            title={m.description}
                                          >
                                            {m.description}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}


                    </div>
                  </div>
                </div>

                {/* RIGHT: Library */}
                <div className="col-12 col-lg-6">
                  <div className="border rounded p-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="m-0"><i className="bi bi-collection me-1" />Ma bibliothèque</h6>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="toggleExistingLibrary"
                          checked={useExistingMedia}
                          onChange={() => setUseExistingMedia(!useExistingMedia)}
                        />
                        <label className="form-check-label ms-2" htmlFor="toggleExistingLibrary">
                          Utiliser un média existant
                        </label>
                      </div>
                    </div>

                    {useExistingMedia ? (
                      <>
                        <div className="row g-2 mb-2">
                          <div className="col-12 col-md-7">
                            <div className="input-group">
                              <span className="input-group-text"><i className="bi bi-search" /></span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Filtrer par titre…"
                                value={mediaFilter}
                                onChange={e => setMediaFilter(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-5">
                            <div className="input-group">
                              <span className="input-group-text"><i className="bi bi-funnel" /></span>
                              <select
                                className="form-select"
                                value={mediaTypeFilter}
                                onChange={e => setMediaTypeFilter(e.target.value)}
                              >
                                <option value="">Tous les formats</option>
                                <option value="image">Images</option>
                                <option value="video">Vidéos</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div id="libScrollRoot" style={styles.libPane}>
                          {(libLoading || isLibPending) && (
                            <div className="d-flex align-items-center gap-2 text-muted small px-2 pt-2">
                              <span className="spinner-border spinner-border-sm" role="status" />
                              <span>Chargement de la bibliothèque…</span>
                            </div>
                          )}
                          {!libLoading && !isLibPending && filteredMyMedia.length === 0 && (
                            <div className="text-center text-muted small py-3">
                              Aucun média ne correspond à vos filtres.
                            </div>
                          )}
                          <div style={styles.libGrid}>
                            {filteredMyMedia.slice(0, libRenderLimit).map(m => {
                              const isVideo = /\.(mp4|webm|ogg)$/i.test(m.file_path || '');
                              const url = withUploadsPath(m.file_path || '');
                              const isSelected = selectedMediaIds.includes(m.id);
                              return (
                                <div key={m.id} style={styles.libItem}
                                  draggable
                                  onDragStart={(e) => onLibraryDragStart(e, m)}
                                  title="Glisser vers une étape pour ajouter">
                                  <div style={styles.libThumbWrap}>
                                    {isVideo ? (
                                      <video src={url} style={styles.libThumb} muted preload="metadata" />
                                    ) : (
                                      <img src={url} alt={m.title} style={styles.libThumb} loading="lazy" />
                                    )}
                                  </div>
                                  <div className="p-2 d-flex align-items-center justify-content-between">
                                    <div className="small text-truncate" title={m.title}>
                                      {m.title || 'Sans titre'}
                                    </div>
                                    <button
                                      className={`btn btn-sm ${isSelected ? 'btn-success' : 'btn-outline-primary'}`}
                                      onClick={() => toggleSelectMedia(m.id)}
                                      title={isSelected ? 'Retirer de la sélection' : 'Ajouter à la sélection'}
                                    >
                                      {isSelected ? <i className="bi bi-check2-circle" /> : <i className="bi bi-plus-circle" />}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* ✅ Affichage progressif côté UI (4 par 4) */}
                          {filteredMyMedia.length > libRenderLimit && (
                            <div className="text-center my-2">
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() =>
                                  setLibRenderLimit(v => Math.min(v + 8, filteredMyMedia.length))
                                }
                                title="Afficher plus d’éléments sans bloquer la page"
                              >
                                <i className="bi bi-plus-lg me-1" />
                                Afficher plus
                              </button>
                            </div>
                          )}



                          <div ref={sentinelRef} style={{ height: 1 }} />

                          {libHasMore && (
                            <div className="text-center my-2">
                              <button
                                className="btn btn-light btn-sm"
                                onClick={() => loadLibraryPage()}
                                disabled={libLoading}
                              >
                                {libLoading ? 'Chargement…' : 'Charger plus'}
                              </button>
                            </div>
                          )}
                          {libError && (
                            <div className="text-center small text-danger">{libError}</div>
                          )}
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => setSelectedMediaIds([])}>
                            <i className="bi bi-arrow-counterclockwise me-1" /> Réinitialiser sélection
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={!selectedMediaIds.length}
                            onClick={async () => {
                              try {
                                await fetch(`/linkedMedia/existing`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({
                                    media_ids: selectedMediaIds,
                                    project_id: id,
                                    step_id: currentStepId || null
                                  })
                                });
                                const rows = await fetch(`/projects/${id}/media`).then(r => r.json());
                                setProjectMedia(rows || []);
                                addToast('Médias ajoutés au projet.', 'success');
                              } catch {
                                addToast('Erreur lors de l’ajout des médias.', 'error');
                              }
                            }}
                          >
                            <i className="bi bi-plus-circle me-1" />
                            Ajouter au projet ({selectedMediaIds.length})
                          </button>
                        </div>
                        <div className="text-muted small mt-2"><i className="bi bi-hand-index" /> Astuce : vous pouvez aussi <strong>glisser</strong> un média directement vers une étape à gauche.</div>
                      </>
                    ) : (
                      <>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label small text-muted">Titre du média</label>
                            <input
                              className="form-control"
                              value={uplTitle}
                              onChange={e => setUplTitle(e.target.value)}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label small text-muted ">Localisation</label>
                            <input
                              className="form-control"
                              value={uplLocation}
                              onChange={e => setUplLocation(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="form-label small text-muted">Description</label>
                          <input
                            className="form-control"
                            value={uplDescription}
                            onChange={e => setUplDescription(e.target.value)}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label small text-muted">Fichier média</label>
                          <input
                            type="file"
                            className="form-control"
                            onChange={e => setUplFile(e.target.files?.[0] || null)}
                          />
                        </div>

                        <div className="text-center mt-3">
                          <button
                            className="btn btn-primary px-4 py-2"
                            onClick={async () => {
                              if (!uplFile || !uplTitle || !uplLocation || !uplDescription) {
                                addToast('Tous les champs sont obligatoires.', 'error');
                                return;
                              }
                              const fd = new FormData();
                              fd.append('media', uplFile);
                              fd.append('title', uplTitle);
                              fd.append('location', uplLocation);
                              fd.append('description', uplDescription);
                              fd.append('project_id', id);
                              fd.append('comment', uplComment);
                              fd.append('step_id', currentStepId || '');

                              const res = await fetch('/linkedMedia', {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                                body: fd
                              });
                              const data = await res.json();

                              if (data?.id) {
                                setUplTitle('');
                                setUplLocation('');
                                setUplDescription('');
                                setUplFile(null);
                                setUplComment('');
                                setUplTags([]);

                                const rows = await fetch(`/projects/${id}/media`).then(r => r.json());
                                setProjectMedia(rows || []);

                                addToast('Média ajouté au projet.', 'success');
                              } else {
                                addToast("Erreur lors de l'ajout du média.", 'error');
                              }
                            }}
                          >
                            <i className="bi bi-check2-circle me-1" />
                            Ajouter ce média au projet
                          </button>
                        </div>
                      </>

                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* === MONTAGE === */}
          {canSeeMontage && step === 'montage' && (
            <section style={styles.card}>
              <div className="d-flex align-items-center justify-content-between">
                <h3 className="mb-3"><i className="bi bi-scissors me-2" />Montage</h3>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  title="Ouvre l’éditeur Twick intégré dans l’étape Export"
                  onClick={() => {
                    setMontageMode('Advanced');
                    setStep('export');
                    try {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } catch (e) { }
                  }}
                >
                  <i className="bi bi-sliders2-vertical me-1" />
                  Advanced mode
                </button>

              </div>
              <div className="mb-3">
                <div className="card shadow-sm border-0">
                  <div className="card-body">
                    <div
                      className="d-flex flex-wrap align-items-center justify-content-between gap-3"
                      style={styles.montageControlsRow}
                    >
                      {/* Left: Mode (segmented, larger hit area) */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold me-1">Mode</span>
                        <div className="btn-group" role="group" aria-label="Montage mode">
                          {/* TEMP (Studio simplification): Manual montage is disabled for now.
                              Keep this code for later re-enable.
                          <input type="radio" className="btn-check" name="montageMode" id="modeManual"
                            checked={montageMode === 'Manuel'} onChange={() => setMontageMode('Manuel')} />
                          <label className={`btn ${montageMode === 'Manuel' ? 'btn-primary' : 'btn-outline-primary'}`} htmlFor="modeManual" title="Vous avez déjà votre vidéo montée">
                            <i className="bi bi-tools me-2" /> Manuel
                          </label>
                          */}

                          <input type="radio" className="btn-check" name="montageMode" id="modeAI"
                            checked={montageMode === 'IA'} onChange={() => setMontageMode('IA')} />
                          <label className={`btn ${montageMode === 'IA' ? 'btn-primary' : 'btn-outline-primary'}`} htmlFor="modeAI" title="L’IA vous assiste (voix, musique, transitions…)">
                            <i className="bi bi-magic me-2" /> Mode assisté
                          </label>

                          {/* TEMP (Studio simplification): Advanced (Twick) is disabled for now.
                              Keep this code for later re-enable.
                          <input type="radio" className="btn-check" name="montageMode" id="modeAdvanced"
                            checked={montageMode === 'Advanced'} onChange={() => setMontageMode('Advanced')} />
                          <label className={`btn ${montageMode === 'Advanced' ? 'btn-primary' : 'btn-outline-primary'}`} htmlFor="modeAdvanced" title="Montez avec l’éditeur avancé intégré (Twick)">
                            <i className="bi bi-sliders2-vertical me-2" /> Advanced mode
                          </label>
                          */}
                        </div>
                      </div>

                      

                      {/* Right: Actions + Summary */}
                      <div className="d-flex align-items-center gap-2">
                       
                        {montageMode !== 'Manuel' && (
                          <button className="btn btn-primary" onClick={saveDraft}>
                            <i className="bi bi-save me-2" />
                            Sauvegarder le montage
                          </button>
                        )}
                      </div>

                    </div>


                    {/* Optional helper line */}
                    <div className="mt-2">
                      <small className="text-muted">
                        Astuce&nbsp;: choisissez un <em>thème</em>, puis sélectionnez un <em>template</em> à l’export. Weavevo s’occupe du montage.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-3">


                <MontageByStep
                  scenarioForm={scenarioForm}
                  groupedByStep={groupedByStep}
                  collapsed={collapsedStepsMontage}
                  setCollapsed={setCollapsedStepsMontage}
                  selectedMediaIds={selectedMediaIds}
                  setSelectedMediaIds={setSelectedMediaIds}
                />
              </div>

              {(montageMode === 'IA' || montageMode === 'Advanced') && (
                <AudioVoice
                  voiceOverFile={voiceOverFile} setVoiceOverFile={setVoiceOverFile}
                  voiceOverFileName={voiceOverFileName} setVoiceOverFileName={setVoiceOverFileName}
                  musicFile={musicFile} setMusicFile={setMusicFile}
                  musicFileName={musicFileName} setMusicFileName={setMusicFileName}
                  voiceUrl={voiceUrl} setVoiceUrl={setVoiceUrl}
                  musicUrl={musicUrl} setMusicUrl={setMusicUrl}
                  musicError={musicError} setMusicError={setMusicError}
                  audioMixOption={audioMixOption} setAudioMixOption={setAudioMixOption}
                  musicVolume={musicVolume} setMusicVolume={setMusicVolume}
                  voiceGender={voiceGender} setVoiceGender={setVoiceGender}
                  voiceLang={voiceLang} setVoiceLang={setVoiceLang}
                  scenario={scenarioForm}
                  projectId={id}
                  transition={transition} setTransition={setTransition}
                  selectedMediaIds={selectedMediaIds}
                  projectMedia={projectMedia}
                  voiceMode={voiceMode} setVoiceMode={setVoiceMode}
                  perStepVoiceMap={perStepVoiceMap}
                  onPerStepVoicesChange={setPerStepVoiceMap}
                  addToast={addToast}
                />
              )}










              {!STUDIO_ASSISTED_ONLY && montageMode === 'Manuel' && (
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-4">
                    {/* Header & Info Banner */}
                    <div className="alert alert-warning d-flex align-items-start rounded-3 mb-4" role="info">
                      <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
                      <div>
                        <h6 className="fw-bold mb-1">Montage manuel requis</h6>
                        <p className="mb-0 small text-muted">
                          Téléchargez vos médias, réalisez le montage avec votre outil préféré (<strong>Premiere</strong>, <strong>DaVinci Resolve</strong>, …),
                          puis importez ici la vidéo finale au format <strong>MP4</strong>.
                        </p>
                      </div>
                    </div>

                    {/* Media Selection & Download */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
                      <div className="fw-semibold">
                        <i className="bi bi-collection-play me-2"></i>
                        Médias sélectionnés : <span className="text-primary">{selectedMediaIds.length}</span>
                      </div>
                      <button
                        className="btn btn-primary btn-sm d-flex align-items-center gap-2 px-3 shadow-sm"
                        style={{
                          background: isDownloading
                            ? 'linear-gradient(90deg, #6ea8fe, #3d8bfd)'
                            : 'linear-gradient(90deg, #0d6efd, #0a58ca)',
                          border: 'none',
                          transition: 'all 0.3s ease-in-out',
                        }}
                        onClick={async () => {
                          setIsDownloading(true);
                          try {
                            await downloadZip();
                            // Optional toast or alert
                            // toast.success("Téléchargement terminé !");
                          } finally {
                            setIsDownloading(false);
                          }
                        }}
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm text-light"
                              role="status"
                              aria-hidden="true"
                            />
                            <span className="fw-semibold">Téléchargement...</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-file-zip-fill fs-6"></i>
                            <span className="fw-semibold">Télécharger tous les médias</span>
                          </>
                        )}
                      </button>

                    </div>

                    {/* Two-column layout */}
                    <div className="row g-4">
                      {/* Left Column: Upload Panel */}
                      <div className="col-12 col-xl-6">
                        <div
                          className="p-4 rounded-4 text-center h-100"
                          style={{
                            border: '2px dashed #cfe4ff',
                            background: 'linear-gradient(135deg,#f8fbff,#ffffff)',
                          }}
                        >
                          <h6 className="fw-semibold mb-3 d-flex align-items-center justify-content-center gap-2">
                            <i className="bi bi-cloud-upload text-primary fs-5"></i>
                            Importer votre montage final
                          </h6>
                          <ManualUploadPanel />

                          {manualVideoFileName && (
                            <div className="small text-muted mt-2">
                              <i className="bi bi-file-earmark-play me-1"></i>
                              {manualVideoFileName}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Column: Preview */}
                      <div className="col-12 col-xl-6">
                        <div className="p-4 rounded-4 border h-100" style={{ backgroundColor: '#fdfdfd' }}>
                          <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                            <i className="bi bi-play-circle text-success fs-5"></i>
                            Prévisualisation
                          </h6>

                          {(finalVideoUrl || uploadedManualVideoFile) ? (
                            <div className="ratio ratio-16x9 rounded overflow-hidden border mb-3">
                              <video
                                src={finalVideoUrl || URL.createObjectURL(uploadedManualVideoFile)}
                                controls
                                playsInline
                              />
                            </div>
                          ) : (
                            <div className="text-muted small mb-3 text-center py-4 border rounded-3">
                              <i className="bi bi-film fs-3 d-block mb-2"></i>
                              Aucune vidéo importée pour le moment.
                            </div>
                          )}

                          {/* Recap placeholder */}
                          <div className="mt-3">
                            {/* ===== Header + chips ===== */}
                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-3">
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center border shadow-sm"
                                  style={{ width: 44, height: 44 }}
                                >
                                  <i className="bi bi-stars fs-5 text-primary"></i>
                                </div>

                                <div>
                                  <h5 className="mb-0 d-flex align-items-center gap-2">
                                    Récapitulatif du montage
                                  </h5>
                                  <small className="text-muted d-flex align-items-center gap-1">
                                    <i className="bi bi-eye"></i>
                                    Dernière vérification avant l’export
                                  </small>
                                </div>
                              </div>

                              <div className="d-flex flex-wrap align-items-center gap-2 justify-content-start justify-content-md-end">
                                <span className="badge rounded-pill text-bg-light d-flex align-items-center gap-1">
                                  <i className="bi bi-layers"></i>
                                  {(TRANSITION_OPTIONS.find(t => t.key === transition)?.label || 'Coupe directe')}
                                </span>

                                <span className="badge rounded-pill text-bg-light d-flex align-items-center gap-1">
                                  <i className="bi bi-sliders"></i>
                                  {audioMixOption === 'voice+music'
                                    ? 'Voix + musique'
                                    : audioMixOption === 'voice_only'
                                      ? 'Voix seule'
                                      : audioMixOption === 'music_only'
                                        ? 'Musique seule'
                                        : (audioMixOption || 'Audio')}
                                </span>

                                {exportFormat && (
                                  <span className="badge rounded-pill text-bg-primary d-flex align-items-center gap-1">
                                    <i className="bi bi-camera-reels"></i>
                                    {(exportFormat || '').toUpperCase()} · {exportRes} · {exportFPS} fps
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="row g-3 g-lg-4">
                              {/* ===== Left: Audio previews ===== */}
                              <div className="col-12 col-lg-6">
                                <div className="card border-0 shadow-sm rounded-4 h-100">
                                  <div className="card-body p-3">

                                    {/* Voix off */}
                                    <div className="mb-3">
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                          <span className="badge rounded-pill text-bg-dark d-flex align-items-center gap-1">
                                            <i className="bi bi-mic-fill"></i>
                                            Voix off
                                          </span>
                                        </div>

                                        {voiceUrl && (
                                          <span className="badge rounded-pill text-bg-light text-muted d-flex align-items-center gap-1">
                                            <i className="bi bi-soundwave"></i>
                                            Pré-écoute
                                          </span>
                                        )}
                                      </div>

                                      <div className="rounded-3 border bg-body-tertiary p-2">
                                        {voiceUrl ? (
                                          <audio
                                            controls
                                            src={String(voiceUrl)}
                                            className="w-100"
                                          />
                                        ) : (
                                          <div className="d-flex align-items-center gap-2 text-muted small fst-italic py-1">
                                            <i className="bi bi-mic-slash"></i>
                                            <span>Aucune pré-écoute de voix configurée pour l’instant.</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Musique */}
                                    <div>
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                          <span className="badge rounded-pill text-bg-dark d-flex align-items-center gap-1">
                                            <i className="bi bi-music-note-beamed"></i>
                                            Musique
                                          </span>
                                        </div>

                                        {musicUrl && (
                                          <span className="badge rounded-pill text-bg-light text-muted d-flex align-items-center gap-1">
                                            <i className="bi bi-headphones"></i>
                                            Pré-écoute
                                          </span>
                                        )}
                                      </div>

                                      <div className="rounded-3 border bg-body-tertiary p-2 mb-3">
                                        {musicUrl ? (
                                          <audio
                                            controls
                                            src={String(musicUrl)}
                                            className="w-100"
                                          />
                                        ) : (
                                          <div className="d-flex align-items-center gap-2 text-muted small fst-italic py-1">
                                            <i className="bi bi-music-note-off"></i>
                                            <span>Aucune pré-écoute de musique configurée pour l’instant.</span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Volume bar */}
                                      <div>
                                        <div className="d-flex align-items-center justify-content-between mb-1 small">
                                          <span className="text-muted d-flex align-items-center gap-1">
                                            <i className="bi bi-volume-up-fill"></i>
                                            Volume musique
                                          </span>
                                          <span className="fw-semibold">
                                            {Math.round((musicVolume ?? 0) * 100)}%
                                          </span>
                                        </div>
                                        <div
                                          className="progress"
                                          role="progressbar"
                                          aria-valuemin={0}
                                          aria-valuemax={100}
                                          aria-valuenow={Math.round((musicVolume ?? 0) * 100)}
                                          style={{ height: 6 }}
                                        >
                                          <div
                                            className="progress-bar"
                                            style={{
                                              width: `${Math.round((musicVolume ?? 0) * 100)}%`,
                                              transition: 'width 0.25s ease',
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>

                                  </div>
                                </div>
                              </div>

                              {/* ===== Right: Technical summary ===== */}
                              <div className="col-12 col-lg-6">
                                <div className="card border-0 shadow-sm rounded-4 h-100">
                                  <div className="card-body p-3">
                                    <ul className="list-unstyled mb-3">
                                      <li className="mb-3 d-flex gap-3">
                                        <div
                                          className="rounded-circle bg-body-secondary d-flex align-items-center justify-content-center flex-shrink-0"
                                          style={{ width: 32, height: 32 }}
                                        >
                                          <i className="bi bi-arrow-left-right"></i>
                                        </div>
                                        <div>
                                          <span className="text-muted small d-block">Transition</span>
                                          <div className="fw-semibold">
                                            {(TRANSITION_OPTIONS.find(t => t.key === transition)?.label || 'Coupe directe')}
                                            <span className="opacity-75 ms-1 small">
                                              ({transition || 'none'})
                                            </span>
                                          </div>
                                        </div>
                                      </li>

                                      <li className="mb-3 d-flex gap-3">
                                        <div
                                          className="rounded-circle bg-body-secondary d-flex align-items-center justify-content-center flex-shrink-0"
                                          style={{ width: 32, height: 32 }}
                                        >
                                          <i className="bi bi-sliders"></i>
                                        </div>
                                        <div>
                                          <span className="text-muted small d-block">Mix audio</span>
                                          <div className="fw-semibold">
                                            {audioMixOption === 'voice+music'
                                              ? 'Voix + musique'
                                              : audioMixOption === 'voice_only'
                                                ? 'Voix seule'
                                                : audioMixOption === 'music_only'
                                                  ? 'Musique seule'
                                                  : (audioMixOption || 'Audio')}
                                            <span className="opacity-75 ms-1 small">
                                              ({Math.round((musicVolume ?? 0) * 100)}% de musique)
                                            </span>
                                          </div>
                                        </div>
                                      </li>

                                      <li className="mb-3 d-flex gap-3">
                                        <div
                                          className="rounded-circle bg-body-secondary d-flex align-items-center justify-content-center flex-shrink-0"
                                          style={{ width: 32, height: 32 }}
                                        >
                                          <i className="bi bi-camera-reels"></i>
                                        </div>
                                        <div>
                                          <span className="text-muted small d-block">Export vidéo</span>
                                          <div className="fw-semibold">
                                            {exportPreset} — {exportRes} · {exportFPS} fps · {exportFormat && exportFormat.toUpperCase()} · {({
                                              classic: 'Classic',
                                              grid_2x2: 'Grid 2×2',
                                              split_v: 'Split Vertical',
                                              split_h: 'Split Horizontal',
                                              hero_3: 'Hero + 3',
                                              strips_3: '3 Strips',
                                              corners_float: 'Floating Corners',
                                            }[exportTemplate] || exportTemplate || 'Classic')}
                                            <span className="opacity-75 ms-1 small">
                                              — bitrate {exportBitrate}
                                            </span>
                                          </div>
                                        </div>
                                      </li>

                                      {exportWatermarkText && (
                                        <li className="mb-1 d-flex gap-3">
                                          <div
                                            className="rounded-circle bg-body-secondary d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 32, height: 32 }}
                                          >
                                            <i className="bi bi-water"></i>
                                          </div>
                                          <div>
                                            <span className="text-muted small d-block">Watermark</span>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                              <span className="badge rounded-pill text-bg-light border d-flex align-items-center gap-1">
                                                <i className="bi bi-type"></i>
                                                « {exportWatermarkText} »
                                              </span>
                                            </div>
                                          </div>
                                        </li>
                                      )}
                                    </ul>

                                    <div className="small text-muted d-flex align-items-start gap-2">
                                      <i className="bi bi-lightbulb-fill text-warning mt-1"></i>
                                      <span>
                                        Vérifiez l’équilibre entre <strong>voix</strong> et <strong>musique</strong> avant de lancer l’export final.
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>



                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(montageMode === 'IA' || montageMode === 'Advanced') && (
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body">
                    {montageConfigChoice === 'LOAD' && selectedConfigId ? (
                      <div className="alert alert-info d-flex align-items-start" role="alert">
                        <i className="bi bi-journal-text me-2 fs-5"></i>
                        <div>Réglage chargé : <strong>{(montageConfigs.find(x => String(x.id) === String(selectedConfigId))?.name) || `Config #${selectedConfigId}`}</strong></div>
                      </div>
                    ) : (
                      <div className="alert alert-secondary d-flex align-items-start" role="alert">
                        <i className="bi bi-sliders me-2 fs-5"></i>
                        <div>Définissez un nouveau réglage (voix, musique, thème, ordre des médias...). Enregistrez-le pour réutiliser plus tard.</div>
                      </div>
                    )}


                  </div>
                </div>

              )}



              {aiPlan && (
                <div className="alert alert-light mt-3 border">
                  <h6><i className="bi bi-card-text me-2" />Plan de montage IA</h6>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{aiPlan}</pre>
                </div>
              )}
              {/* Drafts now live in Montage */}


              {/* Drafts now live in Montage */}
              {montageMode !== 'Manuel' && (
                <details className="mt-4 drafts-accordion" open>
                  <summary
                    className="d-flex align-items-center justify-content-between gap-2"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-journal-text" />
                      <strong>Brouillons enregistrés</strong>
                      <span className="text-muted small d-none d-sm-inline">(auto / manuels)</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-light text-dark border">
                        <i className="bi bi-collection me-1" /> Liste
                      </span>
                      <i className="bi bi-chevron-down chevron" aria-hidden="true" />
                    </div>
                  </summary>

                  <div className="mt-2">
                    <DraftsList
                      projectId={id}
                      token={token}
                      addToast={addToast}
                      reloadSignal={reloadDraftsTick}
                      onRestore={(draft) => {
                        try {
                          restoreDraftIntoEditor(draft);
                          addToast('Brouillon chargé dans l’éditeur', 'success');
                        } catch (e) {
                          addToast('Impossible de charger ce brouillon', 'error');
                        }
                      }}
                    />
                  </div>

                  <style>{`
      .drafts-accordion > summary .chevron { transition: transform .15s ease; }
      .drafts-accordion[open] > summary .chevron { transform: rotate(180deg); }
    `}</style>
                </details>
              )}





            </section>

          )}

          {/* === EXPORT ===ich bin */}
          {canSeeMontage && step === 'export' && (
            <section className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-body p-4 p-lg-4">

                {/* ===== HEADER / CONTEXT ===== */}
                <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                  <div className="d-flex flex-column gap-2">

                    {/* Title + main badges */}
                    <div className="d-flex align-items-center flex-wrap gap-3">
                      <div>
                        <h3 className="mb-0 d-flex align-items-center flex-wrap gap-2">
                          <i className="bi bi-box-arrow-up me-2" />
                          <span>Export final</span>
                          <span className="badge bg-primary-subtle text-primary-emphasis rounded-pill d-inline-flex align-items-center gap-1">
                            <i className="bi bi-layers" />
                            {montageModeLabel}
                          </span>

                         
                        </h3>

                        <small className="text-muted d-flex align-items-center flex-wrap gap-2 mt-1">
                          <span className="d-inline-flex align-items-center gap-1">
                            <i className="bi bi-magic" />
                            Ajuste le format, le watermark et l’audio avant de publier.
                          </span>
                          <span className="d-inline-flex align-items-center gap-1">
                            <i className="bi bi-youtube" /> YouTube
                          </span>
                          <span className="d-inline-flex align-items-center gap-1">
                            <i className="bi bi-instagram" /> Instagram
                          </span>
                          <span className="d-inline-flex align-items-center gap-1">
                            <i className="bi bi-tiktok" /> TikTok
                          </span>
                        </small>
                      </div>
                    </div>

                  </div>

                  {/* Async toggle (IA only) */}
                  {montageMode !== 'Manuel' && (
                    <div className="d-flex align-items-center">

                    </div>
                  )}
                </div>

                {/* ===== TWICK ADVANCED EDITOR (embedded) ===== */}
                {!STUDIO_ASSISTED_ONLY && montageMode === 'Advanced' && (
                  <div className="mb-4">
                    <div className="alert alert-primary-subtle border rounded-4 d-flex align-items-start gap-2">
                      <i className="bi bi-sliders2-vertical fs-5 mt-1" />
                      <div className="small">
                        <div className="fw-semibold">Advanced mode actif</div>
                        <div>Vous pouvez ajuster votre timeline dans Twick directement ici avant l’export final.</div>
                      </div>
                    </div>

                    <div className="border rounded-4 overflow-hidden shadow-sm">
                      {/* Twick cloud bar */}
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 border-bottom bg-white">
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge text-bg-dark">
                            <i className="bi bi-sliders me-1" /> Twick
                          </span>

                          {twickLoading ? (
                            <span className="text-muted small">
                              <i className="bi bi-arrow-repeat me-1" />
                              Chargement...
                            </span>
                          ) : (
                            <span className="text-muted small">
                              {twickSavedAt ? (
                                <>Dernière sauvegarde : {new Date(twickSavedAt).toLocaleString()}</>
                              ) : (
                                <>Aucune sauvegarde</>
                              )}
                            </span>
                          )}

                          {twickDirty ? (
                            <span className="badge text-bg-warning">
                              <i className="bi bi-exclamation-triangle me-1" />
                              Non sauvegardé
                            </span>
                          ) : (
                            twickSavedAt ? (
                              <span className="badge text-bg-success">
                                <i className="bi bi-check2 me-1" />
                                Sauvegardé
                              </span>
                            ) : null
                          )}
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <button
                            className="btn btn-outline-secondary btn-sm rounded-pill"
                            type="button"
                            onClick={() => {
                              setTwickReloadKey((k) => k + 1);
                              loadTwickState();
                            }}
                            disabled={twickLoading || twickSaving}
                          >
                            <i className="bi bi-arrow-clockwise me-1" />
                            Recharger
                          </button>

                          <button
                            className="btn btn-outline-dark btn-sm rounded-pill"
                            type="button"
                            onClick={() => {
                              try {
                                window.dispatchEvent(new CustomEvent('twick:requestExport', { detail: { format: 'mp4' } }));
                              } catch (_) { }
                              try {
                                window.postMessage({ type: 'TWICK_EXPORT_REQUEST', format: 'mp4' }, '*');
                              } catch (_) { }
                              (window.addToast || addToast)?.("Demande d’export envoyée à Twick.", 'info');
                            }}
                            disabled={twickLoading || twickSaving}
                            title="Déclencher l’export depuis l’éditeur Twick"
                          >
                            <i className="bi bi-box-arrow-up-right me-1" />
                            Exporter
                          </button>

                          <button
                            className="btn btn-outline-primary btn-sm rounded-pill"
                            type="button"
                            onClick={() => twickUploadInputRef.current?.click?.()}
                            disabled={twickLoading || twickSaving}
                            title="Uploader une vidéo exportée depuis Twick"
                          >
                            <i className="bi bi-upload me-1" />
                            Uploader
                          </button>

                          <button
                            className="btn btn-primary btn-sm rounded-pill"
                            type="button"
                            onClick={() => generateFinalVideo()}
                            disabled={twickLoading || twickSaving || busy}
                            title="Générer la vidéo finale via le backend (fallback)"
                          >

                            <i className="bi bi-magic me-1" />
                            Générer
                          </button>

                          <input
                            ref={twickUploadInputRef}
                            type="file"
                            accept="video/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const f = e.target.files && e.target.files[0];
                              if (f) uploadTwickFinalVideo(f, f.name);
                              // allow re-selecting same file
                              e.target.value = '';
                            }}
                          />

                          <button
                            className="btn btn-dark btn-sm rounded-pill"
                            type="button"
                            onClick={() => saveTwickState()}
                            disabled={twickLoading || twickSaving || !twickState}
                          >
                            <i className={`bi ${twickSaving ? 'bi-hourglass-split' : 'bi-cloud-arrow-up'} me-1`} />
                            {twickSaving ? 'Sauvegarde...' : 'Enregistrer'}
                          </button>
                        </div>
                      </div>

                      <TwickEditorPage
                        key={`twick-${projectId}-${twickReloadKey}`}
                        embedded
                        montageParams={advancedMontageParams}
                        initialState={twickState || {}}
                        // 🔁 Compatibility: Twick implementations vary in callback naming
                        onStateChange={handleTwickStateChange}
                        onChange={handleTwickStateChange}
                        onUpdateState={handleTwickStateChange}
                        onDirty={() => setTwickDirty(true)}
                        onSave={(state) => saveTwickState(state)}
                        onExport={(fileOrBlob, name) => uploadTwickFinalVideo(fileOrBlob, name)}
                        // ✅ Stop internal retry loops: we load state ourselves via loadTwickState()
                        backend={
                          isOnline ? {
                            saveUrl: `/projects/${projectId}/twick-state`,
                            uploadFinalUrl: `/projects/${projectId}/twick/upload-final`,
                          } : undefined
                        }
                      />
                    </div>
                  </div>
                )}

                {/* ===== INFO SYNCHRO / ASYNCHRONE ===== */}
                {montageMode !== 'Manuel' ? (
                  exportAsync ? (
                    <div
                      className="alert alert-info rounded-4 mb-4 d-flex align-items-start gap-3 shadow-sm"
                      role="alert"
                    >
                      <div

                      >
                        <i className="bi bi-bell fs-5 text-primary" />
                      </div>
                      <div className="small">
                        <div className="fw-semibold mb-1">
                          Rendu en tâche de fond
                          {exportJobId ? (
                            <span className="ms-2 badge bg-primary-subtle text-primary-emphasis">
                              Job&nbsp;#{exportJobId}
                            </span>
                          ) : null}
                        </div>
                        <div>
                          La vidéo sera générée en arrière-plan. Vous recevrez une notification avec un lien
                          vers l’onglet <strong>Export</strong> dès qu’elle sera prête.
                        </div>
                      </div>
                    </div>
                  ) : busy ? (
                    <div className="mb-4">
                      <LoaderDots label="Génération de la vidéo en cours…" />
                    </div>
                  ) : null
                ) : (
                  <div
                    className="alert alert-secondary rounded-4 mb-4 d-flex align-items-start gap-3 shadow-sm"
                    role="alert"
                  >
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: 32, height: 32, backgroundColor: 'rgba(108,117,125,0.12)' }}
                    >
                      <i className="bi bi-tools fs-5 text-secondary" />
                    </div>
                    <div className="small">
                      <div className="fw-semibold mb-1">Mode manuel</div>
                      <div>
                        Importez votre vidéo finale pour profiter de l’aperçu, du partage et de la clôture du
                        projet. Le rendu asynchrone n’est pas disponible dans ce mode.
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== MAIN GRID ===== */}
                <div className="row g-4">
                  {/* ===== LEFT COL : SETTINGS + PLAYER + ACTIONS ===== */}
                  <div className="col-12 col-xl-8">
                    <div className="card border-0 shadow-sm h-100 rounded-4">
                      <div className="card-body">

                        {/* Export settings (IA + Advanced) */}
                        {(montageMode === 'IA' || montageMode === 'Advanced') && (
                          <>
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                              <h6 className="mb-0 d-flex align-items-center gap-2">
                                <span
                                  className="badge rounded-pill bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-1"
                                >
                                  
                                </span>
                              </h6>
                              <span className="badge bg-light text-secondary-emphasis d-flex align-items-center gap-1">
                                <i className="bi bi-hdd-network" />
                                Studio Backend
                              </span>
                            </div>

                            <div className="rounded-4 border bg-light-subtle p-3 mb-3">
                              <div className="row g-3">
                                {/* Preset */}
                                <div className="col-12 col-md-6">
                                  <label className="form-label small text-muted d-flex align-items-center gap-1">
                                    <i className="bi bi-aspect-ratio" />
                                    Préréglage
                                  </label>
                                  <select
                                    className="form-select"
                                    value={exportPreset}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setExportPreset(val);
                                      if (val === 'youtube_1080p_16_9') {
                                        setExportRes('1920x1080');
                                        setExportAspect('16:9');
                                        setExportFPS(30);
                                      }
                                      if (val === 'instagram_square') {
                                        setExportRes('1080x1080');
                                        setExportAspect('1:1');
                                        setExportFPS(30);
                                      }
                                      if (val === 'tiktok_9_16') {
                                        setExportRes('1080x1920');
                                        setExportAspect('9:16');
                                        setExportFPS(30);
                                      }
                                    }}
                                  >
                                    <option value="youtube_1080p_16_9">YouTube 1080p (16:9)</option>
                                    <option value="instagram_square">Instagram carré (1:1)</option>
                                    <option value="tiktok_9_16">TikTok 1080p (9:16)</option>
                                    <option value="custom">Personnalisé</option>
                                  </select>
                                </div>



{/* Template */}
<div className="col-12">
  <div className="wv-tplHead d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
    <div className="d-flex align-items-center gap-2">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2 wv-tplToggle"
        onClick={() => setTplPanelOpen(v => !v)}
      >
        <i className="bi bi-grid-1x2" />
        <span className="fw-semibold">Templates</span>
        <i className={"bi " + (tplPanelOpen ? "bi-chevron-up" : "bi-chevron-down")} />
      </button>
      <span className="small text-muted d-none d-md-inline">
        Pick a style, then export
      </span>
    </div>

    <div className="d-flex align-items-center gap-2">
      <span className="small text-muted d-none d-sm-inline">Motion</span>
      <select
        className="form-select form-select-sm wv-tplMotion"
        value={exportMotion}
        onChange={(e) => setExportMotion(e.target.value)}
      >
        <option value="capcut">Dynamic</option>
        <option value="subtle">Subtle</option>
        <option value="none">Off</option>
      </select>
      <button
        type="button"
        className={"btn btn-sm " + (tplHasOverride ? "btn-primary" : "btn-outline-secondary")}
        onClick={openTplOverrideModal}
        title="Override template defaults (session only)"
      >
        <i className="bi bi-sliders me-2" />
        Customize
        {tplHasOverride && (
          <span className="badge bg-light text-dark ms-2">Custom</span>
        )}
      </button>
    </div>
  </div>

  {/* Template parameter override modal (session-only; no DB update) */}
  {tplOverrideOpen && (
    <div
      className="modal fade show"
      style={{ display: 'block', background: 'rgba(0,0,0,.55)' }}
      role="dialog"
      onMouseDown={() => setTplOverrideOpen(false)}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-0">Template settings</h5>
              <div className="small text-muted">
                Overrides are saved only for your current session and won’t update the template in the database.
              </div>
            </div>
            <button type="button" className="btn-close" onClick={() => setTplOverrideOpen(false)} />
          </div>

          <div className="modal-body">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Transition</label>
                <select
                  className="form-select"
                  value={tplOverrideDraft.transition}
                  onChange={(e) => setTplOverrideDraft((p) => ({ ...(p || {}), transition: e.target.value }))}
                >
                  {TRANSITION_OPTIONS.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Template default: <span className="fw-semibold">{selectedTplBaseDefaults?.transition || TEMPLATE_DEFAULTS[exportTemplate]?.transition || 'none'}</span>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Motion</label>
                <select
                  className="form-select"
                  value={tplOverrideDraft.motion}
                  onChange={(e) => setTplOverrideDraft((p) => ({ ...(p || {}), motion: e.target.value }))}
                >
                  <option value="capcut">Dynamic</option>
                  <option value="subtle">Subtle</option>
                  <option value="none">Off</option>
                </select>
                <div className="form-text">
                  Template default: <span className="fw-semibold">{selectedTplBaseDefaults?.motion || TEMPLATE_DEFAULTS[exportTemplate]?.motion || 'capcut'}</span>
                </div>
              </div>
            </div>

            <div className="alert alert-light border mt-3 mb-0">
              <div className="small">
                Tip: templates apply defaults automatically (random display order, default transition/motion). You can override them here before generating.
              </div>
            </div>
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <button type="button" className="btn btn-outline-danger" onClick={clearTplOverride}>
              Reset to template defaults
            </button>

            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setTplOverrideOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveTplOverride}>
                Save &amp; Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )}



  {/* Templates */}
  {tplPanelOpen && (
    <>
      <div className="wv-tplCats">
        {(() => {
          const allowed = new Set(allExportTemplates.map(t => t.cat));
          // Always show "Pour toi" and "IA GENERATED" chips (even if no DB templates yet)
          allowed.add('for_you');
          allowed.add('ai_generated');

          const effectiveCat = allowed.has(tplCategory) ? tplCategory : 'for_you';
          const cats = EXPORT_TPL_CAT_ORDER
            .filter((id) => allowed.has(id))
            .map((id) => ({ id, label: EXPORT_TPL_CAT_LABELS[id] || id }));

          return cats.map((c) => (
            <button
              key={c.id}
              type="button"
              className={"wv-chip " + (effectiveCat === c.id ? "is-active" : "")}
              onClick={() => { setTplCategory(c.id); setTplPage(1); }}
            >
              {c.label}
            </button>
          ));
        })()}

      </div>

      {(() => {
        const allowed = new Set(allExportTemplates.map(t => t.cat));
        allowed.add('for_you');
        allowed.add('ai_generated');
        const effectiveCat = allowed.has(tplCategory) ? tplCategory : 'for_you';
        if (effectiveCat !== 'ai_generated') return null;

        const disabledNoAuth = !token;

        return (
          <div className="wv-aiTplBox">
            <div className="wv-aiTplHeader">
              <div className="wv-aiTplTitle">
                AI Template Generator <span className="wv-betaPill">BETA</span>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => loadDbExportTemplates()}
                title="Refresh templates"
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Refresh
              </button>
            </div>

            {disabledNoAuth && (
              <div className="alert alert-warning py-2 mb-2">
                You must be logged in to generate and save templates.
              </div>
            )}

            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label small mb-1">Template name</label>
                <input
                  className="form-control form-control-sm"
                  value={aiTplName}
                  onChange={(e) => setAiTplName(e.target.value)}
                  placeholder="Dreamy beaches"
                  disabled={disabledNoAuth}
                />
              </div>

              <div className="col-md-8">
                <label className="form-label small mb-1">Description (optional)</label>
                <input
                  className="form-control form-control-sm"
                  value={aiTplDesc}
                  onChange={(e) => setAiTplDesc(e.target.value)}
                  placeholder="Short description shown on the card"
                  disabled={disabledNoAuth}
                />
              </div>

              <div className="col-12">
                <label className="form-label small mb-1">Prompt</label>
                <textarea
                  className="form-control form-control-sm"
                  rows={2}
                  value={aiTplPrompt}
                  onChange={(e) => setAiTplPrompt(e.target.value)}
                  placeholder="Describe the vibe, pacing, layout, transitions, FX, audio..."
                  disabled={disabledNoAuth}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label small mb-1">Base layout</label>
                <select
                  className="form-select form-select-sm"
                  value={aiTplBaseId}
                  onChange={(e) => setAiTplBaseId(e.target.value)}
                  disabled={disabledNoAuth}
                >
                  <option value="classic">Classic</option>
                  <option value="grid_2x2">Grid 2×2</option>
                  <option value="split_v">Split Vertical</option>
                  <option value="split_h">Split Horizontal</option>
                  <option value="strips_3">3 Strips</option>
                  <option value="floating_corners">Floating Corners</option>
                  <option value="hero_plus_3">Hero + 3</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label small mb-1">Transition</label>
                <select
                  className="form-select form-select-sm"
                  value={aiTplTransition}
                  onChange={(e) => setAiTplTransition(e.target.value)}
                  disabled={disabledNoAuth}
                >
                  <option value="fade">Fade</option>
                  <option value="zoom">Zoom</option>
                  <option value="swipe">Swipe</option>
                  <option value="glitch">Glitch</option>
                  <option value="cut">Cut</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label small mb-1">FX</label>
                <select
                  className="form-select form-select-sm"
                  value={aiTplFx}
                  onChange={(e) => setAiTplFx(e.target.value)}
                  disabled={disabledNoAuth}
                >
                  <option value="none">None</option>
                  <option value="dreamy">Dreamy</option>
                  <option value="moonrise">Moonrise</option>
                  <option value="beat_punch">Beat Punch</option>
                  <option value="glitch">Glitch</option>
                  <option value="vhs">VHS</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label small mb-1">Motion</label>
                <select
                  className="form-select form-select-sm"
                  value={aiTplMotion}
                  onChange={(e) => setAiTplMotion(e.target.value)}
                  disabled={disabledNoAuth}
                >
                  <option value="subtle">Subtle</option>
                  <option value="capcut">CapCut-style</option>
                  <option value="none">None</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label small mb-1">Music mode</label>
                <select
                  className="form-select form-select-sm"
                  value={aiTplMusicMode}
                  onChange={(e) => setAiTplMusicMode(e.target.value)}
                  disabled={disabledNoAuth}
                >
                  <option value="auto">Auto</option>
                  <option value="prompt">Prompt</option>
                  <option value="none">None</option>
                </select>
              </div>

              <div className="col-md-8">
                <label className="form-label small mb-1">Music prompt (optional)</label>
                <input
                  className="form-control form-control-sm"
                  value={aiTplMusicPrompt}
                  onChange={(e) => setAiTplMusicPrompt(e.target.value)}
                  placeholder="Ambient lofi, 30s, soft vibe..."
                  disabled={disabledNoAuth || aiTplMusicMode === 'none'}
                />
              </div>

              <div className="col-12 d-flex align-items-center justify-content-between mt-1" style={{ gap: 10, flexWrap: 'wrap' }}>
                <div className="d-flex align-items-center gap-2">
                  <input
                    className="form-control form-control-sm"
                    style={{ width: 220 }}
                    value={aiTplPassword}
                    onChange={(e) => setAiTplPassword(e.target.value)}
                    placeholder="AI password (optional)"
                    disabled={disabledNoAuth}
                  />
                  <span className="text-white-50 small">Templates are PUBLIC by default.</span>
                </div>

                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  disabled={disabledNoAuth || aiTplLoading}
                  onClick={() => onGenerateAiTemplate()}
                >
                  {aiTplLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-magic me-1" />
                      Save & Use
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="wv-aiBatch">
              <div className="wv-aiBatchHead">Batch generation</div>
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label small mb-1">How many templates?</label>
                  <input
                    type="number"
                    min={1}
                    max={40}
                    className="form-control form-control-sm"
                    value={aiBatchCount}
                    onChange={(e) => setAiBatchCount(e.target.value)}
                    disabled={disabledNoAuth}
                  />
                </div>
                <div className="col-md-9 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    disabled={disabledNoAuth || aiBatchLoading}
                    onClick={() => onBatchGenerateAiTemplates()}
                  >
                    {aiBatchLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shuffle me-1" />
                        Generate batch
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="text-white-50 small mt-1">
                Batch generation creates diverse combinations (layout / transition / FX / motion / music) using your prompt.
              </div>
            </div>
          </div>
        );
      })()}

      <div className="wv-tplWrap">
    {(() => {
      // choose media ids for previews
      const baseIds =
        (Array.isArray(selectedMediaIds) && selectedMediaIds.length ? selectedMediaIds : [])
          .slice(0, 4);

      const takeIds = (n) => {
        const out = [];
        for (const x of baseIds) { if (out.length < n) out.push(x); }
        // fallback: pick from projectMedia if not enough
        if (out.length < n) {
          for (const m of (projectMedia || [])) {
            const mid = m?.id ?? m?.media_id ?? m?.mediaId;
            if (!mid) continue;
            if (out.includes(mid)) continue;
            out.push(mid);
            if (out.length >= n) break;
          }
        }
        return out.slice(0, n);
      };

      const Tile = ({ url, className }) => {
        const isV = /\.(mp4|mov|webm|mkv)$/i.test(url || '');
        if (!url) return <div className={"wv-tplTile skel " + (className || '')} />;
        return isV ? (
          <video className={"wv-tplTile " + (className || '')} src={url} muted playsInline preload="metadata" />
        ) : (
          <img className={"wv-tplTile " + (className || '')} src={url} alt="" loading="lazy" />
        );
      };

      const Canvas = ({ tpl }) => {
        const key = (tpl || '').trim();
        const isWall = key.startsWith('wall_');
        const isFx = key.startsWith('fx_');
        const isBg = key.startsWith('bg_');
        const isAni = key.startsWith('ani_');
        const isCollage = key.startsWith('collage_');
        const isRetro = key.startsWith('retro_');
        const isViby = key.startsWith('viby_');

        const meta = EXPORT_TEMPLATES.find(x => x.id === key);
        const badge = meta?.badge;

        const ids =
          isWall ? takeIds(1)
          : (isFx || isViby) ? takeIds(1)
          : (key === 'split_v' || key === 'split_h') ? takeIds(2)
          : (key === 'strips_3') ? takeIds(3)
          : (isBg || isAni || isCollage || isRetro) ? takeIds(4)
          : takeIds(4);        const urls = ids.map(mediaUrlById).filter(Boolean);
        const u = (i) => urls[i] || '';

        if (isFx) {
          const name =
            key === 'fx_moonrise' ? 'Moonrise'
            : key === 'fx_beat_punch' ? 'Beat Punch'
            : key === 'fx_dreamy' ? 'Dreamy'
            : key === 'fx_glitch' ? 'Glitch'
            : key === 'fx_vhs' ? 'VHS'
            : key === 'fx_neon_night' ? 'Neon Night'
            : key === 'fx_film_grain' ? 'Film Grain'
            : key === 'fx_bokeh_burst' ? 'Bokeh Burst'
            : key === 'fx_prism' ? 'Prism'
            : key === 'fx_fireflies' ? 'Fireflies'
            : key === 'fx_noir' ? 'Noir'
            : 'FX';

          return (
            <div className={"wv-tplCanvas fx " + tpl}>
              <Tile url={u(0)} className="fxBg" />
              <div className="fxShade" />
              <div className="fxBadge">FX</div>
              <div className="fxTitle">{name}</div>
            </div>
          );
        }


        if (isViby) {
          const title = String(meta?.title || 'Viby');
          const label = title.replace(/^Viby\s*[•-]?\s*/i, '').trim() || 'Viby';
          return (
            <div className={"wv-tplCanvas viby " + key}>
              <div className="vibyBg" />
              <div className="vibyHero">
                <Tile url={u(0)} />
              </div>
              <div className="vibyLabel">{String(badge || 'VIBY') + (label ? (' • ' + label.toUpperCase()) : '')}</div>
            </div>
          );
        }

        if (isWall) {
          return (
            <div className={"wv-tplCanvas wall " + tpl}>
              <Tile url={u(0)} className="wallBg" />
              <div className="wallShade" />
              {tpl === 'wall_sale' && (
                <>
                  <div className="wallSticker">50% OFF</div>
                  <div className="wallBar">
                    <div className="wallTitle">Food & Beverage</div>
                    <div className="wallSub">Limited time offer</div>
                  </div>
                </>
              )}
              {tpl === 'wall_brand' && (
                <div className="wallCard">
                  <div className="wallTitle">BRANDNAME</div>
                  <div className="wallSub">Effortless style for modern life</div>
                  <div className="wallCta">Shop now</div>
                </div>
              )}
              {tpl === 'wall_quote' && (
                <div className="wallQuote">“A story told by the world.”</div>
              )}
              {tpl === 'wall_lyrics' && (
                <div className="wallLyrics">
                  <div>♪ This is the moment</div>
                  <div className="dim">♪ We keep moving</div>
                </div>
              )}
              {tpl === 'wall_meme' && (
                <>
                  <div className="wallTopCap">WHEN THE EXPORT WORKS</div>
                  <div className="wallBotCap">FIRST TRY 😄</div>
                </>
              )}
              {tpl === 'wall_retro' && (
                <>
                  <div className="wallFilm" />
                  <div className="wallTs">00:10</div>
                </>
              
              )}
              {key === 'wall_podcast' && (
                <div className="wallPod">
                  <div className="wallTitle">PODCAST</div>
                  <div className="wallSub">New episode • 24 min</div>
                  <div className="wallWave">
                    <span /><span /><span /><span /><span /><span />
                  </div>
                </div>
              )}
              {key === 'wall_news' && (
                <div className="wallNews">
                  <div className="wallNewsHead">BREAKING</div>
                  <div className="wallNewsTitle">A fresh story drops</div>
                  <div className="wallTicker">
                    <span>LIVE • UPDATE • STORY • LIVE • UPDATE • STORY •</span>
                  </div>
                </div>
              )}
              {key === 'wall_recipe' && (
                <div className="wallRecipe">
                  <div className="wallTitle">RECIPE</div>
                  <div className="wallSub">3 ingredients • 10 min</div>
                  <div className="wallBadges">
                    <span className="pill">EASY</span>
                    <span className="pill dim">TASTY</span>
                  </div>
                </div>
              )}
              {key === 'wall_event' && (
                <div className="wallEvent">
                  <div className="wallTitle">EVENT</div>
                  <div className="wallSub">Sat 20:00 • Paris</div>
                  <div className="wallCta">Save the date</div>
                </div>
              )}
{tpl === 'wall_speed' && (
                <div className="wallHud">
                  <span className="pill">SPEED</span>
                  <span className="pill dim">x1.25</span>
                  {badge ? <span className="pill dim">{badge}</span> : null}
                </div>
              )}
            </div>
          );
        }

        
        // Background presets (preview-only visuals; backend stays compatible via baseId mapping)
        if (isBg) {
          const label =
            key === 'bg_gradient_wave' ? 'Wave'
            : key === 'bg_neon_grid' ? 'Neon'
            : key === 'bg_particles' ? 'Particles'
            : key === 'bg_blur_cover' ? 'Blur'
            : key === 'bg_paper_cut' ? 'Paper'
            : key === 'bg_sunburst' ? 'Burst'
            : key === 'bg_glassmorphism' ? 'Glass'
            : 'BG';

          return (
            <div className={"wv-tplCanvas bg " + key}>
              <div className="bgAnim" />
              {/* media-driven cover when available */}
              {key === 'bg_blur_cover' ? <Tile url={u(0)} className="bgCover" /> : null}

              <Tile url={u(0)} className="bgP1" />
              <Tile url={u(1)} className="bgP2" />
              <Tile url={u(2)} className="bgP3" />
              <Tile url={u(3)} className="bgP4" />

              <div className="bgBadge">{label}</div>
            </div>
          );
        }

        // Animated presets (preview-only; output uses base template + motion defaults)
        if (isAni) {
          const label =
            key === 'ani_carousel' ? 'Carousel'
            : key === 'ani_flash_cards' ? 'Flash'
            : key === 'ani_parallax' ? 'Parallax'
            : key === 'ani_pop_stickers' ? 'Pop'
            : key === 'ani_comic_panels' ? 'Comic'
            : key === 'ani_timeline' ? 'Timeline'
            : key === 'ani_spotlight' ? 'Spotlight'
            : 'Anim';

          return (
            <div className={"wv-tplCanvas anim " + key}>
              <div className="animBg" />
              <div className="animStage">
                <Tile url={u(0)} className="animCard a1" />
                <Tile url={u(1)} className="animCard a2" />
                <Tile url={u(2)} className="animCard a3" />
              </div>
              <div className="animPill">{label}</div>
            </div>
          );
        }

        // Collage / Retro (distinct previews; mapped to known bases for backend)
        if (isCollage || isRetro) {
          const label =
            key === 'collage_diagonal' ? 'Diagonal'
            : key === 'collage_stack' ? 'Stack'
            : key === 'collage_circle' ? 'Circle'
            : key === 'retro_polaroid' ? 'Polaroid'
            : key === 'retro_magazine' ? 'Magazine'
            : key === 'retro_super8' ? 'Super 8'
            : 'Style';

          return (
            <div className={"wv-tplCanvas style " + key}>
              <div className="styleBg" />
              <Tile url={u(0)} className="styleHero" />
              <Tile url={u(1)} className="styleS s1" />
              <Tile url={u(2)} className="styleS s2" />
              <Tile url={u(3)} className="styleS s3" />
              <div className="stylePill">{label}</div>
            </div>
          );
        }

if (tpl === 'classic') {
          // stacked clips look
          return (
            <div className="wv-tplCanvas classic">
              <div className="wv-tplStack">
                <div className="wv-tplLine" />
                <div className="wv-tplLine dim" />
                <div className="wv-tplLine dim2" />
              </div>
              <div className="wv-tplHint">Sequential</div>
            </div>
          );
        }

        if (tpl === 'grid_2x2') {
          return (
            <div className="wv-tplCanvas grid2x2">
              <Tile url={u(0)} className="a" />
              <Tile url={u(1)} className="b" />
              <Tile url={u(2)} className="c" />
              <Tile url={u(3)} className="d" />
              <div className="wv-tplBadgeMini">2×2</div>
            </div>
          );
        }

        if (tpl === 'split_v') {
          return (
            <div className="wv-tplCanvas splitv">
              <Tile url={u(0)} className="left" />
              <Tile url={u(1)} className="right" />
              <div className="wv-tplBadgeMini">Split</div>
            </div>
          );
        }

        if (tpl === 'split_h') {
          return (
            <div className="wv-tplCanvas splith">
              <Tile url={u(0)} className="top" />
              <Tile url={u(1)} className="bot" />
              <div className="wv-tplBadgeMini">Split</div>
            </div>
          );
        }

        if (tpl === 'hero_3') {
          return (
            <div className="wv-tplCanvas hero3">
              <Tile url={u(0)} className="hero" />
              <Tile url={u(1)} className="s1" />
              <Tile url={u(2)} className="s2" />
              <Tile url={u(3)} className="s3" />
              <div className="wv-tplBadgeMini">Hero</div>
            </div>
          );
        }

        if (tpl === 'strips_3') {
          return (
            <div className="wv-tplCanvas strips3">
              <Tile url={u(0)} className="s a" />
              <Tile url={u(1)} className="s b" />
              <Tile url={u(2)} className="s c" />
              <div className="wv-tplBadgeMini">3</div>
            </div>
          );
        }

        if (tpl === 'corners_float') {
          return (
            <div className="wv-tplCanvas corners">
              {/* fake blur bg */}
              <div className="wv-tplBlurBg" />
              <Tile url={u(0)} className="p1" />
              <Tile url={u(1)} className="p2" />
              <Tile url={u(2)} className="p3" />
              <Tile url={u(3)} className="p4" />
              <div className="wv-tplBadgeMini">Float</div>
            </div>
          );
        }

        return (
          <div className="wv-tplCanvas">
            <div className="wv-tplHint">Template</div>
          </div>
        );
      };

      const templates = allExportTemplates;

      const allowedCats = new Set(templates.map(t => t.cat));
      allowedCats.add('for_you');
      allowedCats.add('ai_generated');

      const effectiveCat = allowedCats.has(tplCategory) ? tplCategory : 'for_you';

      const visible = templates.filter(t => {
        if (effectiveCat === 'for_you') return true;
        if (effectiveCat === 'ai_generated') return (t.cat === 'ai_generated' || t.cat === 'for_you');
        return t.cat === effectiveCat;
      });

      // Pagination
      const total = visible.length;
      const pageSize = Math.max(1, Number(tplPageSize || 12));
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const page = Math.min(Math.max(1, Number(tplPage || 1)), totalPages);
      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const pageItems = visible.slice(start, end);

      // If the selected template exists in this category but is not visible on the current page,
      // offer a "go to selected" shortcut (no auto-jump to avoid fighting the user while browsing).
      const selectedIndex = visible.findIndex(x => x?.id === exportTemplate);
      const selectedPage = (selectedIndex >= 0) ? (Math.floor(selectedIndex / pageSize) + 1) : null;

      const Pager = () => {
        if (totalPages <= 1) return null;
        return (
          <div className="wv-tplPager d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div className="small text-muted">
              {total} template{total > 1 ? 's' : ''} • Page {page}/{totalPages}
              {total > 0 && (
                <span className="d-none d-md-inline">
                  {' '}• Showing {Math.min(start + 1, total)}–{Math.min(end, total)}
                </span>
              )}
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {selectedPage && selectedPage !== page && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                  onClick={() => setTplPage(selectedPage)}
                  title="Go to the selected template"
                >
                  <i className="bi bi-bullseye" />
                  Selected (p.{selectedPage})
                </button>
              )}

              <label className="small text-muted mb-0 d-none d-sm-inline">Per page</label>
              <select
                className="form-select form-select-sm wv-tplPageSize"
                value={String(pageSize)}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setTplPageSize(Number.isFinite(n) ? n : 12);
                  setTplPage(1);
                }}
                aria-label="Templates per page"
              >
                <option value="8">8</option>
                <option value="12">12</option>
                <option value="24">24</option>
                <option value="48">48</option>
              </select>

              <div className="btn-group btn-group-sm" role="group" aria-label="Template pagination">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setTplPage((p) => Math.max(1, Number(p || 1) - 1))}
                >
                  <i className="bi bi-chevron-left" /> Prev
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setTplPage((p) => Math.min(totalPages, Number(p || 1) + 1))}
                >
                  Next <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>
          </div>
        );
      };

      return (
        <>
          <Pager />
          <div className="wv-tplStrip">
            {pageItems.map((t) => {
              const active = exportTemplate === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setExportTemplate(t.id)}
                  className={
                    "wv-tplCard text-start " +
                    (active ? "is-active" : "")
                  }
                >
                  <div className="wv-tplPreview">
                    <Canvas tpl={t.__db ? (t.baseId || 'classic') : t.id} />
                  </div>

                  <div className="wv-tplMeta">
                    <div className="wv-tplTop">
                      <div className="wv-tplTitle">{t.title}</div>
                      {t.badge && t.badge !== 'LAYOUT' && (
                        <span className="wv-tplPill auto">{t.badge}</span>
                      )}
                    </div>
                    <div className="wv-tplDesc">{t.desc}</div>

                    {t.__db && (
                      <div className="wv-tplDbMeta">
                        <span className={"wv-tplPill " + ((String(t.visibility || 'PRIVATE').toUpperCase() === 'PUBLIC') ? "pub" : "priv")}>
                          {String(t.visibility || 'PRIVATE').toUpperCase()}
                        </span>
                        <span className="wv-tplBy">by {t.created_by || 'unknown'}</span>

                        {me?.username && (String(t.created_by || '') === String(me.username || '')) && (
                          <span
                            role="button"
                            tabIndex={0}
                            className="wv-tplMiniBtn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const current = String(t.visibility || 'PRIVATE').toUpperCase();
                              const next = (current === 'PUBLIC') ? 'PRIVATE' : 'PUBLIC';
                              toggleTemplateVisibility(t.id, next);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                const current = String(t.visibility || 'PRIVATE').toUpperCase();
                                const next = (current === 'PUBLIC') ? 'PRIVATE' : 'PUBLIC';
                                toggleTemplateVisibility(t.id, next);
                              }
                            }}
                            title="Toggle visibility"
                          >
                            {(String(t.visibility || 'PRIVATE').toUpperCase() === 'PUBLIC') ? 'Make private' : 'Publish'}
                          </span>
                        )}
                      </div>
                    )}

                    {active ? (
                      <div className="wv-tplSelected">
                        <i className="bi bi-check2-circle" />
                        Selected
                      </div>
                    ) : (
                      <div className="wv-tplSelected muted">
                        <i className="bi bi-hand-index-thumb" />
                        Choose
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <Pager />
        </>
      );
    })()}
  
  {/* Viby: TikTok/CapCut-like controls (session-only; no DB writes) */}
  <div className="wv-vibyBox mt-3">
    <div className="wv-vibyHead d-flex align-items-center justify-content-between flex-wrap gap-2">
      <button
        type="button"
        className={"btn btn-sm " + (vibyOpen ? "btn-dark" : "btn-outline-dark") + " wv-vibyToggle d-inline-flex align-items-center gap-2"}
        onClick={() => setVibyOpen((v) => !v)}
      >
        <i className={"bi " + (vibyOpen ? "bi-sliders2" : "bi-sliders")} />
        <span>Viby</span>
        <span className="wv-vibySub d-none d-md-inline">music • filters • effects</span>
      </button>

      <div className="d-flex align-items-center gap-2 flex-wrap">
        {vibyHasOverride ? (
          <span className="badge text-bg-primary d-inline-flex align-items-center gap-1">
            <i className="bi bi-stars" /> Custom
          </span>
        ) : (
          <span className="badge text-bg-light border d-inline-flex align-items-center gap-1">
            <i className="bi bi-dot" /> Default
          </span>
        )}

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
          onClick={resetViby}
          disabled={!exportTemplate}
          title="Reset viby settings for this template (session only)"
        >
          <i className="bi bi-arrow-counterclockwise" />
          Reset
        </button>
      </div>
    </div>

    {vibyOpen && (
      <div className="wv-vibyBody mt-2">
        <div className="row g-3">
          <div className="col-12 col-lg-4">
            <div className="wv-vibyCard">
              <button
                type="button"
                className="wv-vibyCardTitleBtn"
                onClick={() => setVibyMusicOpen((v) => !v)}
              >
                <span className="wv-vibyCardTitle"><i className="bi bi-music-note-beamed me-2" />Music</span>
                <i className={"bi " + (vibyMusicOpen ? "bi-chevron-up" : "bi-chevron-down")} />
              </button>

              {vibyMusicOpen && (
                <>
                  <label className="form-label small text-muted mb-1">Mode</label>
              <select
                className="form-select form-select-sm"
                value={String(vibyDraft?.music_mode || 'auto')}
                onChange={(e) => updateViby({ music_mode: e.target.value })}
              >
                <option value="none">None</option>
                <option value="auto">Auto (engine picks)</option>
                <option value="preset">Preset</option>
                <option value="prompt">Prompt</option>
              </select>

              <div className="mt-2">
                <label className="form-label small text-muted mb-1">Preset</label>
                <select
                  className="form-select form-select-sm"
                  value={String(vibyDraft?.music_preset || WV_DEFAULT_VIBY.music_preset)}
                  onChange={(e) => updateViby({ music_preset: e.target.value })}
                  disabled={String(vibyDraft?.music_mode || 'auto') === 'none'}
                >
                  {VIBY_MUSIC_PRESETS.map(p => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
                <div className="form-text small">
                  {(VIBY_MUSIC_PRESETS.find(x => x.key === String(vibyDraft?.music_preset || ''))?.hint) || ''}
                </div>
              </div>

              <div className="mt-2">
                <label className="form-label small text-muted mb-1">Prompt (optional)</label>
                <input
                  className="form-control form-control-sm"
                  value={String(vibyDraft?.music_prompt || '')}
                  onChange={(e) => updateViby({ music_prompt: e.target.value })}
                  placeholder="Ex: uplifting pop with claps, 120bpm, summer vibe"
                  disabled={String(vibyDraft?.music_mode || 'auto') === 'none'}
                />
              </div>

              <div className="wv-vibyMini mt-2">
                <i className="bi bi-info-circle me-1" />
                For now, Viby is exported as <code>export_viby</code> (backend can start consuming it anytime).
              </div>
                </>
              )}
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="wv-vibyCard">
              <div className="wv-vibyCardTitle"><i className="bi bi-droplet-half me-2" />Filter</div>

              <label className="form-label small text-muted mb-1">Filter</label>
              <select
                className="form-select form-select-sm"
                value={String(vibyDraft?.filter || 'none')}
                onChange={(e) => updateViby({ filter: e.target.value })}
              >
                {VIBY_FILTERS.map(f => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>

              <div className="mt-2">
                <div className="d-flex align-items-center justify-content-between">
                  <label className="form-label small text-muted mb-1">Intensity</label>
                  <span className="small text-muted">{wvClamp(vibyDraft?.filter_intensity ?? 60, 0, 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={wvClamp(vibyDraft?.filter_intensity ?? 60, 0, 100)}
                  onChange={(e) => updateViby({ filter_intensity: parseInt(e.target.value, 10) })}
                  className="form-range"
                  disabled={String(vibyDraft?.filter || 'none') === 'none'}
                />
              </div>

              <div className="mt-2">
                <div className="d-flex align-items-center justify-content-between">
                  <label className="form-label small text-muted mb-1">Cut energy</label>
                  <span className="small text-muted">{wvClamp(vibyDraft?.cut_energy ?? 70, 0, 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={wvClamp(vibyDraft?.cut_energy ?? 70, 0, 100)}
                  onChange={(e) => updateViby({ cut_energy: parseInt(e.target.value, 10) })}
                  className="form-range"
                />
                <div className="form-text small">Higher = faster pacing & more aggressive motion.</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="wv-vibyCard">
              <div className="wv-vibyCardTitle"><i className="bi bi-magic me-2" />Effects</div>

              <div className="form-check form-switch mt-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="wvBeatSync"
                  checked={!!(vibyDraft?.beat_sync)}
                  onChange={(e) => updateViby({ beat_sync: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="wvBeatSync">
                  Beat sync (snap cuts & FX to beats)
                </label>
              </div>

              <div className="wv-vibyChips mt-2">
                {VIBY_EFFECTS.map((ef) => {
                  const on = Array.isArray(vibyDraft?.effects) && vibyDraft.effects.includes(ef.key);
                  return (
                    <button
                      key={ef.key}
                      type="button"
                      className={"wv-vibyChip " + (on ? "is-on" : "")}
                      onClick={() => updateViby((prev) => ({ effects: wvToggleInArray(prev.effects || [], ef.key) }))}
                    >
                      {ef.label}
                      {on ? <i className="bi bi-check2 ms-1" /> : null}
                    </button>
                  );
                })}
              </div>

              <div className="wv-vibyMini mt-2">
                <i className="bi bi-shield-check me-1" />
                Session-only override — no template DB update.
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>

</div>

  <div className="form-text small mt-2">
    Templates can be classic, multi‑tile, FX, background, or animated. Classic keeps the sequential montage; others compose your selected media into a styled scene.
  </div>
    </>
  )}

  <style>{`
    /* === Templates (responsive + scroll) === */
    .wv-tplMotion{ width: 170px; }
    @media (max-width: 576px){ .wv-tplMotion{ width: 150px; } }
    .wv-tplToggle{ border-radius: 999px; }
    .wv-tplCats{
      display:flex;
      gap: 10px;
      overflow-x:auto;
      padding: 6px 2px 10px;
      -webkit-overflow-scrolling: touch;
    }
    .wv-tplCats::-webkit-scrollbar{ height: 6px; }
    .wv-chip{
      border: 1px solid rgba(15,23,42,.12);
      background: rgba(248,250,252,.9);
      color: rgba(33,37,41,.75);
      border-radius: 999px;
      padding: .45rem .75rem;
      font-weight: 650;
      font-size: .82rem;
      white-space: nowrap;
      transition: transform .12s ease, border-color .12s ease, background .12s ease;
    }
    .wv-chip:hover{ transform: translateY(-1px); border-color: rgba(13,110,253,.35); }
    .wv-chip.is-active{
      background: rgba(13,110,253,.12);
      border-color: rgba(13,110,253,.35);
      color: rgba(13,110,253,.95);
    }


    /* Pagination (templates) */
    .wv-tplPager{
      padding: 8px 10px;
      border: 1px solid rgba(15,23,42,.10);
      border-radius: 14px;
      background: rgba(248,250,252,.75);
    }
    .wv-tplPageSize{ width: 92px; border-radius: 12px; }
    @media (max-width: 576px){
      .wv-tplPageSize{ width: 86px; }
      .wv-tplPager{ padding: 8px; }
    }


    .wv-tplStrip{
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 12px;
    }
    @media (max-width: 576px){
      .wv-tplStrip{
        display: flex;
        overflow-x: auto;
        gap: 10px;
        padding-bottom: 6px;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
      }
      .wv-tplStrip::-webkit-scrollbar{ height: 6px; }
      .wv-tplCard{ min-width: 260px; scroll-snap-align: start; }
    }

    .wv-tplCard{
      border: 1px solid rgba(15,23,42,.12);
      border-radius: 18px;
      background: #fff;
      display: grid;
      grid-template-columns: 140px 1fr;
      gap: 12px;
      padding: 12px;
      align-items: center;
      box-shadow: 0 10px 30px rgba(15,23,42,.06);
      transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
    }
    .wv-tplCard:hover{
      transform: translateY(-1px);
      box-shadow: 0 14px 42px rgba(15,23,42,.10);
      border-color: rgba(13,110,253,.35);
    }
    .wv-tplCard.is-active{
      border-color: rgba(13,110,253,.55);
      background: linear-gradient(180deg, rgba(13,110,253,.08), rgba(255,255,255,1) 55%);
      box-shadow: 0 16px 54px rgba(13,110,253,.18);
    }

    .wv-tplPreview{
      border-radius: 16px;
      overflow: hidden;
      background: radial-gradient(120px 70px at 20% 15%, rgba(13,110,253,.20), transparent 55%),
                  radial-gradient(120px 70px at 80% 85%, rgba(32,201,151,.16), transparent 55%),
                  #0b1220;
      height: 92px;
      position: relative;
    }


    /* Viby UI */
    .wv-vibyBox{
      border: 1px solid rgba(15,23,42,.10);
      border-radius: 18px;
      background: radial-gradient(240px 120px at 12% 20%, rgba(13,110,253,.14), transparent 60%),
                  radial-gradient(240px 120px at 90% 40%, rgba(32,201,151,.12), transparent 65%),
                  rgba(255,255,255,.9);
      padding: 12px;
      box-shadow: 0 14px 46px rgba(15,23,42,.06);
      backdrop-filter: blur(8px);
    }
    .wv-vibyToggle{ border-radius: 999px; }
    .wv-vibySub{ font-size:.78rem; opacity:.75; }
    .wv-vibyCard{
      border: 1px solid rgba(15,23,42,.10);
      border-radius: 16px;
      background: rgba(255,255,255,.92);
      padding: 12px;
      box-shadow: 0 10px 26px rgba(15,23,42,.05);
      height: 100%;
    }
    .wv-vibyCardTitle{
      font-weight: 800;
      font-size: .92rem;
      display:flex;
      align-items:center;
      margin-bottom: 10px;
    }
    .wv-vibyCardTitleBtn{
      width: 100%;
      border: 0;
      background: transparent;
      padding: 0;
      margin-bottom: 10px;
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
      color: inherit;
      cursor: pointer;
    }
    .wv-vibyCardTitleBtn .wv-vibyCardTitle{ margin-bottom: 0; }
    .wv-vibyCardTitleBtn i.bi{ opacity: .65; }
    .wv-vibyCardTitleBtn:hover i.bi{ opacity: .9; }

    .wv-vibyChips{
      display:flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .wv-vibyChip{
      border: 1px solid rgba(15,23,42,.12);
      background: rgba(255,255,255,.75);
      border-radius: 999px;
      padding: 6px 10px;
      font-size: .78rem;
      line-height: 1;
      transition: transform .08s ease, background .12s ease, border-color .12s ease;
      display:inline-flex;
      align-items:center;
      gap: 6px;
      white-space: nowrap;
    }
    .wv-vibyChip:hover{ transform: translateY(-1px); }
    .wv-vibyChip.is-on{
      background: rgba(13,110,253,.12);
      border-color: rgba(13,110,253,.45);
    }
    .wv-vibyMini{
      font-size: .78rem;
      color: rgba(33,37,41,.72);
      background: rgba(255,255,255,.7);
      border: 1px dashed rgba(15,23,42,.20);
      border-radius: 14px;
      padding: 8px 10px;
    }

    /* Viby template preview */
    .wv-tplCanvas.viby{
      position: relative;
      height: 100%;
      width: 100%;
      display:flex;
      align-items:center;
      justify-content:center;
      overflow:hidden;
      border-radius: 16px;
      background: radial-gradient(120px 80px at 20% 20%, rgba(255,255,255,.16), transparent 60%),
                  radial-gradient(120px 80px at 80% 80%, rgba(13,110,253,.26), transparent 65%),
                  #0b1220;
    }
    .wv-tplCanvas.viby .vibyBg{
      position:absolute;
      inset:-20%;
      background: conic-gradient(from 220deg, rgba(13,110,253,.55), rgba(32,201,151,.45), rgba(255,193,7,.35), rgba(13,110,253,.55));
      filter: blur(18px);
      opacity: .45;
      animation: vibySpin 10s linear infinite;
    }
    @keyframes vibySpin{
      from{ transform: rotate(0deg) scale(1.05); }
      to{ transform: rotate(360deg) scale(1.05); }
    }
    .wv-tplCanvas.viby .vibyHero{
      position:absolute;
      inset: 10px;
      border-radius: 14px;
      overflow:hidden;
      box-shadow: 0 14px 36px rgba(0,0,0,.35);
      transform: rotate(-2deg);
    }
    .wv-tplCanvas.viby .vibyHero .wv-tplTile{ width:100%; height:100%; object-fit: cover; display:block; }
    .wv-tplCanvas.viby .vibyLabel{
      position:absolute;
      bottom: 8px;
      left: 8px;
      background: rgba(0,0,0,.55);
      color: rgba(255,255,255,.92);
      font-weight: 800;
      letter-spacing: .06em;
      font-size: .62rem;
      padding: 5px 8px;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,.14);
      backdrop-filter: blur(8px);
    }

    .wv-tplMeta{ min-width: 0; }
    .wv-tplTop{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
    .wv-tplTitle{ font-weight: 700; font-size: 0.98rem; }
    .wv-tplDesc{ font-size: .83rem; color: rgba(33,37,41,.72); margin-top: 2px; }

    .wv-tplTitle{ min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .wv-tplDesc{
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }
    .wv-tplTop{ min-width:0; }
    .wv-tplDbMeta{
      margin-top: 6px;
      display:flex;
      align-items:center;
      gap: 8px;
      flex-wrap: wrap;
      min-width:0;
      max-width: 100%;
    }
    .wv-tplBy{
      font-size: .78rem;
      color: rgba(33,37,41,.55);
      min-width:0;
      max-width: 100%;
      overflow:hidden;
      text-overflow:ellipsis;
      white-space:nowrap;
    }
    .wv-tplMiniBtn{
      margin-left: auto;
      font-size: .75rem;
      padding: 2px 8px;
      border-radius: 999px;
      border: 1px solid rgba(13,110,253,.25);
      color: rgba(13,110,253,.85);
      background: rgba(13,110,253,.08);
      white-space: nowrap;
    }
    .wv-tplPill.pub{ background: rgba(25,135,84,.12); color:#198754; border-color: rgba(25,135,84,.18); }
    .wv-tplPill.priv{ background: rgba(108,117,125,.12); color:#6c757d; border-color: rgba(108,117,125,.18); }

    .wv-aiBatch{
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px dashed rgba(255,255,255,.14);
    }
    .wv-aiBatchHead{
      font-weight: 700;
      margin-bottom: 6px;
    }
    .wv-tplPill{
      font-size: .70rem;
      padding: .25rem .55rem;
      border-radius: 999px;
      letter-spacing: .4px;
      font-weight: 700;
      border: 1px solid transparent;
      white-space: nowrap;
    }
    .wv-tplPill.auto{ background: rgba(108,117,125,.14); color: #495057; border-color: rgba(108,117,125,.18); }
    .wv-tplPill.capcut{ background: rgba(13,110,253,.14); color: #0d6efd; border-color: rgba(13,110,253,.18); }

    .wv-tplSelected{
      margin-top: 8px;
      display:flex;
      align-items:center;
      gap: 8px;
      font-size: .82rem;
      font-weight: 600;
      color: rgba(13,110,253,.92);
    }
    .wv-tplSelected.muted{ color: rgba(33,37,41,.45); font-weight: 600; }

    /* === Template canvas (real media) === */
    .wv-tplCanvas{ position: relative; width: 100%; height: 100%; }
    .wv-tplCanvas.wall .wallBg{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; filter: saturate(1.05) contrast(1.02); }
    .wv-tplCanvas.wall .wallShade{ position:absolute; inset:0; background: linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.55)); }
    .wv-tplCanvas.wall .wallSticker{
      position:absolute; right:10px; bottom:10px;
      background: rgba(13,110,253,.92);
      color:#fff; font-weight:800; letter-spacing:.3px;
      padding: 6px 10px; border-radius: 12px;
      box-shadow: 0 10px 25px rgba(13,110,253,.35);
      font-size: .78rem;
    }
    .wv-tplCanvas.wall .wallBar{
      position:absolute; left:10px; bottom:10px;
      background: rgba(255,255,255,.92);
      border: 1px solid rgba(15,23,42,.10);
      border-radius: 14px;
      padding: 7px 10px;
      max-width: 78%;
    }
    .wv-tplCanvas.wall .wallCard{
      position:absolute; left:10px; bottom:10px;
      background: rgba(255,255,255,.92);
      border: 1px solid rgba(15,23,42,.10);
      border-radius: 14px;
      padding: 9px 10px;
      max-width: 78%;
    }
    .wv-tplCanvas.wall .wallTitle{ font-weight: 850; font-size: .80rem; color:#0b1220; }
    .wv-tplCanvas.wall .wallSub{ font-weight: 650; font-size: .72rem; color: rgba(11,18,32,.65); margin-top: 2px; }
    .wv-tplCanvas.wall .wallCta{
      margin-top: 6px;
      display:inline-block;
      background: rgba(13,110,253,.10);
      border: 1px solid rgba(13,110,253,.18);
      color: #0d6efd;
      padding: 4px 8px;
      border-radius: 999px;
      font-size: .70rem;
      font-weight: 800;
      letter-spacing:.3px;
    }
    .wv-tplCanvas.wall .wallQuote{
      position:absolute; left:10px; right:10px; bottom:12px;
      background: rgba(255,255,255,.12);
      border: 1px solid rgba(255,255,255,.20);
      color:#fff;
      padding: 10px 12px;
      border-radius: 14px;
      font-weight: 800;
      text-align:center;
      backdrop-filter: blur(8px);
      font-size: .82rem;
    }
    .wv-tplCanvas.wall .wallLyrics{
      position:absolute; left:10px; right:10px; bottom:12px;
      color:#fff;
      font-weight: 850;
      font-size: .78rem;
      line-height: 1.2;
      text-shadow: 0 2px 10px rgba(0,0,0,.35);
    }
    .wv-tplCanvas.wall .wallLyrics .dim{ opacity:.75; margin-top:4px; }

    /* FX presets preview */
    .wv-tplCanvas.fx .fxBg{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; filter: saturate(1.12) contrast(1.05); }
    .wv-tplCanvas.fx .fxShade{ position:absolute; inset:0; background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.18), rgba(0,0,0,.55)); }
    .wv-tplCanvas.fx .fxBadge{
      position:absolute; left:10px; top:10px;
      background: rgba(255,255,255,.16);
      border: 1px solid rgba(255,255,255,.22);
      color:#fff;
      padding: 4px 10px;
      border-radius: 999px;
      font-weight: 900;
      font-size: .70rem;
      letter-spacing:.7px;
      backdrop-filter: blur(8px);
    }
    .wv-tplCanvas.fx .fxTitle{
      position:absolute; left:10px; right:10px; bottom:12px;
      color:#fff;
      font-weight: 900;
      font-size: .86rem;
      text-shadow: 0 2px 14px rgba(0,0,0,.45);
    }
    .wv-tplCanvas.fx.fx_moonrise .fxShade{ background: radial-gradient(circle at 30% 25%, rgba(255,255,255,.22), rgba(0,0,0,.62)); }
    .wv-tplCanvas.fx.fx_beat_punch .fxShade{ background: radial-gradient(circle at 70% 30%, rgba(13,110,253,.18), rgba(0,0,0,.62)); }
    .wv-tplCanvas.fx.fx_dreamy .fxShade{ background: radial-gradient(circle at 35% 30%, rgba(255,192,203,.18), rgba(0,0,0,.62)); }
    .wv-tplCanvas.fx.fx_glitch .fxShade{ background: radial-gradient(circle at 70% 30%, rgba(0,255,255,.12), rgba(0,0,0,.66)); }
    .wv-tplCanvas.fx.fx_vhs .fxShade{ background: radial-gradient(circle at 30% 30%, rgba(255,234,167,.12), rgba(0,0,0,.66)); }
    .wv-tplCanvas.wall .wallTopCap,
    .wv-tplCanvas.wall .wallBotCap{
      position:absolute; left:10px; right:10px;
      color:#fff; font-weight: 900;
      text-align:center;
      text-shadow: 0 2px 12px rgba(0,0,0,.45);
      letter-spacing:.4px;
      font-size: .80rem;
    }
    .wv-tplCanvas.wall .wallTopCap{ top:10px; }
    .wv-tplCanvas.wall .wallBotCap{ bottom:12px; }
    .wv-tplCanvas.wall .wallFilm{
      position:absolute; inset:8px;
      border-radius: 12px;
      border: 2px solid rgba(255,255,255,.18);
      box-shadow: inset 0 0 0 6px rgba(0,0,0,.25);
    }
    .wv-tplCanvas.wall .wallTs{
      position:absolute; left:10px; bottom:10px;
      background: rgba(0,0,0,.45);
      color:#fff;
      padding: 4px 8px;
      border-radius: 10px;
      font-weight: 850;
      font-size: .72rem;
      backdrop-filter: blur(8px);
    }
    .wv-tplCanvas.wall .wallHud{
      position:absolute; left:10px; top:10px;
      display:flex; gap:6px; flex-wrap:wrap;
    }
    .wv-tplCanvas.wall .wallHud .pill{
      background: rgba(0,0,0,.40);
      border: 1px solid rgba(255,255,255,.18);
      color:#fff;
      padding: 3px 8px;
      border-radius: 999px;
      font-weight: 900;
      font-size: .68rem;
      backdrop-filter: blur(8px);
    }
    .wv-tplCanvas.wall .wallHud .pill.dim{ opacity:.75; }

    .wv-tplHint{
      position:absolute; left:10px; bottom:8px;
      font-size: .72rem; color: rgba(255,255,255,.78);
      background: rgba(0,0,0,.35);
      padding: 2px 8px; border-radius: 999px;
      backdrop-filter: blur(6px);
    }
    .wv-tplBadgeMini{
      position:absolute; right:8px; top:8px;
      font-size: .68rem; font-weight: 800;
      color: rgba(255,255,255,.86);
      background: rgba(13,110,253,.55);
      padding: 2px 8px; border-radius: 999px;
      backdrop-filter: blur(6px);
      border: 1px solid rgba(255,255,255,.18);
    }

    .wv-tplTile{
      position:absolute;
      object-fit: cover;
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,.14);
      box-shadow: 0 12px 30px rgba(0,0,0,.25);
      background: rgba(255,255,255,.08);
    }
    .wv-tplTile.skel{
      background: linear-gradient(90deg, rgba(255,255,255,.08), rgba(255,255,255,.14), rgba(255,255,255,.08));
      background-size: 180% 100%;
      animation: wvSkel 1.2s infinite linear;
      border: 1px solid rgba(255,255,255,.08);
      box-shadow: none;
    }
    @keyframes wvSkel{ 0%{background-position: 0% 0%} 100%{background-position: 180% 0%} }

    /* classic */
    .wv-tplCanvas.classic{ background: transparent; }
    .wv-tplStack{ position:absolute; inset: 14px 14px 18px 14px; display:flex; flex-direction:column; gap:6px; }
    .wv-tplLine{ flex:1; border-radius: 10px; background: rgba(255,255,255,.18); }
    .wv-tplLine.dim{ opacity: .75; }
    .wv-tplLine.dim2{ opacity: .55; }

    /* grid 2x2 */
    .wv-tplCanvas.grid2x2 .wv-tplTile.a{ left:8px; top:8px; width: calc(50% - 10px); height: calc(50% - 10px); }
    .wv-tplCanvas.grid2x2 .wv-tplTile.b{ right:8px; top:8px; width: calc(50% - 10px); height: calc(50% - 10px); }
    .wv-tplCanvas.grid2x2 .wv-tplTile.c{ left:8px; bottom:8px; width: calc(50% - 10px); height: calc(50% - 10px); }
    .wv-tplCanvas.grid2x2 .wv-tplTile.d{ right:8px; bottom:8px; width: calc(50% - 10px); height: calc(50% - 10px); }

    /* split v */
    .wv-tplCanvas.splitv .wv-tplTile.left{ left:8px; top:8px; width: calc(50% - 10px); height: calc(100% - 16px); }
    .wv-tplCanvas.splitv .wv-tplTile.right{ right:8px; top:8px; width: calc(50% - 10px); height: calc(100% - 16px); }

    /* split h */
    .wv-tplCanvas.splith .wv-tplTile.top{ left:8px; top:8px; width: calc(100% - 16px); height: calc(50% - 10px); }
    .wv-tplCanvas.splith .wv-tplTile.bot{ left:8px; bottom:8px; width: calc(100% - 16px); height: calc(50% - 10px); }

    /* hero + 3 */
    .wv-tplCanvas.hero3 .wv-tplTile.hero{ left:8px; top:8px; width: 62%; height: calc(100% - 16px); }
    .wv-tplCanvas.hero3 .wv-tplTile.s1{ right:8px; top:8px; width: calc(38% - 16px); height: calc((100% - 24px)/3); }
    .wv-tplCanvas.hero3 .wv-tplTile.s2{ right:8px; top: calc(8px + (100% - 24px)/3 + 4px); width: calc(38% - 16px); height: calc((100% - 24px)/3); }
    .wv-tplCanvas.hero3 .wv-tplTile.s3{ right:8px; bottom:8px; width: calc(38% - 16px); height: calc((100% - 24px)/3); }

    /* strips 3 */
    .wv-tplCanvas.strips3 .wv-tplTile.s.a{ left:8px; top:8px; width: calc(33.33% - 10px); height: calc(100% - 16px); }
    .wv-tplCanvas.strips3 .wv-tplTile.s.b{ left: calc(33.33% + 3px); top:8px; width: calc(33.33% - 10px); height: calc(100% - 16px); }
    .wv-tplCanvas.strips3 .wv-tplTile.s.c{ right:8px; top:8px; width: calc(33.33% - 10px); height: calc(100% - 16px); }

    /* floating corners */
    .wv-tplCanvas.corners .wv-tplBlurBg{
      position:absolute; inset:0;
      background: radial-gradient(180px 110px at 20% 20%, rgba(13,110,253,.35), transparent 60%),
                  radial-gradient(180px 110px at 80% 80%, rgba(32,201,151,.30), transparent 60%),
                  rgba(255,255,255,.07);
      filter: blur(1px);
      opacity: .9;
    }
    .wv-tplCanvas.corners .wv-tplTile.p1{ left:10px; top:10px; width: 44%; height: 38%; }
    .wv-tplCanvas.corners .wv-tplTile.p2{ right:10px; top:10px; width: 44%; height: 38%; }
    .wv-tplCanvas.corners .wv-tplTile.p3{ left:10px; bottom:10px; width: 44%; height: 38%; }
    .wv-tplCanvas.corners .wv-tplTile.p4{ right:10px; bottom:10px; width: 44%; height: 38%; }
  

    
    /* ===== NEW: more stylish previews (BG / ANIM / STYLE) ===== */

    /* Extra FX shades */
    .wv-tplCanvas.fx.fx_neon_night .fxShade{
      background:
        radial-gradient(circle at 30% 35%, rgba(255,255,255,.18), transparent 55%),
        radial-gradient(circle at 75% 35%, rgba(255,0,255,.12), rgba(0,0,0,.72));
    }
    .wv-tplCanvas.fx.fx_film_grain .fxShade{
      background:
        linear-gradient(180deg, rgba(255,255,255,.10), rgba(0,0,0,.70));
    }
    .wv-tplCanvas.fx.fx_bokeh_burst .fxShade{
      background:
        radial-gradient(circle at 20% 30%, rgba(255,200,120,.16), transparent 55%),
        radial-gradient(circle at 70% 60%, rgba(120,200,255,.14), rgba(0,0,0,.70));
    }
    .wv-tplCanvas.fx.fx_prism .fxShade{
      background:
        radial-gradient(circle at 30% 30%, rgba(0,255,255,.12), transparent 55%),
        radial-gradient(circle at 70% 70%, rgba(255,0,120,.10), rgba(0,0,0,.74));
    }
    .wv-tplCanvas.fx.fx_fireflies .fxShade{
      background:
        radial-gradient(circle at 30% 30%, rgba(255,255,255,.12), transparent 60%),
        radial-gradient(circle at 70% 70%, rgba(32,201,151,.10), rgba(0,0,0,.74));
    }
    .wv-tplCanvas.fx.fx_noir .fxShade{
      background:
        linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.78));
    }
    .wv-tplCanvas.fx.fx_film_grain:before,
    .wv-tplCanvas.fx.fx_vhs:before,
    .wv-tplCanvas.fx.fx_noir:before{
      content:'';
      position:absolute;
      inset:0;
      background: repeating-linear-gradient(0deg, rgba(255,255,255,.05), rgba(255,255,255,.05) 1px, transparent 1px, transparent 4px);
      opacity:.18;
      mix-blend-mode: overlay;
      pointer-events:none;
      border-radius: 14px;
    }

    /* Background templates */
    .wv-tplCanvas.bg{
      overflow:hidden;
      border-radius: 14px;
      background: rgba(255,255,255,.06);
    }
    .wv-tplCanvas.bg .bgAnim{
      position:absolute;
      inset:-40px;
      opacity:.95;
      filter: blur(2px);
      background:
        radial-gradient(220px 160px at 20% 20%, rgba(13,110,253,.38), transparent 60%),
        radial-gradient(220px 160px at 80% 40%, rgba(255,0,122,.20), transparent 60%),
        radial-gradient(220px 160px at 70% 85%, rgba(32,201,151,.30), transparent 60%),
        rgba(255,255,255,.06);
      animation: wvBgDrift 5.2s ease-in-out infinite alternate;
    }
    @keyframes wvBgDrift{
      from{ transform: translate3d(-12px,-8px,0) scale(1.05); }
      to{ transform: translate3d(10px,8px,0) scale(1.08); }
    }
    .wv-tplCanvas.bg.bg_neon_grid .bgAnim{
      background:
        linear-gradient(180deg, rgba(0,0,0,.20), rgba(0,0,0,.72)),
        repeating-linear-gradient(90deg, rgba(0,255,255,.14), rgba(0,255,255,.14) 1px, transparent 1px, transparent 12px),
        repeating-linear-gradient(0deg, rgba(255,0,255,.10), rgba(255,0,255,.10) 1px, transparent 1px, transparent 12px);
      opacity:.85;
      filter: blur(.6px);
      animation: wvNeonPan 4.5s linear infinite;
    }
    @keyframes wvNeonPan{
      from{ transform: translateX(-24px) translateY(-16px); }
      to{ transform: translateX(24px) translateY(16px); }
    }
    .wv-tplCanvas.bg.bg_particles:after{
      content:'';
      position:absolute;
      inset:-20px;
      background:
        radial-gradient(circle at 10% 25%, rgba(255,255,255,.20), transparent 40%),
        radial-gradient(circle at 35% 70%, rgba(255,255,255,.18), transparent 45%),
        radial-gradient(circle at 70% 35%, rgba(255,255,255,.18), transparent 45%),
        radial-gradient(circle at 85% 75%, rgba(255,255,255,.16), transparent 45%);
      opacity:.55;
      filter: blur(1px);
      animation: wvParticleFloat 3.8s ease-in-out infinite alternate;
      pointer-events:none;
    }
    @keyframes wvParticleFloat{
      from{ transform: translate3d(-8px,6px,0); }
      to{ transform: translate3d(10px,-6px,0); }
    }

    .wv-tplCanvas.bg .bgCover{
      position:absolute;
      inset:0;
      width:100%;
      height:100%;
      object-fit:cover;
      filter: blur(8px) saturate(1.1) contrast(1.05);
      transform: scale(1.05);
      opacity:.65;
    }

    .wv-tplCanvas.bg .bgP1{ left:10px; top:12px; width: 42%; height: 38%; }
    .wv-tplCanvas.bg .bgP2{ right:10px; top:18px; width: 46%; height: 34%; }
    .wv-tplCanvas.bg .bgP3{ left:14px; bottom:12px; width: 48%; height: 34%; }
    .wv-tplCanvas.bg .bgP4{ right:12px; bottom:12px; width: 40%; height: 38%; }
    .wv-tplCanvas.bg.bg_sunburst .bgAnim{
      background:
        repeating-conic-gradient(from 0deg, rgba(255,255,255,.06) 0deg 8deg, rgba(13,110,253,.18) 8deg 16deg),
        radial-gradient(circle at 50% 50%, rgba(0,0,0,.15), rgba(0,0,0,.72));
      opacity:.80;
      filter: blur(.2px);
      animation: wvSpin 6s linear infinite;
    }
    @keyframes wvSpin{ from{transform: rotate(0deg) scale(1.05)} to{transform: rotate(360deg) scale(1.05)} }

    .wv-tplCanvas.bg.bg_paper_cut .bgAnim{
      background:
        linear-gradient(180deg, rgba(255,255,255,.10), rgba(0,0,0,.70)),
        repeating-linear-gradient(90deg, rgba(255,255,255,.08), rgba(255,255,255,.08) 2px, transparent 2px, transparent 10px);
      opacity:.70;
      filter: blur(.8px);
      animation: none;
    }
    .wv-tplCanvas.bg.bg_glassmorphism .bgAnim{
      background:
        radial-gradient(240px 160px at 25% 25%, rgba(255,255,255,.14), transparent 60%),
        radial-gradient(220px 160px at 75% 75%, rgba(13,110,253,.18), rgba(0,0,0,.70));
      opacity:.88;
    }
    .wv-tplCanvas.bg .bgBadge{
      position:absolute;
      left:10px;
      top:10px;
      background: rgba(255,255,255,.14);
      border: 1px solid rgba(255,255,255,.18);
      color:#fff;
      padding: 3px 10px;
      border-radius: 999px;
      font-weight: 900;
      font-size: .70rem;
      backdrop-filter: blur(10px);
      text-transform: uppercase;
      letter-spacing:.6px;
    }

    /* Animated templates */
    .wv-tplCanvas.anim{
      overflow:hidden;
      border-radius: 14px;
      background: rgba(255,255,255,.06);
    }
    .wv-tplCanvas.anim .animBg{
      position:absolute;
      inset:0;
      background:
        radial-gradient(220px 140px at 25% 30%, rgba(13,110,253,.22), transparent 60%),
        radial-gradient(220px 140px at 75% 70%, rgba(255,0,122,.16), rgba(0,0,0,.70));
      opacity:.95;
    }
    .wv-tplCanvas.anim .animStage{
      position:absolute;
      inset: 14px;
      perspective: 600px;
    }
    .wv-tplCanvas.anim .animCard{
      position:absolute;
      width: 70%;
      height: 75%;
      left: 50%;
      top: 50%;
      transform: translate(-50%,-50%);
      border-radius: 14px;
      box-shadow: 0 18px 50px rgba(0,0,0,.30);
    }
    .wv-tplCanvas.anim .animCard.a1{ transform: translate(-58%,-50%) rotate(-6deg); opacity:.92; }
    .wv-tplCanvas.anim .animCard.a2{ transform: translate(-50%,-54%) rotate(2deg);  opacity:1; }
    .wv-tplCanvas.anim .animCard.a3{ transform: translate(-42%,-50%) rotate(8deg);  opacity:.88; }
    .wv-tplCanvas.anim.ani_carousel .animCard{ animation: wvCarousel 2.8s ease-in-out infinite; }
    @keyframes wvCarousel{
      0%{ transform: translate(-58%,-50%) rotate(-6deg); }
      50%{ transform: translate(-42%,-52%) rotate(7deg); }
      100%{ transform: translate(-58%,-50%) rotate(-6deg); }
    }
    .wv-tplCanvas.anim.ani_flash_cards .animCard{ animation: wvFlash 1.8s ease-in-out infinite; }
    @keyframes wvFlash{
      0%{ transform: translate(-56%,-52%) rotate(-8deg) scale(.98); }
      45%{ transform: translate(-50%,-54%) rotate(1deg) scale(1.02); }
      100%{ transform: translate(-56%,-52%) rotate(-8deg) scale(.98); }
    }
    .wv-tplCanvas.anim.ani_spotlight:after{
      content:'';
      position:absolute;
      inset:-40px;
      background: radial-gradient(200px 160px at 20% 20%, rgba(255,255,255,.14), transparent 60%);
      animation: wvSpot 3.2s ease-in-out infinite alternate;
      mix-blend-mode: screen;
      pointer-events:none;
    }
    @keyframes wvSpot{
      from{ transform: translate3d(0,0,0); }
      to{ transform: translate3d(60px,45px,0); }
    }
    .wv-tplCanvas.anim .animPill{
      position:absolute;
      left:10px;
      top:10px;
      background: rgba(0,0,0,.35);
      border: 1px solid rgba(255,255,255,.16);
      color:#fff;
      padding: 3px 10px;
      border-radius: 999px;
      font-weight: 900;
      font-size: .70rem;
      backdrop-filter: blur(10px);
      letter-spacing:.5px;
    }

    /* Style (Collage/Retro) */
    .wv-tplCanvas.style{
      overflow:hidden;
      border-radius: 14px;
      background: rgba(255,255,255,.06);
    }
    .wv-tplCanvas.style .styleBg{
      position:absolute; inset:0;
      background:
        radial-gradient(260px 180px at 30% 30%, rgba(255,255,255,.10), transparent 60%),
        radial-gradient(260px 180px at 70% 70%, rgba(13,110,253,.18), rgba(0,0,0,.72));
      opacity:.95;
    }
    .wv-tplCanvas.style .styleHero{
      left:10px; top:10px; width: 56%; height: calc(100% - 20px);
      transform: rotate(-1.2deg);
    }
    .wv-tplCanvas.style .styleS.s1{ right:10px; top:10px; width: calc(44% - 16px); height: calc((100% - 28px)/3); }
    .wv-tplCanvas.style .styleS.s2{ right:10px; top: calc(10px + (100% - 28px)/3 + 4px); width: calc(44% - 16px); height: calc((100% - 28px)/3); }
    .wv-tplCanvas.style .styleS.s3{ right:10px; bottom:10px; width: calc(44% - 16px); height: calc((100% - 28px)/3); }

    .wv-tplCanvas.style.collage_stack .styleHero{ transform: rotate(-7deg) translateX(-2px); }
    .wv-tplCanvas.style.collage_stack .styleS.s1{ transform: rotate(6deg); }
    .wv-tplCanvas.style.collage_stack .styleS.s2{ transform: rotate(-5deg); }
    .wv-tplCanvas.style.collage_stack .styleS.s3{ transform: rotate(4deg); }

    .wv-tplCanvas.style.collage_diagonal .styleHero{
      clip-path: polygon(0 0, 85% 0, 60% 100%, 0 100%);
      border-radius: 14px;
    }

    .wv-tplCanvas.style.collage_circle .styleHero{
      border-radius: 999px;
      width: 54%;
      height: 54%;
      top: 23%;
      left: 10%;
      transform: none;
    }
    .wv-tplCanvas.style.collage_circle .styleS.s1{ border-radius: 999px; }
    .wv-tplCanvas.style.collage_circle .styleS.s2{ border-radius: 999px; }
    .wv-tplCanvas.style.collage_circle .styleS.s3{ border-radius: 999px; }

    .wv-tplCanvas.style.retro_polaroid:after{
      content:'';
      position:absolute;
      left:10px; right:10px; bottom:10px; top:10px;
      border-radius: 14px;
      border: 1px dashed rgba(255,255,255,.18);
      opacity:.9;
      pointer-events:none;
    }
    .wv-tplCanvas.style.retro_magazine .styleBg{
      background:
        repeating-linear-gradient(90deg, rgba(255,255,255,.06), rgba(255,255,255,.06) 2px, transparent 2px, transparent 12px),
        radial-gradient(240px 160px at 70% 30%, rgba(255,0,122,.14), transparent 60%),
        rgba(0,0,0,.72);
      opacity:.92;
    }
    .wv-tplCanvas.style.retro_super8:before{
      content:'';
      position:absolute;
      inset:8px;
      border-radius: 12px;
      border: 2px solid rgba(255,255,255,.12);
      box-shadow: inset 0 0 0 6px rgba(0,0,0,.25);
      pointer-events:none;
    }
    .wv-tplCanvas.style .stylePill{
      position:absolute;
      left:10px;
      top:10px;
      background: rgba(255,255,255,.14);
      border: 1px solid rgba(255,255,255,.18);
      color:#fff;
      padding: 3px 10px;
      border-radius: 999px;
      font-weight: 900;
      font-size: .70rem;
      backdrop-filter: blur(10px);
      letter-spacing:.5px;
    }

    /* New wall overlays */
    .wv-tplCanvas.wall .wallPod,
    .wv-tplCanvas.wall .wallNews,
    .wv-tplCanvas.wall .wallRecipe,
    .wv-tplCanvas.wall .wallEvent{
      position:absolute;
      left:10px;
      right:10px;
      bottom:12px;
      background: rgba(255,255,255,.14);
      border: 1px solid rgba(255,255,255,.18);
      border-radius: 14px;
      padding: 10px 12px;
      color:#fff;
      backdrop-filter: blur(10px);
      box-shadow: 0 16px 40px rgba(0,0,0,.25);
    }
    .wv-tplCanvas.wall .wallWave{
      display:flex; gap:4px; align-items:flex-end;
      margin-top: 6px; height: 18px;
    }
    .wv-tplCanvas.wall .wallWave span{
      display:block; width: 6px; border-radius: 6px;
      background: rgba(255,255,255,.75);
      height: 40%;
      animation: wvWave 1.2s ease-in-out infinite;
      opacity:.9;
    }
    .wv-tplCanvas.wall .wallWave span:nth-child(2){ animation-delay: .1s; height: 70%; }
    .wv-tplCanvas.wall .wallWave span:nth-child(3){ animation-delay: .2s; height: 55%; }
    .wv-tplCanvas.wall .wallWave span:nth-child(4){ animation-delay: .3s; height: 85%; }
    .wv-tplCanvas.wall .wallWave span:nth-child(5){ animation-delay: .4s; height: 60%; }
    .wv-tplCanvas.wall .wallWave span:nth-child(6){ animation-delay: .5s; height: 75%; }
    @keyframes wvWave{
      0%{ transform: scaleY(.6); opacity:.65; }
      50%{ transform: scaleY(1.0); opacity:1; }
      100%{ transform: scaleY(.6); opacity:.65; }
    }

    .wv-tplCanvas.wall .wallNewsHead{ font-weight: 900; letter-spacing:.6px; font-size: .70rem; opacity:.9; }
    .wv-tplCanvas.wall .wallNewsTitle{ margin-top: 2px; font-weight: 900; font-size: .82rem; }
    .wv-tplCanvas.wall .wallTicker{
      margin-top: 8px;
      background: rgba(0,0,0,.25);
      border: 1px solid rgba(255,255,255,.14);
      border-radius: 999px;
      overflow:hidden;
      padding: 4px 0;
      font-size: .70rem;
      font-weight: 800;
      white-space: nowrap;
    }
    .wv-tplCanvas.wall .wallTicker span{
      display:inline-block;
      padding-left: 8px;
      animation: wvTicker 2.8s linear infinite;
      opacity:.95;
    }
    @keyframes wvTicker{
      from{ transform: translateX(0); }
      to{ transform: translateX(-40%); }
    }
    .wv-tplCanvas.wall .wallRecipe .wallBadges{
      margin-top: 6px;
      display:flex;
      gap:6px;
      flex-wrap:wrap;
    }
    .wv-tplCanvas.wall .wallRecipe .pill{
      background: rgba(0,0,0,.25);
      border: 1px solid rgba(255,255,255,.14);
      color:#fff;
      padding: 3px 8px;
      border-radius: 999px;
      font-weight: 900;
      font-size: .68rem;
    }

/* === AI Template Generator (BETA) === */
    .wv-aiTplBox{
      margin-top: 10px;
      position: relative;
      border-radius: 16px;
      padding: 14px;
      border: 1px solid rgba(255,255,255,.14);
      background: linear-gradient(135deg, rgba(255,255,255,.10), rgba(255,255,255,.04));
      backdrop-filter: blur(12px);
      box-shadow: 0 18px 60px rgba(0,0,0,.18);
      overflow: hidden;
    }
    .wv-aiTplBox::before{
      content:"";
      position:absolute;
      inset:-2px;
      background:
        radial-gradient(420px 220px at 10% 0%, rgba(99,102,241,.22), transparent 60%),
        radial-gradient(420px 220px at 100% 20%, rgba(236,72,153,.16), transparent 60%),
        radial-gradient(420px 220px at 90% 110%, rgba(34,197,94,.10), transparent 65%);
      opacity: .9;
      pointer-events:none;
    }
    .wv-aiTplBox > *{ position: relative; }
    .wv-aiTplHeader{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
      margin-bottom: 8px;
    }
    .wv-aiTplTitle{
      font-weight: 700;
      letter-spacing: .2px;
    }
    .wv-betaPill{
      display:inline-block;
      margin-left: 8px;
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 999px;
      background: rgba(255,255,255,.12);
      border: 1px solid rgba(255,255,255,.14);
    }
                
    /* === DB template meta on cards === */
    .wv-tplDbMeta{
      display:flex;
      align-items:center;
      gap: 8px;
      margin-top: 8px;
      font-size: 12px;
      opacity: .9;
    }
    .wv-tplPill.pub{ background: rgba(25, 135, 84, .18); border: 1px solid rgba(25, 135, 84, .35); }
    .wv-tplPill.priv{ background: rgba(255,255,255,.10); border: 1px solid rgba(255,255,255,.14); }
    .wv-tplBy{ opacity: .85; }
    .wv-tplMiniBtn{
      margin-left: auto;
      font-weight: 700;
      cursor: pointer;
      padding: 3px 8px;
      border-radius: 10px;
      background: rgba(255,255,255,.12);
      border: 1px solid rgba(255,255,255,.14);
      user-select:none;
    }
        .wv-tplMiniBtn:hover{ background: rgba(255,255,255,.16); }

    /* === Media order (drag & drop) === */
    .wv-orderBox{
      padding: 12px;
      border-radius: 16px;
      border: 1px solid rgba(0,0,0,.06);
      background: rgba(255,255,255,.85);
      backdrop-filter: blur(8px);
      box-shadow: 0 8px 30px rgba(0,0,0,.06);
    }
    .wv-orderList{
      display:flex;
      flex-direction:column;
      gap: 10px;
      max-height: 340px;
      overflow:auto;
      padding-right: 6px;
    }
    .wv-orderItem{
      display:flex;
      align-items:center;
      gap: 10px;
      padding: 8px 10px;
      border-radius: 14px;
      border: 1px solid rgba(0,0,0,.08);
      background: #fff;
      cursor: grab;
      user-select:none;
    }
    .wv-orderItem.is-over{
      outline: 2px dashed rgba(13,110,253,.55);
      outline-offset: 2px;
    }
    .wv-orderItem.is-dragging{ opacity: .65; }
    .wv-orderThumb{
      width: 56px;
      height: 42px;
      border-radius: 10px;
      overflow:hidden;
      background: rgba(0,0,0,.05);
      flex: 0 0 auto;
      display:flex;
      align-items:center;
      justify-content:center;
    }
    .wv-orderThumb img,
    .wv-orderThumb video{
      width: 100%;
      height: 100%;
      object-fit: cover;
      display:block;
    }
    .wv-orderThumb video{ background: #000; }
    .wv-orderThumbFallback{
      width:100%;
      height:100%;
      display:flex;
      align-items:center;
      justify-content:center;
      color: rgba(0,0,0,.45);
      font-size: 18px;
    }
    .wv-orderMid{ min-width: 0; flex: 1 1 auto; }
    .wv-orderIdx{ font-weight: 800; letter-spacing: .1px; line-height: 1.2; }
    .wv-orderId{ white-space: nowrap; overflow:hidden; text-overflow: ellipsis; }
    .wv-orderActions{ display:flex; align-items:center; gap: 6px; flex: 0 0 auto; }
    .wv-orderActions .btn{ padding: 2px 8px; border-radius: 10px; }
    .wv-orderGrip{ opacity: .55; padding: 4px 6px; border-radius: 10px; }
    .wv-orderItem:hover .wv-orderGrip{ opacity: .85; }

    /* === Collapsible sections (details/summary) === */
    .wv-collapsibleSection{
      border-radius: 14px;
    }
    .wv-collapsibleSummary{
      cursor: pointer;
      list-style: none;
      outline: none;
      user-select: none;
    }
    .wv-collapsibleSummary::-webkit-details-marker{ display:none; }
    .wv-collapsibleSummary .wv-collapsibleChevron{
      opacity: .65;
      transition: transform .15s ease, opacity .15s ease;
    }
    details[open] .wv-collapsibleSummary .wv-collapsibleChevron{
      transform: rotate(180deg);
      opacity: .9;
    }

`}</style>
</div>


                                {/* Media order */}
                                <div className="col-12">
                                  <div className="wv-orderBox">
                                    <details className="wv-collapsibleSection">
                                      <summary className="wv-collapsibleSummary">
                                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                          <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-list-ol text-primary" />
                                            <span className="fw-semibold">Ordre d’affichage des médias</span>
                                            <span className={"badge " + (exportOrderTouched ? "text-bg-primary" : "text-bg-light")}>
                                              {exportOrderTouched ? "Personnalisé" : "Aléatoire (par défaut)"}
                                            </span>
                                          </div>
                                          <i className="bi bi-chevron-down wv-collapsibleChevron" aria-hidden="true" />
                                        </div>
                                        <div className="small text-muted mt-1">
                                          Par défaut, l’ordre est aléatoire. Vous pouvez le modifier ici avant de générer la vidéo.
                                        </div>
                                      </summary>

                                      <div className="mt-2">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => {
                                            setExportMediaOrder(wvShuffleCopy(selectedMediaIds));
                                            setExportOrderTouched(false);
                                          }}
                                          disabled={(selectedMediaIds?.length ?? 0) < 2}
                                          title="Revenir à l’ordre aléatoire (par défaut)"
                                        >
                                          <i className="bi bi-shuffle me-1" />
                                          Aléatoire
                                        </button>

                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => {
                                            setExportMediaOrder(Array.isArray(selectedMediaIds) ? [...selectedMediaIds] : []);
                                            setExportOrderTouched(true);
                                          }}
                                          disabled={(selectedMediaIds?.length ?? 0) < 2}
                                          title="Utiliser l’ordre de sélection"
                                        >
                                          <i className="bi bi-arrow-counterclockwise me-1" />
                                          Reset
                                        </button>
                                      </div>
                                        <div className="wv-orderList mt-3">
                                      {(Array.isArray(exportMediaOrder) ? exportMediaOrder : []).map((mid, idx) => {
                                        const url = mediaUrlById(mid);
                                        const isVid = isVideoUrl(url);
                                        const dragActive = exportDragIndex === idx;
                                        const over = exportDragOverIndex === idx;

                                        return (
                                          <div
                                            key={String(mid)}
                                            className={"wv-orderItem " + (dragActive ? "is-dragging " : "") + (over ? "is-over" : "")}
                                            draggable
                                            onDragStart={(e) => {
                                              try { e.dataTransfer.setData('text/plain', String(mid)); } catch { /* ignore */ }
                                              setExportDragIndex(idx);
                                            }}
                                            onDragEnd={() => {
                                              setExportDragIndex(null);
                                              setExportDragOverIndex(null);
                                            }}
                                            onDragOver={(e) => {
                                              e.preventDefault();
                                              setExportDragOverIndex(idx);
                                            }}
                                            onDrop={(e) => {
                                              e.preventDefault();
                                              if (exportDragIndex == null) return;
                                              if (exportDragIndex === idx) return;
                                              setExportMediaOrder((prev) => wvMoveItem(prev, exportDragIndex, idx));
                                              setExportOrderTouched(true);
                                              setExportDragIndex(null);
                                              setExportDragOverIndex(null);
                                            }}
                                            title="Glisser-déposer pour réordonner"
                                          >
                                            <div className="wv-orderThumb">
                                              {url ? (
                                                isVid ? (
                                                  <video src={url} muted playsInline preload="metadata" />
                                                ) : (
                                                  <img src={url} alt="" />
                                                )
                                              ) : (
                                                <div className="wv-orderThumbFallback">
                                                  <i className="bi bi-image" />
                                                </div>
                                              )}
                                            </div>

                                            <div className="wv-orderMid">
                                              <div className="wv-orderIdx">#{idx + 1}</div>
                                              <div className="wv-orderId text-muted small">ID: {String(mid)}</div>
                                            </div>

                                            <div className="wv-orderActions">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => {
                                                  setExportMediaOrder((prev) => wvMoveItem(prev, idx, idx - 1));
                                                  setExportOrderTouched(true);
                                                }}
                                                disabled={idx === 0}
                                                title="Monter"
                                              >
                                                <i className="bi bi-chevron-up" />
                                              </button>

                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => {
                                                  setExportMediaOrder((prev) => wvMoveItem(prev, idx, idx + 1));
                                                  setExportOrderTouched(true);
                                                }}
                                                disabled={idx === (exportMediaOrder.length - 1)}
                                                title="Descendre"
                                              >
                                                <i className="bi bi-chevron-down" />
                                              </button>

                                              <span className="wv-orderGrip" aria-hidden="true" title="Drag">
                                                <i className="bi bi-grip-vertical" />
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })}

                                      {(!exportMediaOrder || exportMediaOrder.length === 0) && (
                                        <div className="text-muted small py-2">Aucun média sélectionné pour le moment.</div>
                                      )}
                                    </div>
                                      </div>
                                    </details>
                                  </div>
                                </div>


                                {/* Resolution settings (collapsed by default) */}
                                <div className="col-12">
                                  <details className="wv-collapsibleSection">
                                    <summary className="wv-collapsibleSummary">
                                      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                        <div className="d-flex align-items-center gap-2">
                                          <i className="bi bi-arrows-fullscreen text-primary" />
                                          <span className="fw-semibold">Réglages résolution & qualité</span>
                                        </div>
                                        <i className="bi bi-chevron-down wv-collapsibleChevron" aria-hidden="true" />
                                      </div>
                                      <div className="small text-muted mt-1">
                                        Résolution, FPS, format, bitrate et watermark (optionnels).
                                      </div>
                                    </summary>

                                    <div className="row g-3 mt-1">
{/* Resolution */}
                                <div className="col-6 col-md-3">
                                  <label className="form-label small text-muted d-flex align-items-center gap-1">
                                    <i className="bi bi-arrows-fullscreen" />
                                    Résolution
                                  </label>
                                  <input
                                    className="form-control"
                                    value={exportRes}
                                    onChange={(e) => setExportRes(e.target.value)}
                                  />
                                </div>

                                {/* FPS */}
                                <div className="col-6 col-md-3">
                                  <label className="form-label small text-muted d-flex align-items-center gap-1">
                                    <i className="bi bi-speedometer2" />
                                    FPS
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={exportFPS}
                                    onChange={(e) =>
                                      setExportFPS(parseInt(e.target.value || '30', 10))
                                    }
                                  />
                                </div>

                                {/* Format */}
                                <div className="col-6 col-md-4">
                                  <label className="form-label small text-muted d-flex align-items-center gap-1">
                                    <i className="bi bi-filetype-mp4" />
                                    Format
                                  </label>
                                  <select
                                    className="form-select"
                                    value={exportFormat}
                                    onChange={(e) => setExportFormat(e.target.value)}
                                  >
                                    <option value="mp4">MP4</option>
                                    <option value="mov">MOV</option>
                                  </select>
                                </div>

                                {/* Bitrate */}
                                <div className="col-6 col-md-4">
                                  <label className="form-label small text-muted d-flex align-items-center gap-1">
                                    <i className="bi bi-graph-up-arrow" />
                                    Bitrate (vidéo)
                                  </label>
                                  <input
                                    className="form-control"
                                    value={exportBitrate}
                                    onChange={(e) => setExportBitrate(e.target.value)}
                                    placeholder="ex: 6M"
                                  />
                                </div>

                                {/* Watermark */}
                                <div className="col-12 col-md-4">
                                  <label className="form-label small text-muted d-flex align-items-center gap-1">
                                    <i className="bi bi-water" />
                                    Watermark (texte)
                                  </label>
                                  <input
                                    className="form-control"
                                    value={exportWatermarkText || ''}
                                    onChange={(e) => setExportWatermarkText(e.target.value)}
                                    placeholder="Facultatif"
                                  />
                                </div>
                                    </div>
                                  </details>
                                </div>

                              </div>

                              <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
                                <button
                                  className="btn btn-primary d-flex align-items-center gap-2"
                                  onClick={generateFinalVideo}
                                  disabled={busy || (selectedMediaIds?.length ?? 0) === 0}
                                  title={
                                    (selectedMediaIds?.length ?? 0) === 0
                                      ? 'Sélectionnez au moins un média'
                                      : 'Générer la vidéo'
                                  }
                                >
                                  {busy ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span>Génération…</span>
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-cpu me-1" />
                                      Générer la vidéo
                                    </>
                                  )}
                                </button>

                                <span className="badge text-bg-light d-flex align-items-center gap-1">
                                  <i className="bi bi-collection-play" />
                                  {(selectedMediaIds?.length || 0)} média(s) sélectionné(s)
                                </span>

                                <span className="small text-muted d-flex align-items-center gap-1">
                                  <i className="bi bi-lightning-charge" />
                                  Les paramètres seront pris en compte directement dans le rendu final.
                                
</span>
                              </div>

                              {/* Runway AI (beta) */}
                              <div className="mt-3 p-3 rounded-4 border bg-white">
                                <div className="d-flex align-items-start justify-content-between gap-3">
                                  <div>
                                    <div className="fw-semibold d-flex align-items-center gap-2">
                                      <i className="bi bi-stars text-primary" />
                                      Runway AI <span className="badge text-bg-light">beta</span>
                                    </div>
                                    <div className="small text-muted">
                                      Génération IA depuis un média “seed” + un prompt (style CapCut/FX). La clé Runway reste côté serveur.
                                    </div>
                                  </div>

                                                                    {!runwayUnlocked ? (
                                    <div className="d-flex flex-column align-items-end gap-1">
                                      <div className="d-flex align-items-center gap-2">
                                        <input
                                          type="password"
                                          className="form-control form-control-sm"
                                          style={{ width: 180 }}
                                          value={runwayPassInput}
                                          onChange={(e) => { setRunwayPassInput(e.target.value); setRunwayPassError(''); }}
                                          placeholder="Mot de passe"
                                        />
                                        <button className="btn btn-sm btn-outline-dark" type="button" onClick={unlockRunway}>
                                          <i className="bi bi-lock-fill me-1" />
                                          Déverrouiller
                                        </button>
                                      </div>
                                      {runwayPassError && <div className="small text-danger">{runwayPassError}</div>}
                                    </div>
                                  ) : (
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="form-check form-switch mb-0">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id="runwayEnabledSwitch"
                                          checked={runwayEnabled}
                                          onChange={(e) => setRunwayEnabled(e.target.checked)}
                                        />
                                        <label className="form-check-label small" htmlFor="runwayEnabledSwitch">
                                          Activer
                                        </label>
                                      </div>
                                      <button
                                        className="btn btn-sm btn-outline-secondary"
                                        type="button"
                                        onClick={lockRunway}
                                        title="Verrouiller Runway"
                                      >
                                        <i className="bi bi-lock me-1" />
                                        Verrouiller
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {runwayUnlocked && runwayEnabled && (
                                  <div className="mt-3">
                                    <div className="row g-2">
                                      <div className="col-md-6">
                                        <label className="form-label small mb-1">Média “seed”</label>
                                        <select
                                          className="form-select"
                                          value={runwaySeedId}
                                          onChange={(e) => setRunwaySeedId(e.target.value)}
                                          disabled={(selectedMediaIds?.length || 0) === 0}
                                        >
                                          {(selectedMediaIds || []).map((mid) => {
                                            const m = mediaById[mid];
                                            const label = (m?.title || m?.name || `Media #${mid}`).toString();
                                            const url = mediaRunwaySeedUrlById(mid);
                                            const badge = isVideoUrl(url) ? '🎬' : '🖼️';
                                            return (
                                              <option key={mid} value={String(mid)}>
                                                {badge} {label}
                                              </option>
                                            );
                                          })}
                                        </select>
                                        {(selectedMediaIds?.length || 0) === 0 && (
                                          <div className="small text-muted mt-1">Sélectionne au moins 1 média pour activer Runway.</div>
                                        )}
                                      </div>

                                      <div className="col-md-3">
                                        <label className="form-label small mb-1">Mode</label>
                                        <select className="form-select" value={runwayMode} onChange={(e) => setRunwayMode(e.target.value)}>
                                          <option value="auto">Auto</option>
                                          <option value="image_to_video">Image → Video</option>
                                          <option value="video_to_video">Video → Video</option>
                                          <option value="text_to_video">Text → Video</option>
                                        </select>
                                      </div>

                                      <div className="col-md-3">
                                        <label className="form-label small mb-1">Durée</label>
                                        <input
                                          type="number"
                                          className="form-control"
                                          min={2}
                                          max={10}
                                          value={runwayDuration}
                                          onChange={(e) => setRunwayDuration(Number(e.target.value || 8))}
                                          disabled={runwayMode === 'video_to_video'}
                                        />
                                        <div className="small text-muted mt-1">2–10s (ignoré en video→video)</div>
                                      </div>
                                    </div>

                                    <div className="row g-2 mt-1">
                                      <div className="col-md-4">
                                        <label className="form-label small mb-1">Ratio (Runway)</label>
                                        <select className="form-select" value={runwayRatio} onChange={(e) => setRunwayRatio(e.target.value)}>
                                          <option value="720:1280">9:16 (720×1280)</option>
                                          <option value="1280:720">16:9 (1280×720)</option>
                                          <option value="960:960">1:1 (960×960)</option>
                                          <option value="832:1104">3:4 (832×1104)</option>
                                          <option value="1104:832">4:3 (1104×832)</option>
                                          <option value="1584:672">Cinema (1584×672)</option>
                                          <option value="848:480">Wide (848×480)</option>
                                          <option value="640:480">Classic (640×480)</option>
                                        </select>
                                      </div>

                                      <div className="col-md-8">
                                        <label className="form-label small mb-1">Prompt</label>
                                        <textarea
                                          className="form-control"
                                          rows={3}
                                          value={runwayPromptText}
                                          onChange={(e) => setRunwayPromptText(e.target.value)}
                                          placeholder="Décris le style, le rythme, les transitions…"
                                        />
                                      </div>
                                    </div>

                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                      <button
                                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                                        onClick={generateWithRunway}
                                        disabled={runwayBusy || busy || (selectedMediaIds?.length || 0) === 0}
                                      >
                                        {runwayBusy ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            <span>Runway…</span>
                                          </>
                                        ) : (
                                          <>
                                            <i className="bi bi-stars" />
                                            Générer avec Runway
                                          </>
                                        )}
                                      </button>

                                      <button
                                        className="btn btn-outline-secondary"
                                        onClick={refreshRunwayOrg}
                                        disabled={runwayBusy}
                                        title="Vérifier crédits / configuration Runway"
                                      >
                                        <i className="bi bi-speedometer2 me-1" />
                                        Crédits
                                      </button>

                                      {runwayTaskId && (
                                        <span className="badge text-bg-light">Task: {String(runwayTaskId).slice(0, 8)}…</span>
                                      )}
                                      {runwayStatus && (
                                        <span className="badge text-bg-light">{runwayStatus}</span>
                                      )}
                                    </div>

                                    {runwayOrgInfo && (
                                      <div className="small text-muted mt-2">{runwayOrgInfo}</div>
                                    )}

                                    {runwayOutputUrl && (
                                      <div className="mt-3">
                                        <div className="small text-muted mb-2">Résultat Runway</div>
                                        <video
                                          src={runwayOutputUrl}
                                          controls
                                          playsInline
                                          style={{ width: '100%', borderRadius: 16, background: '#000' }}
                                        />
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                          <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                              setFinalVideoUrl(runwayOutputUrl);
                                              addToast('Vidéo Runway mise comme vidéo finale (aperçu).', 'success');
                                            }}
                                          >
                                            Utiliser comme vidéo finale
                                          </button>
                                          <a className="btn btn-outline-secondary" href={runwayOutputUrl} target="_blank" rel="noreferrer">
                                            Ouvrir
                                          </a>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                            </div>
                          </>
                        )}

                        {/* Video player */}
                        {finalVideoUrl && (montageMode === 'Manuel' || montageMode === 'Advanced' || aiHasGenerated) && (
                          <div className="mt-4">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                              <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                                <i className="bi bi-film text-primary" />
                                Aperçu de la vidéo
                              </h6>
                              <span className="badge bg-light text-secondary-emphasis d-flex align-items-center gap-1">
                                <i className="bi bi-hdmi" />
                                {exportRes} • {exportFormat && exportFormat.toUpperCase()}
                              </span>
                            </div>

                            <div className="d-flex justify-content-center">
                              <div
                                className="rounded-4 shadow-sm border overflow-hidden"
                                style={{
                                  width: '100%',
                                  maxWidth: '960px',
                                  aspectRatio: '16 / 9',
                                  background: '#000',
                                }}
                              >
                                <video
                                  key={finalVideoUrl}
                                  src={finalVideoUrl}
                                  controls
                                  autoPlay
                                  muted
                                  playsInline
                                  preload="metadata"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                />
                              </div>
                            </div>

                            <div className="mt-2 text-muted small text-break d-flex align-items-center gap-2">
                              <i className="bi bi-link-45deg" />
                              <span>{finalVideoUrl}</span>
                            </div>

                            {/* Actions */}
                            <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
                              <button
                                className="btn btn-primary d-flex align-items-center gap-2"
                                disabled={!finalVideoUrl || busy}
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(
                                      window.location.origin + finalVideoUrl
                                    );
                                    (window.addToast || addToast)?.(
                                      'Lien copié. Collez-le dans l’onglet Messages ✅',
                                      'success',
                                    );
                                  } catch {
                                    (window.addToast || addToast)?.(
                                      'Impossible de copier le lien.',
                                      'error',
                                    );
                                  }
                                }}
                              >
                                <i className="bi bi-chat-dots" />
                                Partager dans Messages
                              </button>

                              <div className="btn-group">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center gap-1"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  disabled={!finalVideoUrl || busy}
                                >
                                  <i className="bi bi-share" />
                                  Partager • autres canaux
                                </button>
                                <ul className="dropdown-menu">
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={async () => {
                                        try {
                                          await navigator.clipboard.writeText(
                                            window.location.origin + finalVideoUrl
                                          );
                                          (window.addToast || addToast)?.(
                                            'Lien copié. Collez-le dans YouTube/Instagram/TikTok.',
                                            'success',
                                          );
                                        } catch {
                                          (window.addToast || addToast)?.(
                                            'Copie du lien impossible.',
                                            'error',
                                          );
                                        }
                                      }}
                                    >
                                      <i className="bi bi-clipboard me-2" /> Copier le lien
                                    </button>
                                  </li>
                                  <li>
                                    <hr className="dropdown-divider" />
                                  </li>
                                  <li className="px-3 text-muted small">Ouvrir rapidement :</li>
                                  <li>
                                    <a
                                      className="dropdown-item"
                                      href="https://studio.youtube.com/"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <i className="bi bi-youtube me-2" /> YouTube Studio
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      className="dropdown-item"
                                      href="https://business.facebook.com/latest/home"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <i className="bi bi-instagram me-2" /> Instagram/Facebook
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      className="dropdown-item"
                                      href="https://www.tiktok.com/upload"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <i className="bi bi-tiktok me-2" /> TikTok
                                    </a>
                                  </li>
                                </ul>
                              </div>

                              <button
                                className="btn btn-outline-danger ms-auto d-flex align-items-center gap-1"
                                disabled={busy}
                                onClick={async () => {
                                  try {
                                    const r = await fetch(`/projects/${id}/close`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ status: 'Clôturé' }),
                                    });
                                    if (!r.ok) throw new Error();
                                    (window.addToast || addToast)?.(
                                      'Projet clôturé ✅',
                                      'success',
                                      5000
                                    );
                                  } catch {
                                    (window.addToast || addToast)?.(
                                      'Échec de la clôture.',
                                      'error',
                                      5000
                                    );
                                  }
                                }}
                              >
                                <i className="bi bi-lock-fill" />
                                Clôturer le projet
                              </button>

                            </div>
                          </div>
                        )}

                        {/* Manual mode helper without video */}
                        {!STUDIO_ASSISTED_ONLY && montageMode === 'Manuel' && !finalVideoUrl && (
                          <div
                            className="alert alert-warning mt-4 d-flex align-items-start gap-3 rounded-4"
                            role="alert"
                          >
                            <div
                              className="rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{ width: 32, height: 32, backgroundColor: 'rgba(255,193,7,0.18)' }}
                            >
                              <i className="bi bi-cloud-upload fs-5 text-warning" />
                            </div>
                            <div className="small">
                              Importez votre vidéo finale pour afficher l’aperçu, activer le partage et
                              clôturer le projet.
                            </div>
                          </div>
                        )}

                        {/* Busy (sync IA) */}
                        {/* Busy (sync IA) – PANEL CLAIR AVEC FAUX STEPS */}
                        {!exportAsync && busy && (
                          <div className="mt-4 p-3 p-md-4 rounded-4 border bg-light-subtle">
                            {/* Header */}
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-1">
                                  <i className="bi bi-magic" />
                                  Montage en cours
                                </span>
                                <span className="small text-muted d-flex align-items-center gap-1">
                                  <i className="bi bi-cpu" />
                                  l'agent IA assemble vos clips, vos voix et vos transitions…
                                </span>
                              </div>

                              <span className="badge bg-white border text-muted d-flex align-items-center gap-1">
                                <i className="bi bi-clock-history" />
                                Temps créatif : parfait pour préparer votre description
                              </span>
                            </div>

                            {/* Progress bar */}
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1 small">
                                <span className="text-muted d-flex align-items-center gap-1">
                                  <i className="bi bi-speedometer2" />
                                  Avancement du rendu
                                </span>
                                <span className="fw-semibold">
                                  {exportFakeProgress}%
                                </span>
                              </div>
                              <div className="progress" style={{ height: 10 }}>
                                <div
                                  className="progress-bar progress-bar-striped progress-bar-animated"
                                  role="progressbar"
                                  style={{ width: `${exportFakeProgress}%` }}
                                  aria-valuenow={exportFakeProgress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                />
                              </div>
                            </div>

                            {/* Steps visuels */}
                            <div className="row g-3 small">
                              <div className="col-12 col-md-8">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <i className="bi bi-list-stars" />
                                  <span className="fw-semibold">Étapes du montage</span>
                                </div>

                                <div className="d-flex flex-column gap-2">
                                  {FAKE_EXPORT_STEPS.map((step, idx) => {
                                    const isDone = idx < exportFakeStepIndex;
                                    const isCurrent = idx === exportFakeStepIndex;

                                    return (
                                      <div
                                        key={step.key}
                                        className="d-flex align-items-start gap-2"
                                      >
                                        <div
                                          className={
                                            'rounded-circle d-flex align-items-center justify-content-center border ' +
                                            (isCurrent
                                              ? 'bg-primary-subtle border-primary'
                                              : isDone
                                                ? 'bg-success-subtle border-success'
                                                : 'bg-white border-secondary-subtle')
                                          }
                                          style={{
                                            width: 26,
                                            height: 26,
                                          }}
                                        >
                                          {isDone ? (
                                            <i className="bi bi-check2" style={{ fontSize: 14 }} />
                                          ) : (
                                            <i className={`bi ${step.icon}`} style={{ fontSize: 13 }} />
                                          )}
                                        </div>
                                        <div>
                                          <div className="fw-semibold d-flex align-items-center gap-2">
                                            {step.label}
                                            {isCurrent && (
                                              <span className="badge bg-primary-subtle text-primary-emphasis">
                                                En cours…
                                              </span>
                                            )}
                                            {isDone && !isCurrent && (
                                              <span className="badge bg-success-subtle text-success">
                                                Terminé
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-muted">
                                            {step.desc}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Colonne droite : petit rappel “fun” */}
                              <div className="col-12 col-md-4">
                                <div className="border rounded-4 bg-white p-3 h-100 d-flex flex-column justify-content-between">
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <i className="bi bi-lightbulb" />
                                      <span className="fw-semibold">Mini check-list</span>
                                    </div>
                                    <ul className="list-unstyled text-muted mb-0">
                                      <li className="d-flex align-items-center gap-1">
                                        <i className="bi bi-check-circle text-success" />
                                        Préparer le titre & la description.
                                      </li>
                                      <li className="d-flex align-items-center gap-1">
                                        <i className="bi bi-check-circle text-success" />
                                        Penser aux tags (YouTube / Insta).
                                      </li>
                                      <li className="d-flex align-items-center gap-1">
                                        <i className="bi bi-check-circle text-success" />
                                        Rédiger un message pour l’équipe.
                                      </li>
                                    </ul>
                                  </div>
                                  <div className="mt-3 d-flex align-items-center gap-2 text-muted">
                                    <LoaderDots label="Le montage avance…" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  </div>

                  {/* ===== RIGHT COL : RECAP + MEDIA ===== */}
                  <div className="col-12 col-xl-4">
                    <div className="card border-0 shadow-sm h-100 rounded-4">
                      <div className="card-body">
                        {/* Recap header */}
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                          <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                            <i className="bi bi-info-circle" />
                            Récapitulatif du montage
                          </h6>

                          <span className="badge rounded-pill bg-light text-secondary-emphasis d-flex align-items-center gap-2">
                            <i className="bi bi-collection-play" />
                            {(selectedMediaIds || []).length} média(s) • {exportRes}{' '}
                            {exportFormat && `• ${exportFormat.toUpperCase()}`}
                          </span>
                        </div>

                        <div className="row g-3">
                          {/* Col synthèse + audio */}
                          <div className="col-12 col-lg-5">
                            <ul className="list-unstyled small text-muted mb-0">
                              <li className="mb-1 d-flex align-items-start gap-2">
                                <i className="bi bi-arrow-left-right mt-1" />
                                <span>
                                  <strong>Transition :</strong>{' '}
                                  {(
                                    TRANSITION_OPTIONS.find((t) => t.key === transition)?.label ||
                                    'Coupe directe'
                                  )}{' '}
                                  <span className="opacity-75">({transition || 'none'})</span>
                                </span>
                              </li>

                              <li className="mb-1 d-flex align-items-start gap-2">
                                <i className="bi bi-volume-up mt-1" />
                                <span>
                                  <strong>Mix audio :</strong>{' '}
                                  {audioMixOption === 'voice+music'
                                    ? 'Voix + musique'
                                    : audioMixOption === 'voice_only'
                                      ? 'Voix seule'
                                      : audioMixOption === 'music_only'
                                        ? 'Musique seule'
                                        : audioMixOption}{' '}
                                  <span className="opacity-75">
                                    ({Math.round((musicVolume ?? 0) * 100)} % de musique)
                                  </span>
                                </span>
                              </li>

                              <li className="mb-1 d-flex align-items-start gap-2">
                                <i className="bi bi-camera-reels mt-1" />
                                <span>
                                  <strong>Export vidéo :</strong> {exportPreset} — {exportRes} @ {exportFPS}{' '}
                                  fps, {exportFormat && exportFormat.toUpperCase()} (bitrate {exportBitrate})
                                </span>
                              </li>

                              {exportWatermarkText && (
                                <li className="mb-1 d-flex align-items-start gap-2">
                                  <i className="bi bi-brush mt-1" />
                                  <span>
                                    <strong>Watermark :</strong> « {exportWatermarkText} »
                                  </span>
                                </li>
                              )}
                            </ul>

                            {/* Audio preview block */}
                            <div className="mt-3 p-2 rounded-3 border bg-light-subtle small">
                              <div className="d-flex align-items-center mb-2 gap-2">
                                <i className="bi bi-soundwave" />
                                <span className="fw-semibold">Aperçu audio</span>
                              </div>

                              <div className="d-flex flex-column gap-3">
                                {/* Voice off */}
                                <div>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="text-muted d-flex align-items-center gap-1">
                                      <i className="bi bi-mic" />
                                      Voix off
                                    </span>
                                    <span className="badge bg-light text-dark">
                                      {voiceMode === 'per-step' ? 'Par étape' : 'Globale'}
                                    </span>
                                  </div>

                                  {voiceMode === 'per-step' ? (
                                    <div className="d-flex flex-column gap-2">
                                      {Array.isArray(scenarioForm?.steps) &&
                                        scenarioForm.steps.map((st, idx) => {
                                          const info = perStepVoiceMap?.[idx];
                                          const label =
                                            st.title || st.location || `Étape ${idx + 1}`;
                                          return (
                                            <div key={idx} className="p-1 rounded-3 bg-white border">
                                              <div className="small fw-semibold mb-1 d-flex align-items-center gap-1">
                                                <i className="bi bi-flag" />
                                                {label}
                                              </div>
                                              {info?.url ? (
                                                <audio
                                                  controls
                                                  src={String(info.url)}
                                                  style={{ width: '100%' }}
                                                />
                                              ) : (
                                                <div className="text-muted small fst-italic">
                                                  Aucune voix pour cette étape.
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                    </div>
                                  ) : (
                                    <>
                                      {voiceUrl ? (
                                        <audio
                                          controls
                                          src={String(voiceUrl)}
                                          style={{ width: '100%' }}
                                        />
                                      ) : (
                                        <div className="text-muted fst-italic small">
                                          Aucune pré-écoute de voix configurée pour l’instant.
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>

                                {/* Music */}
                                <div>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="text-muted d-flex align-items-center gap-1">
                                      <i className="bi bi-music-note-beamed" />
                                      Musique
                                    </span>
                                  </div>

                                  {musicUrl ? (
                                    <audio
                                      controls
                                      src={String(musicUrl)}
                                      style={{ width: '100%' }}
                                    />
                                  ) : (
                                    <div className="text-muted fst-italic small">
                                      Aucune pré-écoute de musique configurée pour l’instant.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Col preview clips + media list */}
                          <div className="col-12 col-lg-7">
                            <h6 className="mt-1 mb-2 d-flex align-items-center gap-2">
                              <i className="bi bi-film" /> Clips visuels
                            </h6>

                            <div className="d-flex flex-wrap gap-3">
                              {((projectMedia || []).filter((m) =>
                                (selectedMediaIds || []).includes(m.id),
                              )).map((m) => {
                                const title =
                                  m.title || m.file_name || m.file_path || 'media';
                                const rawPath =
                                  m.thumbnail_url ||
                                  m.thumb ||
                                  m.preview ||
                                  m.preview_url ||
                                  m.file_path ||
                                  m.path ||
                                  m.url ||
                                  '';
                                const src = withUploadsPath(rawPath);
                                const isVideo = /\.(mp4|mov|webm|mkv|ogg)$/i.test(
                                  String(m.file_path || m.path || m.url || ''),
                                );

                                return (
                                  <div
                                    key={m.id}
                                    className="border rounded-3 shadow-sm"
                                    title={title}
                                    style={{
                                      width: 144,
                                      height: 81,
                                      overflow: 'hidden',
                                      background: '#000',
                                    }}
                                  >
                                    {isVideo ? (
                                      <video
                                        src={src}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          display: 'block',
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.poster =
                                            '/static/img/placeholder_media.png';
                                        }}
                                      />
                                    ) : (
                                      <img
                                        src={src}
                                        alt={title}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          display: 'block',
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.src =
                                            '/static/img/placeholder_media.png';
                                        }}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                              {selectedMediaIds.length === 0 && (
                                <div className="text-muted small d-flex align-items-center gap-1">
                                  <i className="bi bi-exclamation-circle" />
                                  Aucun média sélectionné.
                                </div>
                              )}
                            </div>

                            <h6 className="mt-3 mb-2 d-flex align-items-center gap-2">
                              <i className="bi bi-list-ul" /> Liste des médias utilisés
                            </h6>

                            <ol className="small mb-0">
                              {((projectMedia || []).filter((m) =>
                                (selectedMediaIds || []).includes(m.id),
                              )).map((m, idx) => (
                                <li
                                  key={m.id}
                                  className="text-truncate d-flex align-items-center gap-1"
                                >
                                  <i className="bi bi-play-fill small" />
                                  <span>
                                    {m.title || m.file_name || `Media #${idx + 1}`}
                                  </span>
                                </li>
                              ))}
                              {selectedMediaIds.length === 0 && (
                                <li className="text-muted d-flex align-items-center gap-1">
                                  <i className="bi bi-dash-circle" /> —
                                </li>
                              )}
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}





        </main>
      </div>
    </div>
  );
}
function MontageByStep({
  scenarioForm,
  groupedByStep,
  collapsed,
  setCollapsed,
  selectedMediaIds,
  setSelectedMediaIds
}) {
  const toggleSelect = (id) =>
    setSelectedMediaIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const setMany = (ids, shouldSelect) => {
    const clean = (ids || []).filter((x) => x != null);
    setSelectedMediaIds((prev) => {
      const s = new Set(prev || []);
      clean.forEach((id) => {
        if (shouldSelect) s.add(id);
        else s.delete(id);
      });
      return Array.from(s);
    });
  };

  const steps = Array.isArray(scenarioForm?.steps) ? scenarioForm.steps : [];

  // Keys des étapes connues (string), tolérant si jamais id est absent
  const stepKeys = new Set(steps.map((st, idx) => String(st?.id ?? idx + 1)));

  // Regroupe "no-step" + tout step_id qui ne correspond à aucune étape (mismatch)
  const orphanItems = (() => {
    const byId = new Map();
    try {
      for (const [k, arr] of (groupedByStep || new Map()).entries()) {
        if (k === 'no-step' || !stepKeys.has(String(k))) {
          (arr || []).forEach((m) => {
            if (m?.id == null) return;
            if (!byId.has(m.id)) byId.set(m.id, m);
          });
        }
      }
    } catch {}
    const list = Array.from(byId.values());
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  })();

  const groups = [
    ...steps.map((st, idx) => {
      const key = String(st?.id ?? idx + 1);
      return {
        key,
        title: `Étape #${st?.id ?? idx + 1} — ${st?.location || 'Lieu'}`,
        items: groupedByStep.get(key) || []
      };
    }),
    {
      key: 'no-step',
      title: 'Autres contributions (non associées à une étape)',
      items: orphanItems
    }
  ];

  return (
    <div className="vstack gap-3">
      {groups.map((g) => {
        const key = g.key;
        const items = Array.isArray(g.items) ? g.items : [];
        const total = items.length;
        const selectedCount = items.reduce(
          (acc, m) => acc + (selectedMediaIds.includes(m.id) ? 1 : 0),
          0
        );
        const isCollapsed = !!collapsed?.[key];

        return (
          <div key={key} className="border rounded-3 p-2">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => setCollapsed((s) => ({ ...s, [key]: !s?.[key] }))}
                  aria-expanded={!isCollapsed}
                  title={isCollapsed ? 'Développer' : 'Réduire'}
                >
                  {isCollapsed ? (
                    <i className="bi bi-chevron-right" />
                  ) : (
                    <i className="bi bi-chevron-down" />
                  )}
                </button>

                <h6 className="m-0">
                  <i className="bi bi-ladder me-1" /> {g.title}
                </h6>

                <span className="badge bg-light text-dark">
                  {selectedCount}/{total} sélectionné(s)
                </span>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  disabled={!total}
                  onClick={() => setMany(items.map((m) => m.id), true)}
                  title="Sélectionner tous les médias de ce groupe"
                >
                  <i className="bi bi-check2-square me-1" />
                  Tout sélectionner
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={!total}
                  onClick={() => setMany(items.map((m) => m.id), false)}
                  title="Retirer tous les médias de ce groupe"
                >
                  <i className="bi bi-x-square me-1" />
                  Tout retirer
                </button>
              </div>
            </div>

            {!isCollapsed && (
              <>
                {total === 0 ? (
                  <div className="text-muted small mt-2 d-flex align-items-center gap-2">
                    <i className="bi bi-info-circle" />
                    Aucun média dans ce groupe.
                  </div>
                ) : (
                  <div className="row g-2 mt-2">
                    {items.map((m) => {
                      const isVideo = /\.(mp4|webm|ogg)$/i.test(m.file_path || '');
                      const url = withUploadsPath(m.file_path || '');
                      const checked = selectedMediaIds.includes(m.id);

                      return (
                        <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={m.id}>
                          <div className={`border rounded-3 p-2 ${checked ? 'border-primary' : ''}`}>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSelect(m.id)}
                                id={`sel_${key}_${m.id}`}
                              />
                              <label
                                className="form-check-label small text-truncate"
                                htmlFor={`sel_${key}_${m.id}`}
                                title={m.title || m.file_name || ''}
                              >
                                {m.title || m.file_name || 'Media'}
                              </label>
                            </div>

                            <div
                              style={{
                                width: '100%',
                                height: 120,
                                overflow: 'hidden',
                                borderRadius: 10,
                                background: '#000'
                              }}
                            >
                              {isVideo ? (
                                <video
                                  src={url}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <img
                                  src={url}
                                  alt={m.title || 'media'}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}




// NEW: AudioVoice.jsx / AudioVoice section in JourneyAdmin.js
function AudioVoice({
  voiceOverFile,
  setVoiceOverFile,
  voiceOverFileName,
  setVoiceOverFileName,
  musicFile,
  setMusicFile,
  musicFileName,
  setMusicFileName,

  // 🔊 Pré-écoutes VOIX & MUSIQUE (venant du parent JourneyAdmin)
  voiceUrl,
  setVoiceUrl,
  musicUrl,
  setMusicUrl,
  musicError,
  setMusicError,

  kc_token,
  audioMixOption,
  setAudioMixOption,
  musicVolume,
  setMusicVolume,
  voiceGender,
  setVoiceGender,
  voiceLang,
  setVoiceLang,
  scenario,
  projectId,
  transition,
  setTransition,
  selectedMediaIds,
  projectMedia,
  voiceMode,           // 'single' | 'per-step'
  setVoiceMode,
  perStepVoiceMap,     // 👈 map restaurée depuis le draft (JSON de voice_by_step)
  onPerStepVoicesChange,

  // 🔔 toast du parent (optionnel)
  addToast,
}) {
  const [mode, setMode] = useState('ia');
  const [musicPreviewUrl, setMusicPreviewUrl] = useState('');
  const [musicLoading, setMusicLoading] = useState(false);
  const [loadingTTS, setLoadingTTS] = useState(false);

  // Voix par étape (state local, indexées par idx)
  const [perStepVoices, setPerStepVoices] = useState({});
  const [isTransitionOpen, setIsTransitionOpen] = useState(true);

  const [isVoiceOpen, setIsVoiceOpen] = useState(true);
  const [isMusicOpen, setIsMusicOpen] = useState(true);

  // Enregistrement micro
  const [isRecording, setIsRecording] = useState(false);
  const [recordError, setRecordError] = useState('');
  const [recordedPreviewUrl, setRecordedPreviewUrl] = useState('');
  const recorderRef = useRef(null);
  const recordStreamRef = useRef(null);
  // null = enregistrement global, sinon index de l'étape en cours
  const [recordingStepIndex, setRecordingStepIndex] = useState(null);

  // Voix par étape (busy)
  const [perStepBusy, setPerStepBusy] = useState({});

  // Suggestions & génération IA musique
  const [musicAiSuggestions, setMusicAiSuggestions] = useState([]);
  const [musicAiPrompt, setMusicAiPrompt] = useState('');
  const [musicAiLoading, setMusicAiLoading] = useState(false);

  // ---------- Hydratation voix par étape depuis le draft ----------
  useEffect(() => {
    if (voiceMode !== 'per-step') {
      setPerStepVoices({});
      return;
    }

    if (!perStepVoiceMap || typeof perStepVoiceMap !== 'object') {
      setPerStepVoices({});
      return;
    }

    const next = {};
    Object.entries(perStepVoiceMap).forEach(([key, val]) => {
      if (!val) return;
      const idx = Number(key);
      if (!Number.isFinite(idx)) return;

      let url = null;
      let fileName = null;
      let type = 'restored';

      if (typeof val === 'string') {
        if (/^https?:\/\//.test(val) || val.startsWith('/uploads/')) {
          url = val;
        } else {
          url = withUploadsPath(val);
        }
      } else if (val && typeof val === 'object') {
        fileName = val.fileName || null;
        type = val.type || 'restored';
        if (val.url) {
          url = val.url;
        } else if (val.serverPath) {
          url = val.serverPath;
        } else if (val.fileName) {
          url = withUploadsPath(val.fileName);
        }
        if (url && !/^https?:\/\//.test(url) && !url.startsWith('/uploads/')) {
          url = withUploadsPath(url);
        }
      }

      if (!url) return;

      next[idx] = { type, fileName, url };
    });

    setPerStepVoices(next);
  }, [perStepVoiceMap, voiceMode]);

  // ---------- Préview voix globale ----------
  useEffect(() => {
    // 1) fichier uploadé localement
    if (voiceOverFile instanceof File) {
      const url = URL.createObjectURL(voiceOverFile);
      setVoiceUrl(url);
      return () => {
        try {
          URL.revokeObjectURL(url);
        } catch { }
      };
    }

    // 2) fichier déjà hébergé (généré IA ou existant)
    if (voiceOverFileName) {
      setVoiceUrl(withUploadsPath(voiceOverFileName));
      return;
    }

    // 3) enregistrement micro temporaire
    if (recordedPreviewUrl) {
      setVoiceUrl(recordedPreviewUrl);
      return;
    }

    // 4) rien
    setVoiceUrl('');
  }, [voiceOverFile, voiceOverFileName, recordedPreviewUrl, setVoiceUrl]);

  // ---------- Préview musique ----------
  useEffect(() => {
    if (musicFile instanceof File) {
      const localUrl = URL.createObjectURL(musicFile);
      setMusicUrl(localUrl);
      return;
    }
    if (musicFileName) {
      setMusicUrl(withUploadsPath(musicFileName));
      return;
    }
    setMusicUrl('');
  }, [musicFile, musicFileName, setMusicUrl]);

  // Track and safely revoke previous blob URLs only when they are replaced or on final unmount
  const prevVoiceBlobRef = useRef(null);
  const prevMusicBlobRef = useRef(null);

  useEffect(() => {
    if (voiceUrl && voiceUrl.startsWith('blob:')) {
      if (prevVoiceBlobRef.current && prevVoiceBlobRef.current !== voiceUrl) {
        try {
          URL.revokeObjectURL(prevVoiceBlobRef.current);
        } catch { }
      }
      prevVoiceBlobRef.current = voiceUrl;
    }
  }, [voiceUrl]);

  useEffect(() => {
    if (musicUrl && musicUrl.startsWith('blob:')) {
      if (prevMusicBlobRef.current && prevMusicBlobRef.current !== musicUrl) {
        try {
          URL.revokeObjectURL(prevMusicBlobRef.current);
        } catch { }
      }
      prevMusicBlobRef.current = musicUrl;
    }
  }, [musicUrl]);

  // ---------- Upload handlers ----------
  const handleVoiceUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsRecording(false);
    setRecordError('');
    if (recordedPreviewUrl) {
      try {
        URL.revokeObjectURL(recordedPreviewUrl);
      } catch { }
      setRecordedPreviewUrl('');
    }

    setVoiceOverFile(file);
    setVoiceOverFileName(null);
  };

  const handleMusicUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMusicFile(file);
    setMusicFileName(null);
  };

  // ---------- IA – voix globale à partir du scénario ----------
  // ---------- IA – voix globale à partir du scénario ----------
  const generateVoiceFromScenario = async () => {
    const baseText =
      scenario?.description ||
      scenario?.script ||
      scenario?.plan ||
      '';

    if (!baseText || baseText.trim().length < 5) {
      (window.addToast || addToast)?.(
        'Aucun texte de scénario suffisant pour générer la voix.',
        'error',
        5000
      );
      return;
    }

    setLoadingTTS(true);
    try {
      const res = await fetch(`/projects/${projectId}/generate-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: baseText,
          gender: voiceGender,
          languageCode: voiceLang,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.voiceFile) {
        throw new Error(data.error || 'Erreur de génération de la voix IA.');
      }

      // reset record
      setIsRecording(false);
      setRecordError('');
      if (recordedPreviewUrl) {
        try {
          URL.revokeObjectURL(recordedPreviewUrl);
        } catch { }
        setRecordedPreviewUrl('');
      }

      setVoiceOverFile(null);
      setVoiceOverFileName(data.voiceFile);
      addToast?.('🎤 Voix IA générée avec succès', 'success');
    } catch (e) {
      console.error(e);
      const msg = e.message || 'Erreur lors de la génération de la voix IA.';
      (window.addToast || addToast)?.(msg, 'error', 5000);
    } finally {
      setLoadingTTS(false);
    }
  };


  // ---------- Enregistrement micro – voix globale ----------
  const startRecording = async () => {
    setRecordError('');
    setRecordingStepIndex(null);

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      typeof window === 'undefined' ||
      typeof window.MediaRecorder === 'undefined'
    ) {
      setRecordError("L'enregistrement audio n'est pas supporté sur ce navigateur.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mr = new MediaRecorder(stream);
      recorderRef.current = mr;

      const chunks = [];
      mr.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) chunks.push(evt.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File(
          [blob],
          `voice_recording_${Date.now()}.webm`,
          { type: 'audio/webm' }
        );

        setVoiceOverFile(file);
        setVoiceOverFileName(null);

        if (recordedPreviewUrl) {
          try {
            URL.revokeObjectURL(recordedPreviewUrl);
          } catch { }
        }
        const url = URL.createObjectURL(blob);
        setRecordedPreviewUrl(url);

        if (recordStreamRef.current) {
          recordStreamRef.current.getTracks().forEach((track) => track.stop());
        }
        setRecordingStepIndex(null);
      };

      mr.start();
      setIsRecording(true);
    } catch (e) {
      console.error('record error', e);
      setRecordError("Impossible d'accéder au micro (permission refusée ?)");
      setIsRecording(false);
      setRecordingStepIndex(null);
    }
  };

  // ---------- Enregistrement micro – voix PAR ÉTAPE ----------
  const startStepRecording = async (stepIndex) => {
    setRecordError('');
    setRecordingStepIndex(stepIndex);

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      typeof window === 'undefined' ||
      typeof window.MediaRecorder === 'undefined'
    ) {
      setRecordError("L'enregistrement audio n'est pas supporté sur ce navigateur.");
      setRecordingStepIndex(null);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mr = new MediaRecorder(stream);
      recorderRef.current = mr;

      const chunks = [];
      mr.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) chunks.push(evt.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File(
          [blob],
          `voice_step_${stepIndex}_${Date.now()}.webm`,
          { type: 'audio/webm' }
        );
        const url = URL.createObjectURL(blob);

        setPerStepVoices((prev) => {
          const next = {
            ...prev,
            [stepIndex]: {
              type: 'record',
              fileName: file.name,
              url,
              file,
            },
          };
          onPerStepVoicesChange?.(next);
          return next;
        });

        if (recordStreamRef.current) {
          recordStreamRef.current.getTracks().forEach((track) => track.stop());
        }
        setRecordingStepIndex(null);
      };

      mr.start();
      setIsRecording(true);
    } catch (e) {
      console.error('record error', e);
      setRecordError("Impossible d'accéder au micro (permission refusée ?)");
      setIsRecording(false);
      setRecordingStepIndex(null);
    }
  };

  const stopRecording = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
    } catch { }
    setIsRecording(false);
    setRecordingStepIndex(null);
  };

  // ---------- Étapes du scénario ----------
  const rawSteps = (() => {
    const s = scenario?.steps;
    if (!s) return [];
    if (Array.isArray(s)) return s;
    if (typeof s === 'string') {
      try {
        const arr = JSON.parse(s);
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    }
    return [];
  })();

  const handleGenerateStepVoice = async (index, step) => {
    const text = (step?.description || step?.text || step?.title || '').trim();
    if (!text || text.length < 5) {
      (window.addToast || addToast)?.(
        'Pas assez de texte dans cette étape pour générer une voix.',
        'error',
        5000
      );
      return;
    }

    setPerStepBusy((prev) => ({ ...prev, [index]: true }));
    try {
      const res = await fetch(`/projects/${projectId}/generate-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          gender: voiceGender,
          languageCode: voiceLang,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.voiceFile) {
        throw new Error(data.error || 'Erreur TTS IA pour cette étape.');
      }

      const fileName = data.voiceFile;
      const url = withUploadsPath(fileName);

      setPerStepVoices((prev) => {
        const next = {
          ...prev,
          [index]: {
            type: 'ia',
            fileName,
            url,
          },
        };
        onPerStepVoicesChange?.(next);
        return next;
      });
    } catch (err) {
      console.error(err);
      const msg = err.message || "Erreur lors de la génération de la voix pour cette étape.";
      (window.addToast || addToast)?.(msg, 'error', 5000);
    } finally {
      setPerStepBusy((prev) => ({ ...prev, [index]: false }));
    }
  };

  // ---------- IA – suggestions de musique ----------
  const handleGenerateAiMusicSuggestions = async () => {
    setMusicAiLoading(true);
    setMusicAiSuggestions([]);

    try {
      const body = {
        mood: musicAiPrompt || 'ambiance douce et contemplative pour documentaire',
        durationSeconds: 90,
        reference: scenario?.description || '',
        projectTitle: scenario?.title || '',
      };

      const res = await fetch('/ai/generate-music-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
      setMusicAiSuggestions(suggestions);
    } catch (err) {
      console.error('AI music', err);
      const msg = err.message || "Erreur lors de la suggestion de musique par l'IA.";
      (window.addToast || addToast)?.(msg, 'error', 5000);
    } finally {
      setMusicAiLoading(false);
    }
  };


  const handleGenerateAiMusicAudio = async () => {
    const cleanPrompt = (musicAiPrompt || '').trim();
    if (!cleanPrompt) {
      (window.addToast || addToast)?.(
        "Écris d'abord une description pour la musique.",
        'error',
        5000
      );
      return;
    }

    setMusicAiLoading(true);
    setMusicError?.(null);

    try {
      const res = await fetch(`/projects/${projectId}/ai-generate-music-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: cleanPrompt }),
      });

      const data = await res.json();
      if (!res.ok || !data.file) {
        throw new Error(data.error || `Erreur génération musique locale`);
      }

      setMusicFileName(data.file);
      setMusicFile(`/uploads/${data.file}`);
      addToast?.('🎵 Musique IA générée avec succès !', 'success');
    } catch (err) {
      console.error('🎵 AI music error:', err);
      setMusicError?.(err.message || 'Erreur IA locale');
      addToast?.(`Erreur génération musique IA: ${err.message}`, 'error');
    } finally {
      setMusicAiLoading(false);
    }
  };


  // ---------- Rendu ----------
  return (
    <div className="card border-0 shadow-sm rounded-4 h-100">
      <div className="card-body p-3 p-md-4">
        {/* ===================== TRANSITION ===================== */}
        <div className="mb-4">
          <div
            className="d-flex justify-content-between align-items-center mb-2 cursor-pointer"
            style={{ cursor: 'pointer' }}
            onClick={() => setIsTransitionOpen((v) => !v)}
          >
            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
              <h6 className="fw-semibold d-flex align-items-center gap-2 mb-0">
                <i className="bi bi-film text-primary" />
                Transition entre les médias
              </h6>
              <small className="text-muted d-none d-md-inline">
                Choisissez le style de passage d’un plan à l’autre
              </small>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-light border-0 d-inline-flex align-items-center gap-1"
            >
              <span className="small text-muted">
                {isTransitionOpen ? 'Réduire' : 'Afficher'}
              </span>
              <i
                className={
                  'bi ' + (isTransitionOpen ? 'bi-chevron-up' : 'bi-chevron-down')
                }
              />
            </button>
          </div>

          {isTransitionOpen && (
            <>
              <div className="row g-2">
                {TRANSITION_OPTIONS.map((t) => {
                  const isActive = transition === t.key;
                  return (
                    <div className="col-12 col-sm-6 col-lg-4" key={t.key}>
                      <button
                        type="button"
                        className={
                          'btn btn-sm w-100 text-start transition-choice ' +
                          (isActive ? 'btn-primary' : 'btn-outline-secondary')
                        }
                        onClick={() => setTransition(t.key)}
                      >
                        <div className="d-flex align-items-start">
                          <i
                            className={`bi ${t.icon || 'bi-play-btn'} me-2 mt-1`}
                          />
                          <div>
                            <div className="fw-semibold">
                              {t.label}
                              {isActive && (
                                <span className="badge bg-light text-dark ms-2">
                                  Actif
                                </span>
                              )}
                            </div>
                            {t.hint && (
                              <div className="small opacity-75">{t.hint}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="form-text mt-2">
                Astuce : commencez avec <strong>Fondu enchaîné</strong> pour un
                rendu fluide, puis testez les transitions plus dynamiques (slide,
                radial, zoom) pour les séquences clés.
              </div>
            </>
          )}
        </div>

        {/* ===================== VOIX OFF ===================== */}
        <div className="mb-4">
          <div
            className="d-flex justify-content-between align-items-center mb-2"
            style={{ cursor: 'pointer' }}
            onClick={() => setIsVoiceOpen((v) => !v)}
          >
            <h6 className="fw-semibold d-flex align-items-center gap-2 mb-0">
              <i className="bi bi-mic text-primary" /> Voix off
            </h6>

            <div className="d-flex align-items-center gap-2">
              <div
                className="btn-group btn-group-sm"
                role="group"
                aria-label="Mode voix off"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={`btn ${voiceMode === 'single' ? 'btn-primary' : 'btn-outline-primary'
                    }`}
                  onClick={() => setVoiceMode('single')}
                >
                  Voix globale
                </button>
                <button
                  type="button"
                  className={`btn ${voiceMode === 'per-step'
                    ? 'btn-primary'
                    : 'btn-outline-primary'
                    }`}
                  onClick={() => setVoiceMode('per-step')}
                >
                  Par étape
                </button>
              </div>

              <button
                type="button"
                className="btn btn-sm btn-light border-0 d-inline-flex align-items-center gap-1"
              >
                <span className="small text-muted">
                  {isVoiceOpen ? 'Réduire' : 'Afficher'}
                </span>
                <i
                  className={
                    'bi ' + (isVoiceOpen ? 'bi-chevron-up' : 'bi-chevron-down')
                  }
                />
              </button>
            </div>
          </div>

          {isVoiceOpen && (
            <>
              <p className="text-muted small mb-3 d-flex align-items-center gap-2">
                <i className="bi bi-info-circle text-primary"></i>
                Choisissez entre une narration <strong>unique</strong> ou une voix{' '}
                <strong>par étape</strong> pour créer un montage immersif et
                professionnel.
              </p>

              {/* ======================= MODE VOIX GLOBALE ======================= */}
              {voiceMode === 'single' && (
                <>
                  {/* Upload global voice file */}
                  <div className="mb-3">
                    <label className="form-label small text-uppercase text-muted fw-semibold d-flex align-items-center gap-2">
                      <i className="bi bi-file-earmark-music"></i>
                      Voix off (fichier)
                    </label>
                    <div className="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
                      <input
                        type="file"
                        accept="audio/*"
                        className="form-control form-control-sm"
                        onChange={handleVoiceUpload}
                      />
                      {voiceOverFileName && (
                        <span className="badge rounded-pill text-bg-info-subtle border">
                          <i className="bi bi-robot me-1"></i> IA : {voiceOverFileName}
                        </span>
                      )}
                      {voiceOverFile && (
                        <span className="badge rounded-pill text-bg-secondary-subtle border">
                          <i className="bi bi-file-earmark-music me-1"></i>{' '}
                          {voiceOverFile.name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* IA settings */}
                  <div className="mb-3">
                    <label className="form-label small text-uppercase text-muted fw-semibold d-flex align-items-center gap-2">
                      <i className="bi bi-stars"></i>
                      Paramètres IA
                    </label>

                    <div className="row g-2">
                      <div className="col-6">
                        <select
                          className="form-select form-select-sm"
                          value={voiceLang}
                          onChange={(e) => setVoiceLang(e.target.value)}
                        >
                          <option value="fr-FR">🇫🇷 Français</option>
                          <option value="en-US">🇺🇸 Anglais (US)</option>
                          <option value="en-GB">🇬🇧 Anglais (UK)</option>
                        </select>
                      </div>

                      <div className="col-6">
                        <select
                          className="form-select form-select-sm"
                          value={voiceGender}
                          onChange={(e) => setVoiceGender(e.target.value)}
                        >
                          <option value="FEMALE">Féminin</option>
                          <option value="MALE">Masculin</option>
                          <option value="NEUTRAL">Neutre</option>
                        </select>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {/* Generate AI voice */}
                      <button
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2"
                        type="button"
                        onClick={generateVoiceFromScenario}
                        disabled={loadingTTS}
                      >
                        {loadingTTS ? (
                          <>
                            <span className="spinner-border spinner-border-sm"></span>
                            Génération IA…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-magic"></i>
                            Générer depuis le scénario
                          </>
                        )}
                      </button>

                      {/* Recording button */}
                      <button
                        className={`btn btn-sm d-inline-flex align-items-center gap-2 ${isRecording ? 'btn-danger' : 'btn-outline-secondary'
                          }`}
                        type="button"
                        onClick={isRecording ? stopRecording : startRecording}
                      >
                        {isRecording ? (
                          <>
                            <i className="bi bi-stop-circle"></i>
                            Arrêter l'enregistrement
                          </>
                        ) : (
                          <>
                            <i className="bi bi-mic"></i>
                            Enregistrer ma voix
                          </>
                        )}
                      </button>
                    </div>

                    {recordError && (
                      <div className="mt-2 small text-danger d-flex align-items-center gap-1">
                        <i className="bi bi-exclamation-triangle"></i>
                        {recordError}
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  {voiceUrl && (
                    <div className="mt-3">
                      <label className="form-label small text-uppercase text-muted fw-semibold d-flex align-items-center gap-2">
                        <i className="bi bi-headphones"></i>
                        Pré-écoute
                      </label>
                      <audio
                        controls
                        src={String(voiceUrl)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </>
              )}

              {/* ======================= MODE VOIX PAR ÉTAPE ======================= */}
              {voiceMode === 'per-step' && (
                <div className="mt-3">
                  <p className="text-muted small mb-2 d-flex align-items-center gap-2">
                    <i className="bi bi-soundwave text-primary fs-6"></i>
                    Génère, importe ou enregistre une voix dédiée pour chaque étape
                    sélectionnée.
                  </p>

                  {rawSteps.length === 0 && (
                    <div className="alert alert-warning small mb-0 d-flex align-items-center gap-2">
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      <span>
                        Aucune étape détectée. Sauvegarde d’abord le scénario.
                      </span>
                    </div>
                  )}

                  {rawSteps.length > 0 && (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(260px, 1fr))',
                        gap: 14,
                      }}
                    >
                      {rawSteps.map((st, idx) => {
                        const stepId = st.id ?? idx + 1;

                        const hasSelectedMediaForStep = (() => {
                          if (
                            !Array.isArray(projectMedia) ||
                            !Array.isArray(selectedMediaIds)
                          )
                            return false;
                          const idsInStep = projectMedia
                            .filter(
                              (m) =>
                                (m.step_id == null ? 'no-step' : String(m.step_id)) ===
                                String(stepId)
                            )
                            .map((m) => m.id);
                          return idsInStep.some((id) =>
                            selectedMediaIds.includes(id)
                          );
                        })();

                        const info = perStepVoices[idx];
                        const stepText = (st.description || st.text || '').trim();
                        const locationLabel =
                          st.location || st.place || st.city || '';
                        const title =
                          st.title || locationLabel || `Étape ${idx + 1}`;

                        const isRecordingThisStep =
                          isRecording && recordingStepIndex === idx;

                        return (
                          <div
                            key={idx}
                            className="bg-white border rounded-3 p-3"
                            style={{
                              boxShadow: '0 6px 14px rgba(0,0,0,.08)',
                              transition: 'all .2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow =
                                '0 10px 22px rgba(0,0,0,.14)';
                              e.currentTarget.style.transform =
                                'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow =
                                '0 6px 14px rgba(0,0,0,.08)';
                              e.currentTarget.style.transform =
                                'translateY(0)';
                            }}
                          >
                            {/* Header */}
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="avatar avatar-xs rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center">
                                    <i className="bi bi-flag text-primary"></i>
                                  </span>
                                  <span className="fw-semibold">{title}</span>
                                </div>
                                {stepText && (
                                  <div className="small text-muted mt-1">
                                    {stepText.slice(0, 110)}
                                    {stepText.length > 110 ? '…' : ''}
                                  </div>
                                )}
                              </div>

                              <span
                                className={
                                  'badge rounded-pill px-2 ' +
                                  (hasSelectedMediaForStep
                                    ? 'bg-success-subtle text-success'
                                    : 'bg-secondary-subtle text-secondary')
                                }
                              >
                                {hasSelectedMediaForStep ? (
                                  <>
                                    <i className="bi bi-check-circle me-1"></i>{' '}
                                    Médias OK
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-x-circle me-1"></i> Aucun
                                    média
                                  </>
                                )}
                              </span>
                            </div>

                            {/* Buttons */}
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {/* IA Generation */}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2"
                                onClick={() => handleGenerateStepVoice(idx, st)}
                                disabled={
                                  perStepBusy[idx] || !hasSelectedMediaForStep
                                }
                              >
                                {perStepBusy[idx] ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm"></span>
                                    Génération…
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-stars"></i>
                                    Générer IA
                                  </>
                                )}
                              </button>

                              {/* Recording */}
                              <button
                                className={
                                  'btn btn-sm d-inline-flex align-items-center gap-2 ' +
                                  (isRecordingThisStep
                                    ? 'btn-danger'
                                    : 'btn-outline-secondary')
                                }
                                type="button"
                                onClick={
                                  isRecordingThisStep
                                    ? stopRecording
                                    : () => startStepRecording(idx)
                                }
                              >
                                {isRecordingThisStep ? (
                                  <>
                                    <i className="bi bi-stop-circle"></i>
                                    Stop
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-mic"></i>
                                    Enregistrer
                                  </>
                                )}
                              </button>

                              {/* Upload */}
                              <label className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2 mb-0">
                                <i className="bi bi-upload"></i> Importer
                                <input
                                  type="file"
                                  accept="audio/*"
                                  hidden
                                  disabled={!hasSelectedMediaForStep}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    const url = URL.createObjectURL(f);
                                    setPerStepVoices((prev) => {
                                      const next = {
                                        ...prev,
                                        [idx]: {
                                          type: 'file',
                                          fileName: f.name,
                                          url,
                                        },
                                      };
                                      onPerStepVoicesChange?.(next);
                                      return next;
                                    });
                                  }}
                                />
                              </label>
                            </div>

                            {/* Preview */}
                            <div className="mt-2">
                              {info?.url ? (
                                <audio
                                  controls
                                  src={info.url}
                                  style={{ width: '100%' }}
                                />
                              ) : (
                                <div className="small text-muted fst-italic">
                                  Aucune voix définie.
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <hr className="my-3" />

        {/* ===================== MUSIQUE DE FOND ===================== */}
        <div>
          <div
            className="d-flex justify-content-between align-items-center mb-2"
            style={{ cursor: 'pointer' }}
            onClick={() => setIsMusicOpen((v) => !v)}
          >
            <h6 className="fw-semibold d-flex align-items-center gap-2 mb-0">
              <i className="bi bi-music-note-beamed text-primary" /> Musique de
              fond
            </h6>

            <button
              type="button"
              className="btn btn-sm btn-light border-0 d-inline-flex align-items-center gap-1"
            >
              <span className="small text-muted">
                {isMusicOpen ? 'Réduire' : 'Afficher'}
              </span>
              <i
                className={
                  'bi ' + (isMusicOpen ? 'bi-chevron-up' : 'bi-chevron-down')
                }
              />
            </button>
          </div>

          {isMusicOpen && (
            <>
              <div className="mb-3">
                <label className="form-label small text-uppercase text-muted fw-semibold">
                  Fichier musique
                </label>
                <div className="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
                  <input
                    type="file"
                    accept="audio/*"
                    className="form-control"
                    onChange={handleMusicUpload}
                  />
                  {musicFileName && (
                    <span className="badge text-bg-light">
                      IA / projet : {musicFileName}
                    </span>
                  )}
                  {musicFile && (
                    <span className="badge text-bg-light">
                      fichier: {musicFile.name}
                    </span>
                  )}
                </div>
              </div>

              {musicUrl && (
                <div className="mb-3">
                  <label className="form-label small text-uppercase text-muted fw-semibold">
                    Pré-écoute musique
                  </label>
                  {musicUrl ? (
                    <audio
                      controls
                      src={String(musicUrl)}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    <div className="text-muted fst-italic small">
                      Aucune pré-écoute de musique configurée pour l’instant.
                    </div>
                  )}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label small text-uppercase text-muted fw-semibold">
                  Suggestions IA pour la musique
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Ex: samba, ambiance festive avec percussions brésiliennes..."
                  value={musicAiPrompt}
                  onChange={(e) => setMusicAiPrompt(e.target.value)}
                />
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
                    onClick={handleGenerateAiMusicSuggestions}
                    disabled={musicAiLoading}
                  >
                    {musicAiLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Idées IA…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-music-note-list" />
                        Suggérer des ambiances
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2"
                    onClick={handleGenerateAiMusicAudio}
                    disabled={musicAiLoading}
                  >
                    {musicAiLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Génération musique…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-stars" />
                        Générer une musique IA
                      </>
                    )}
                  </button>
                </div>

                {musicAiSuggestions.length > 0 && (
                  <ul className="mt-2 small text-muted mb-0">
                    {musicAiSuggestions.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                )}

                {musicError && (
                  <div className="alert alert-danger small mt-2 mb-0" role="alert">
                    {musicError}
                  </div>
                )}
              </div>

              <div className="row g-3 align-items-center">
                <div className="col-12 col-md-6">
                  <label className="form-label small text-uppercase text-muted fw-semibold">
                    Mixage audio
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={audioMixOption}
                    onChange={(e) => setAudioMixOption(e.target.value)}
                  >
                    {/* ⚠️ Aligné avec le backend: 'voice+music' | 'voice_only' | 'music_only' */}
                    <option value="voice+music">Voix + musique</option>
                    <option value="voice_only">Voix seule</option>
                    <option value="music_only">Musique seule</option>
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small text-uppercase text-muted fw-semibold">
                    Volume musique ({musicVolume}%)
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max="100"
                    value={musicVolume}
                    onChange={(e) => setMusicVolume(Number(e.target.value))}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ================= Subcomponents ================= */
function Pill({ icon, label, right }) {
  return (
    <span className="badge d-inline-flex align-items-center gap-2 bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25" style={{ borderRadius: 999 }}>
      {icon && <i className={`bi ${icon}`} />}{label}{right}
    </span>
  );
}

function ProjectAccordions({
  roles, visibility, usersByUsername, avatarFor,
  scenarioForm, projectId
}) {
  const [openSummary, setOpenSummary] = useState(false);
  const [openTeam, setOpenTeam] = useState(false);

  const grouped = useMemo(() => {
    const g = {};
    (roles || []).forEach(r => {
      const role = (r.role || '').toLowerCase();
      if (!g[role]) g[role] = [];
      g[role].push(r);
    });
    return g;
  }, [roles]);

  return (
    <>
      {/* Summary (title removed; description read-only) */}
      <div className="mb-2">
        <button
          type="button"
          style={styles.accordionBtn}
          onClick={() => setOpenSummary(v => !v)}
          aria-expanded={openSummary}
        >
          <span><i className="bi bi-info-circle me-2" />Résumé du projet</span>
          <i className={`bi ${openSummary ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
        </button>
        {openSummary && (
          <div style={styles.accordionPanel}>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={scenarioForm?.description || '—'}
                  readOnly
                  disabled
                />
                <div className="form-text">
                  Cette description est en lecture seule. Modifiez le scénario complet depuis le Wizard si nécessaire.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Team (unchanged) */}
      <div className="mb-2">
        <button
          type="button"
          style={styles.accordionBtn}
          onClick={() => { setOpenTeam(v => !v); }}
          aria-expanded={openTeam}
        >
          <span><i className="bi bi-people me-2" />Équipe & rôles</span>
          <span className="d-flex align-items-center gap-2">
            <span className="badge bg-light text-dark">{roles.length}</span>
            <i className={`bi ${openTeam ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
          </span>
        </button>
        {openTeam && (
          <div style={styles.accordionPanel}>
            {visibility === 'PUBLIC' && (
              <div className="alert alert-info py-2 px-3 mb-2">
                <i className="bi bi-globe me-2" />Projet public : tout utilisateur peut contribuer.
              </div>
            )}

            {['admin', 'scenariste', 'co-scenariste', 'contributor']
              .filter(role => grouped[role]?.length)
              .map(role => (
                <div key={role} className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="m-0 d-flex align-items-center gap-2">
                      <i className="bi bi-shield-check" />
                      {prettyRole(role)} <span className="badge bg-light text-dark">{grouped[role].length}</span>
                    </h6>
                  </div>
                  <div className="d-flex flex-wrap gap-3">
                    {grouped[role].map(r => (
                      <div key={r.username} className="d-flex align-items-center gap-2 border rounded-pill px-3 py-2 bg-light">
                        <img
                          src={avatarFor(r.username)}
                          alt={r.username}
                          width={28}
                          height={28}
                          className="rounded-circle border"
                          style={{ objectFit: 'cover' }}
                          onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
                        />
                        <span className="small fw-semibold">@{r.username}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

            {!['admin', 'scenariste', 'co-scenariste', 'contributor'].some(role => grouped[role]?.length) && (
              <div className="text-muted small">Aucun membre enregistré.</div>
            )}
          </div>
        )}
      </div>


    </>
  );
}


